import type {MessageId} from './en.js';

/**
 * 简体中文 — the Chinese UI.
 *
 * Typed as a complete `Record<MessageId, string>` on purpose: adding an English
 * id without its Chinese is a compile error, not a silent fall-through to
 * English. That is the "future builds must ship a Chinese UI" rule expressed as
 * something the build refuses to let you forget.
 *
 * NOT translated, by Jacky's rule (2026-09-16): product names, collection names,
 * colour names and supplier names. 「Alisa — Signature Floors」「Wonderwood Hybrid
 * 9mm」stay exactly as the catalogue writes them, because a Chinese-reading
 * customer buys the same product code the installer lays. Units, room names and
 * the customer's own words are theirs, not ours.
 */
export const ZH: Record<MessageId, string> = {
  "proposal.link.customer": "客户方案链接",
  "proposal.link.readFailed": "无法读取已保存的客户链接。",
  "proposal.link.retry": "重新读取已保存链接",
  "proposal.sourceLoading": "正在加载咨询或工程…",
  "proposal.link.loading": "正在读取已保存的客户链接…",
  "service.standard": "标准服务",
  "service.custom": "自定义例外",
  "service.choose": "选择已登记的服务",
  "service.execution": "由谁完成此项工作？",
  "service.artemis": "Artemis 自行施工",
  "service.contractor": "承包商",
  "service.zero": "Artemis 人工成本为 $0。材料和垃圾处理费用单独记录。",
  "service.conditions": "条件与包含范围",
  "service.settings": "登记或编辑标准服务及费率",
  "service.loading": "正在加载已登记服务…",
  "service.readFailed": "无法加载已登记服务。选择标准工作前请刷新。",
  "service.unregistered": "未登记",
  "service.search": "查找标准服务",
  "service.add": "添加标准服务",
  "service.newName": "新标准服务",
  "service.sell": "客户单价 · 不含 GST",
  "service.artemisLabour": "Artemis 人工成本 · 不含 GST",
  "service.otherCost": "Artemis 材料／处理／其他每单位成本",
  "service.contractorCost": "承包商完整每单位成本 · 不含 GST",
  "service.contractorReference": "未输入金额时使用的已登记承包商标准服务编号",
  "service.customerNote": "客户工作范围说明",
  "service.configureCost": "登记成本与条件",
  "service.evidence": "成本来源或经营者指示",
  "service.approved": "已批准用于此项标准工作的成本依据",
  "service.materialsNotice": "未知成本请留空。纯人工服务不包含单独供应和计价的材料；完整服务必须将全部材料及费用计入一次。",
  "service.advanced": "服务选项、测量及高级设置",
  "service.libraryKicker": "产品及服务",
  "service.libraryTitle": "服务库",
  "service.libraryLede": "登记团队可加入报价书的重复服务：数量依据、客户说明、未含 GST 单价、人工及成本。",
  "service.libraryDescription": "报价书快捷服务和 EmmA 共用一个服务库；修改会保存为新的已审计配置版本。",
  "service.libraryVersion": "{title} · 版本 {version} · 由 {by} 于 {date} 编辑",
  "service.libraryDefaults": "{title} · 使用默认配置",
  "service.libraryLoadFailed": "无法加载已保存的服务库，已停用保存。请重新加载页面。",
  "service.libraryLoading": "正在加载已保存的服务库……",
  "service.changeReason": "修改内容",
  "service.changeReasonPlaceholder": "用一句话说明修改内容（随版本保存）",
  "service.saveVersion": "保存为版本 {version}",
  "service.save": "保存服务库",
  "service.resetSaved": "恢复已保存内容",
  "service.saveNote": "保存时按结构校验 · 保留历史版本 · 范围：{scope}",

  'customer.plan.recordedDimensions': '{count} 条已记录尺寸',
  "cost.prompt": "查看本报价已有的成本估算，不阻止发布或接受。未知金额保留为空。开票后在工作单确认实际供应商发票、材料用量及施工队费用，保留已接受价格及已发布文件，并记录内部实际成本跟进。不要发送任何消息。",
  "cost.confirmActual": "确认实际成本",
  "cost.openTasks": "打开内部确认任务",
  "cost.details": "工作项目与成本明细",
  "cost.previous": "上一页成本",
  "cost.next": "更多成本",
  "cost.engine": "已配置的完整工作成本",
  "cost.catalogue": "供应商当前材料成本",
  "cost.standard": "已批准的工作项目费率",
  "cost.truncated": "当前显示 {count} 项，共 {total} 项。EmmA 审核成本时可读取其余项目。",
  "cost.title": "内部成本核对 · 未含 GST",
  "cost.needsYou": "EmmA 需要您 · 开票后确认实际成本",
  "cost.unresolved": "{count} 项估算成本未知 · 可以发布报价",
  "cost.readFailed": "成本估算暂时无法读取，仍可发布报价。",
  "cost.releaseBlocked": "实际成本将在开票后确认。",
  "cost.discuss": "与 EmmA 讨论成本",
  "cost.saveFirst": "请先保存当前工作内容，再核对成本。",
  "cost.ready": "所有提供的工作均有成本依据。",
  "cost.work": "工作项目",
  "cost.basis": "成本依据",
  "cost.amount": "成本 · 未含 GST",
  "cost.missing": "需要您补充成本依据",
  "cost.covered": "已包含在有依据的套餐成本中",
  "cost.base": "基础报价",
  "cost.option": "备选方案",
  "cost.variation": "暂定工作",
  "cost.baseCost": "基础成本",
  "cost.profit": "基础利润",

  "site.projects.intro": "查看过去 30 天最新完成的地板项目，以及 2026 年全部项目。照片由我们的安装团队拍摄，并链接所用产品及花色。",
  "site.projects.period": "项目时间范围",
  "site.projects.recent": "过去 30 天",
  "site.projects.year": "{year} 年全部项目",
  "site.projects.count": "共 {count} 个项目 · 每页最多 {size} 个",
  "site.projects.empty": "此范围暂无项目",
  "site.projects.emptyBody": "我们正在审核完工照片。请选择其他时间范围查看更多项目。",
  "site.projects.pagination": "项目分页",
  "site.projects.previous": "← 上一页",
  "site.projects.next": "下一页 →",
  "site.projects.page": "第 {page} 页，共 {pages} 页",

 "customer.pages.method":"Artemis 如何铺装地板",
 "customer.pages.document":"您的地板方案",
 "customer.pages.pdf":"打开 / 下载方案 PDF",
 "customer.pages.choices":"选择产品及颜色 / 确认方案",
 "customer.pages.zoom":"打开第 {page} 页以放大查看",
 "customer.pages.failed":"此页未能加载。请打开已审核的 PDF，或展开下方选项。",

 "woLink.title":"安装工作单",
 "woLink.help":"请保存此链接。无需登录即可查看最新工作单。",
 "woLink.unavailable":"工作单链接不可用，请联系办公室。",
 "woLink.open":"打开工作单",
 "woLink.copy":"复制工作单链接",
 "woLink.copied":"已复制工作单链接",
 "woLink.loading":"正在加载工作单…",
 "woLink.empty":"请先为已保存的日期指派安装团队，即可获得工作单链接。",
 "woLink.sms":"短信发送工作单",
 "woLink.recipient":"安装工手机号",
 "woLink.message":"短信内容",
 "woLink.send":"通过短信网关发送",
 "woLink.sending":"发送中…",
 "woLink.sent":"已提交至短信网关（发送方 Artemis FLR）。发送结果见沟通台。",
 "woLink.pendingSend":"已保存；发送在沟通台等待批准。",
 "woLink.sendFailed":"未发送：{message}",
 "woLink.noRecipient":"该施工队未保存手机号，请在安装工资料中添加。",
 "woLink.smsHelp":"通过短信网关发送（发送方 Artemis FLR），并记录在沟通台。",
 "woLink.smsBody":"{crew}，您好。{name}，{address} 的工作单：\n{url}\n请保存此链接，以查看排期和工程更新。",
 "woLink.site":"工地与进场信息",
 "woLink.maps":"导航到工地",
 "woLink.days":"已安排的工作日期",
 "woLink.done":"当天已完成",
 "woLink.complete":"标记当天完成",
 "woLink.scopeTick":"我已完成上方所列的当天工作",
 "woLink.accept":"接受已安排的工作",
 "woLink.accepted":"已接受安排的工作",
 "woLink.acceptFirst":"报告完成前，请先接受安排的工作。",
 "woLink.notes":"备注 / 问题",
 "woLink.save":"保存进度备注",
 "woLink.issue":"报告问题",
 "woLink.saved":"已保存。您的报告显示在下方。",
 "woLink.failed":"报告未保存。已保留您的文字，请刷新工作单后重试。",
 "woLink.pending":"报告已提交审核，尚未记录为完成。",
 "woLink.history":"已保存的报告",
 "woLink.materials":"材料",
 "woLink.pickups":"供应商提货",
 "woLink.pickupNumber":"提货时出示此编号",
 "woLink.pickupAwaiting":"供应商提货编号尚待邮件确认。提货前请联系办公室。",
 "woLink.drawing":"现场图纸 · 仅供参考，请现场核实尺寸",
 "woLink.photos":"添加照片",
 "woLink.print":"打印工作单",
 "woLink.refresh":"刷新工作单",
 "woLink.noScope":"办公室需先记录当天工作范围，然后才能标记完成。",
 "woLink.photoFailed":"照片上传失败，报告尚未提交。",
 "woLink.photoView":"查看已保存照片",
 "woLink.test":"测试工作单 — 演练数据",

  'installDay.office':'办公室',
  'installDay.officeClosed':'办公室已关闭此工作日',
  'installDay.reopened':'工作日已重新开启',
  'installDay.unverified':'已过日期 · 办公室尚未关闭',
  'installDay.planned':'已计划',
  'installDay.crewUnknown':'施工队待确认',
  'installDay.close':'关闭工作日',
  'installDay.reopen':'重新开启工作日',
  'installDay.note':'办公室备注',
  'installDay.noteRequired':'保存前请输入办公室备注。',
  'installDay.closeHint':'办公室核实了哪些工作？',
  'installDay.reopenHint':'为什么重新开启此工作日？',
  'installDay.confirmClose':'保存工作日关闭',
  'installDay.confirmReopen':'保存重新开启',
  'installDay.saving':'正在保存…',
  'installDay.closed':'办公室已关闭施工工作日',
  'installDay.notSaved':'工作日尚未保存，请刷新后重试。',
  'installDay.installerRecorded':'已记录安装人员完工报告',
  "cash.managedDeposit": "由收款及收据管理。更正时保留原收据历史。",
  "cash.final": "全部尾款",
  "cash.open": "现金收款及收据",
  "cash.title": "收款及收据",
  "cash.close": "关闭",
  "cash.loading": "正在读取收款记录…",
  "cash.failed": "无法读取收款记录，请重试。",
  "cash.agreedCash": "商定现金金额",
  "cash.noAddedGst": "不另加 GST",
  "cash.cashHint": "按商定现金金额记录实际收到的现金。已有定金只扣除一次。这些记录不代表 Xero 或银行对账。",
  "cash.agreedExGst": "商定金额（未含 GST）",
  "cash.gst": "GST 税额",
  "cash.payable": "商定应付金额",
  "cash.received": "累计已收款",
  "cash.balance": "未付余额",
  "cash.credit": "多付余额",
  "cash.hint": "记录实际收到的现金。应付金额依据当前工作的商定金额及已记录的 GST 税务处理计算。已有定金只扣除一次。这些记录不代表 Xero 或银行对账。",
  "cash.kind": "付款用途",
  "cash.deposit": "定金",
  "cash.part": "分期付款",
  "cash.amount": "实际现金收款 · 澳元",
  "cash.date": "收款日期 · 悉尼",
  "cash.note": "收据备注",
  "cash.record": "记录现金并生成收据",
  "cash.opening": "已有定金",
  "cash.openingHint": "已计入累计收款。开具收据不会重复记录付款。",
  "cash.issue": "开具现金收据",
  "cash.unknownDate": "日期未记录",
  "cash.unknownMethod": "方式未记录",
  "cash.voided": "已作废",
  "cash.preview": "收据 / 打印 PDF",
  "cash.email": "电子邮件收据",
  "cash.sms": "短信收据",
  "cash.correct": "更正记录",
  "cash.issueHint": "请确认这笔已有现金款项的实际收款日期。此操作不会增加付款。",
  "cash.cancel": "取消",
  "cash.voidHint": "填写原因并作废错误记录。收据会保留并标记为作废，此操作不会退还现金。",
  "cash.reason": "更正原因",
  "cash.void": "作废错误付款",
  "cash.saved": "已记录现金并保存收据，尚未发送。",
  "cash.issued": "已为已有付款保存收据，尚未发送。",
  "cash.queued": "收据已加入发送队列，尚未确认送达。",
  "cash.notSent": "收据未发送。",

 'commercial.editOptions':'编辑服务选项',
 'commercial.optionLabel':'选项说明',
 'commercial.addOption':'+ 服务选项',
 'commercial.offers':'报价方案',
 'commercial.copy':'副本',
 'commercial.duplicateOffer':'复制报价 / 套餐',
 'commercial.history':'版本记录',
 'commercial.historyHelp':'恢复工作版本后保存将创建新版本，已发布报价和已接受协议保持不变。',
 'commercial.historyFailed':'暂时无法读取版本记录。',
 'commercial.issued':'已发布副本',
 'commercial.restored':'已恢复早期工作版本，请检查后保存为新版本。',
 'commercial.autoAssessment':'自动更新现场测量',

 'commercial.offerRole':'报价类型',
 'commercial.replaces':'替代范围',

 "commercial.workingRevision":"工作修订稿",
 "commercial.details":"报价详情",
 "commercial.reading":"正在读取测量…",
 "commercial.latest":"更新测量",
 "commercial.followAll":"全部跟随现场测量",
 "commercial.followAllDone":"{followed} 行已跟随现场测量。",
 "commercial.followAllUnresolved":"没有测量数据：{names} — 保持手动输入。",
 "commercial.measurePlusWaste":"实测 {measured} m² + 损耗 {percent}%（{waste} m²）= {quantity} m²",
 "commercial.quantityOverride":"报价数量已手动改为 {quantity} m²",
 "commercial.measureUnavailable":"暂无实测面积 · 显示报价数量",
 "commercial.wasteUnavailable":"实测 {measured} m² · 暂无损耗规则",
 "commercial.rollBasis":"实测 {measured} m² · 卷长 {length} LM × 宽 {width} m = {area} m²",
 "commercial.preview":"预览 / 文件",
 "commercial.scope":"范围与价格",
 "commercial.addProduct":"产品与服务",
 "commercial.serviceHelp":"可添加任何服务，包括搬动家具。自行填写说明、数量及价格。",
 "commercial.serviceExample":"例如：搬动家具",
 "commercial.serviceInvalid":"请填写服务说明及有效的数量和单价。",
 "commercial.addWork":"+ 添加服务",
 "commercial.duplicate":"复制",
 "commercial.undo":"撤销",
 "commercial.measurements":"数量详情",
 "commercial.description":"范围 / 描述",
 "commercial.unit":"单位",
 "commercial.rate":"单价 · 不含 GST",
 "commercial.amount":"金额 · 不含 GST",
 "commercial.product":"已选产品",
 "commercial.follow":"跟随现场测量",
 "commercial.manual":"手动覆盖",
 "commercial.alternative":"备选 · 不计入总额",
 "commercial.resetRate":"使用建议单价",
 "commercial.included":"包含",
 "commercial.excluded":"不包含",
 "commercial.variation":"变更项 · 不计入总额",
 "commercial.optional":"可选项 · 不计入总额",
 "commercial.empty":"添加产品或自定义工作以开始。",
 "commercial.measured":"实测数量",
 "commercial.scotiaDrawing":"最新画布：{rooms} 收边线共 {length} 延米，分 {count} 段（{status}）。",
 "commercial.scotiaEstimate":"图纸估算，需现场核实",
 "commercial.scotiaConfirmed":"测量已确认",
 "commercial.scotiaSeparate":"上方支数是材料预留量，不是安装延米。",
 "commercial.scotiaNewer":"该图纸比已保存的提案更新；请检查“更新测量”。",
 "commercial.source":"数量来源",
 "commercial.carpetBasis":"地面面积跟随测量，采购卷材长度单独保留。",
 "commercial.allowance":"实测面积加 {percent}% 余量。",
 "commercial.treatment":"报价方式",
 "commercial.overrideHelp":"手动数量及销售单价不受图纸更新影响，原始测量数据保持不变。",
 "commercial.selectLine":"选择项目以查看实测数量及数量来源。",
 "commercial.assessment":"打开现场测量",
 "commercial.total":"完整报价 · 不含 GST",
 "commercial.save":"保存工作范围",
 "commercial.deliver":"发送 / 私密链接",
 "commercial.back":"返回范围与价格",
 "commercial.draftPreview":"私人工作草稿 · 本次修改尚未发布",
"commercial.openBookReleased":"按成本公开的客户副本 · 已发布 v{version} · 客户看到的就是这份",
"commercial.openBookOffered":"可选产品 {products} 款 · 地毯：{carpet}",
"commercial.openBookNoCarpet":"无",
"commercial.openBookNote":"按成本公开的方案在发布时按目录成本计价，客户在自己的页面上选择板材。下方编辑器里的产品与价格是工作草稿，不是客户副本；重新发布须通过 EmmA 的公开成本发布。",
"commercial.openBookPreview":"打开客户副本",
 "commercial.print":"打印 / 保存 PDF",
 "commercial.recovery":"有可恢复的工作副本，保存前请检查。",
 "commercial.restore":"恢复工作副本",
 "commercial.discard":"放弃恢复副本",
 "commercial.conflict":"保存已暂停。请保留工作副本，并核对已保存版本后重试。",
 "commercial.storageFailed":"浏览器无法保留恢复副本，离开前请保存。",
 "commercial.updated":"已加载最新测量。跟随测量的数量已更新，手动数量及单价保留。",
 "commercial.noAssessment":"此工作尚无已保存的共享测量。",
 "commercial.previewPending":"价格计算完成后才能生成客户预览。",
 "commercial.saveFailed":"草稿尚未保存，工作副本已保留。",
 "commercial.copyOffer":"准备另一份报价",

 'installationNotice.title':'安装服务说明',
 'installationNotice.preview':'客户将收到的完整内容',
 'installationNotice.stale':'已保存的报价范围发生变化。您填写的内容已保留，请与下方当前范围核对后再保存。',
 'installationNotice.sendFailed':'尚未确认发送。重试前请核对刷新后的收件人、内容及状态。',
 'installationNotice.help':'关联本项目的客户通知，无需签字或回复。生成草稿不会发送，也不会更改接受记录、付款或预约。',
 'installationNotice.loading':'正在加载通知…',
 'installationNotice.unavailable':'暂时无法加载通知。准备或发送前请刷新。',
 'installationNotice.sourceError':'生成通知前需核对已保存的报价范围。历史版本仍显示在下方。',
 'installationNotice.emma':'请 EmmA 准备',
 'installationNotice.prepare':'准备通知',
 'installationNotice.reviewScope':'请核对已保存的报价是否与客户实际商定的内容一致。此处修改仅影响通知，不会修正报价或施工指示。',
 'installationNotice.scope':'已商定范围 — 每行一项',
 'installationNotice.details':'已确认的项目具体安排',
 'installationNotice.detailsHelp':'例如：各楼层垫层、收边、客户提供的垃圾箱及已商定的付款安排。不得新增费用或义务。',
 'installationNotice.evidence':'内部协议依据 — 不发送给客户',
 'installationNotice.language':'通知语言',
 'installationNotice.email':'电子邮件',
 'installationNotice.sms':'短信',
 'installationNotice.smsHelp':'短信总长度（包括服务说明）最多为 1,600 个字符。更长的通知必须使用电子邮件，系统不会截断内容。',
 'installationNotice.confirm':'施工范围和项目安排符合客户已同意的内容；本通知不增加任何义务。',
 'installationNotice.save':'保存草稿并预览',
 'installationNotice.saved':'通知草稿已保存，尚未发送',
 'installationNotice.notSaved':'尚未保存到工程。您填写的内容仍保留在编辑器中。请解决以下问题后重试。',
 'installationNotice.cancel':'关闭编辑器并保留内容',
 'installationNotice.recheck':'已核对当前范围，保留我填写的内容',
 'installationNotice.recoveryFound':'上次保存失败的内容仍有恢复副本。未发送任何信息。',
 'installationNotice.restore':'恢复我填写的内容',
 'installationNotice.emailMissing':'尚未保存邮箱，请在联系人页面保存后返回',
 'installationNotice.phoneMissing':'尚未保存手机号码',
 'installationNotice.editContact':'打开联系人页面，保留此处填写的内容',
 'installationNotice.recoveryUnavailable':'暂时无法读取保存失败的恢复副本。下方编辑内容未被更改。',
 'installationNotice.kept':'输入内容已保留在当前标签页中。可前往联系人页面后返回，或刷新页面。尚未保存到工程；退出登录或关闭此标签页会移除本地副本。',
 'installationNotice.storageFailed':'此浏览器无法保留恢复副本。离开或刷新页面前，请先复制您填写的内容。',
 'installationNotice.empty':'此项目尚未保存安装通知。',
 'installationNotice.history':'已保存版本',
 'installationNotice.from':'发件人',
 'installationNotice.deliveryHelp':'保存的文字就是客户版本。已提交仅表示发送服务已接收，不代表客户已阅读或同意。',
 'installationNotice.uncertain':'发送结果不确定。再次发送前请先核实发送记录。',
 'installationNotice.state.draft':'草稿 — 未发送',
 'installationNotice.superseded':'旧版本 — 已有更新通知',
 'installationNotice.state.queued':'已排队',
 'installationNotice.state.sending':'正在发送',
 'installationNotice.state.submitted':'已提交发送服务',
 'installationNotice.state.uncertain':'发送结果不确定',
 'installationNotice.state.failed':'发送失败',
  "installer.guide.eyebrow": "ARTEMIS FLOORING · 安装团队指南",
  "installer.guide.title": "工作安排，手机轻松查看。",
  "installer.guide.intro": "欢迎使用 Artemis Installer。用您自己的手机号登录，再把 Artemis 添加到手机主屏幕，随时打开。",
  "installer.guide.open": "打开安装门户",
  "installer.guide.free": "无需从 App Store 或 Google Play 下载。",
  "installer.guide.login": "1. 使用手机号登录",
  "installer.guide.login1": "iPhone 使用 Safari，Android 使用 Chrome 打开门户。如果本指南在微信或其他应用内打开，请先选择在浏览器中打开。",
  "installer.guide.login2": "输入收到欢迎短信的手机号。澳大利亚号码可用 04 或 +61 开头。点击发送验证码。",
  "installer.guide.login3": "输入短信验证码并确认，无需密码。请勿与他人分享验证码。",
  "installer.guide.login4": "请使用自己的账号。安装人员查看分配给自己的工作；公司经理管理团队，并为公司的工作分配人员。",
  "installer.guide.install": "2. 将 Artemis 添加到主屏幕",
  "installer.guide.installintro": "请在安装门户页面操作，而不是在本指南页面操作。完成后，主屏幕上会出现打开工作区的应用图标。",
  "installer.guide.apple": "Apple · iPhone 设置",
  "installer.guide.apple1": "在 Safari 打开 install.artemisflooring.com。点击分享（方框向上箭头），某些版本需先打开更多菜单。",
  "installer.guide.apple2": "选择添加到主屏幕。如未显示，请向下滚动分享菜单，通过编辑操作添加此项。",
  "installer.guide.apple3": "如显示作为网页 App 打开，请保持开启，然后点击添加。从主屏幕打开 Artemis 图标。",
  "installer.guide.android": "Android · Chrome 设置",
  "installer.guide.android1": "在 Chrome 打开 install.artemisflooring.com。点击地址栏旁的三个点菜单。",
  "installer.guide.android2": "选择安装并创建快捷方式 → 安装。某些版本显示安装应用或添加到主屏幕。",
  "installer.guide.android3": "按提示确认，然后从主屏幕或应用列表打开 Artemis。",
  "installer.guide.after": "首次从主屏幕打开时，可能需要再次登录，请使用同一手机号。登录、查看工作和保存更新时，请保持网络连接。",
  "installer.guide.help": "需要帮助？",
  "installer.guide.code": "收不到验证码或无法登录？",
  "installer.guide.codebody": "请检查手机号和手机信号，并使用最新验证码。如仍失败，请联系 Artemis 办公室或您的公司经理，核对账号和登记号码。请勿重复注册账号。",
  "installer.guide.empty": "已登录，却没有工作？",
  "installer.guide.emptybody": "账号可能先于工作分配完成开通。请联系办公室或公司经理检查工作分配。",
  "installer.guide.reply": "Artemis FLR 欢迎短信为单向通知。需要帮助时，请通过平时的联系方式联系办公室或公司经理。",
  "installer.guide.sources": "手机设置帮助：Apple · Google",
  "installer.guide.pageTitle": "入门指南 · Artemis Installer",

  'job.workingProposal': '当前已存方案 · 未含 GST',
  'job.recordedValue': '已记录的工作金额',
  'job.quoteProposal': '报价与方案',
  'job.profit.title': '安装师傅费用与盈利',
  'job.tab.quote': '报价与收款',
  'job.tab.notes': '备注与历史',
  'jobPage.title': '客户工单页面',
  'jobPage.help': '客户打开的私密链接：查看地板、颜色和安装日期，并在页面上做出选择。每次打开和选择都记录在此。',
  'jobPage.live': '客户页面仅在正式工作区可用。',
  'jobPage.loading': '正在加载客户页面…',
  'jobPage.status.active': '有效',
  'jobPage.status.revoked': '已撤销',
  'jobPage.status.expired': '已过期',
  'jobPage.createdAt': '创建',
  'jobPage.opens': '打开次数',
  'jobPage.lastOpened': '最近',
  'jobPage.neverOpened': '从未打开',
  'jobPage.expires': '到期',
  'jobPage.choice': '客户选择',
  'jobPage.noChoice': '尚未选择',
  'jobPage.create': '创建页面链接',
  'jobPage.replace': '更换链接',
  'jobPage.revoke': '撤销',
  'jobPage.copy': '复制链接',
  'jobPage.copyNow': '请立即复制：系统只保存其指纹，无法再次显示。更换链接会使此链接失效。',
  'jobPage.confirmReplace': '更换客户链接？他们手上的链接将失效。',
  'jobPage.confirmRevoke': '撤销客户链接？页面将不再可用。',
  'jobPage.noContact': '请先在工单上保存客户。',
  'jobPage.created': '客户页面链接已创建。',
  'jobPage.revoked': '客户页面链接已撤销。',
  'jobPage.message.draft': '起草给客户的邮件',
  'jobPage.message.subject': '您在 Artemis 的地板工单',
  'jobPage.message.body': '您好{name}：\n\n这是您的地板工单私密页面：{url}\n\n页面显示您的地板、可选颜色和安装日期。请在页面上点选颜色并确认，或直接回复此邮件。',
  'jobPage.message.drafted': '邮件草稿已保存；请在“沟通”中发送。',
  'jobPage.message.review': '邮件已起草。请打开“沟通”查看并发送。',
  'jobPage.log': '页面活动',
  'jobPage.actor.customer': '客户',
  'jobPage.actor.staff': '办公室',
  'jobPage.event.open': '打开了页面',
  'jobPage.event.choice': '为 {product} 选择了 {colour}',
  'jobPage.event.created': '创建了链接',
  'jobPage.event.revoked': '撤销了链接',
  'jobPage.event.escalation': '请求办公室协助：{what}',
  'job.ov.accepted': '成交金额',
  'job.ov.noQuote': '尚无已接受报价',
  'job.ov.payments': '收款',
  'job.ov.deposit': '定金 {amount} · {date}',
  'job.ov.noDeposit': '未登记定金',
  'job.ov.paidXero': 'Xero 已收 {amount} · 未付 {due} · {invoice}',
  'job.ov.paid': '已付清 {date}',
  'job.ov.fee': '安装师傅费用',
  'job.ov.feeNone': '没有分包施工费',
  'job.ov.feeUnresolved': '{count} 行待定费用',
  'job.ov.feeConfirmed': '已确认，短信{state}',
  'job.ov.feeUnconfirmed': '尚未向师傅确认',
  'job.ov.profit': '毛利',
  'job.ov.install': '安装',
  'job.ov.nextDay': '下一个施工日 {date} · {crew}',
  'job.ov.lastDay': '最近施工日 {date} · {crew}',
  'job.ov.noDay': '尚未预约施工日',
  'job.ov.materials': '材料',
  'job.ov.orders': '{count} 张采购单 · 已发出 {committed}',
  'job.ov.noOrders': '无采购单',
  'job.ov.open': '打开',
  'job.profit.loading': '正在读取采购单和施工队…',
  'job.profit.unavailable': '无法读取盈利情况。',
  'job.profit.revenue': '已接受金额',
  'job.profit.materials': '材料',
  'job.profit.materialsOrders': '已发送 {committed} · 草稿 {planned}',
  'job.profit.materialsQuote': '已接受报价的引擎成本（含材料与人工）',
  'job.profit.materialsSuperseded': '{count} 张已被取代的草稿不计入',
  'po.cancelDraft': '取消此草稿',
  'po.cancelDraftReason': '撤回原因，例如：已被 9 月 29 日发出的订单取代',
  'po.cancelDraftConfirm': '取消草稿',
  'po.cancelSent': '向供应商取消订单',
  'po.cancelSentHelp': '将此订单标记为已取消，并起草发给 {supplier} 的取消邮件。邮件在“沟通”中等待您批准。',
  'po.cancelSentReason': '取消原因（供应商会看到）',
  'po.cancelSentConfirm': '取消订单并起草邮件',
  'po.cancelSentDone': '订单已取消。发给 {supplier} 的邮件已起草，等待您批准。',
  'po.cancelSentOpenComms': '打开沟通',
  'orders.title': '订单',
  'orders.lede': '哪些需要分配工程、哪些在路上、哪些已完成。只有工程即将开工而供应商毫无回复时才显示橙色。',
  'orders.group.needsJob': '需要分配工程',
  'orders.group.needsJobSub': '从发票或微信记录，未经采购单下单',
  'orders.group.toSend': '待发送',
  'orders.group.toSendSub': '尚未发送的草稿',
  'orders.group.onTheWay': '已备货或在路上',
  'orders.group.onTheWaySub': '已发给供应商',
  'orders.group.done': '已完成',
  'orders.state.cancelled': '已取消',
  'orders.state.collected': '已提货',
  'orders.state.delivered': '已送达',
  'orders.state.noJob': '尚未分配工程',
  'orders.state.draft': '草稿，未发送',
  'orders.state.ready': '可以提货',
  'orders.state.confirmed': '已确认',
  'orders.state.noWord': '{supplier} 尚无回复',
  'orders.theSupplier': '供应商',
  'orders.noJobYet': '尚无工程',
  'orders.multiJob': '{count} 个工程',
  'orders.fact.sent': '{date} 发出',
  'orders.fact.pickup': '提货号 {ref}',
  'orders.fact.installs': '{date} 开工',
  'orders.fact.credit': '赊账条款',
  'orders.tag.check': '今天核实',
  'orders.tag.done': '完成',
  'orders.tag.draft': '草稿',
  'orders.tag.confirmed': '已确认',
  'orders.tag.sent': '已发送',
  'orders.open': '打开',
  'orders.assignTo': '分配给 {job}',
  'orders.otherJob': '其他工程…',
  'orders.chooseJob': '选择工程…',
  'orders.stock': '入库',
  'orders.suggest': '看起来是 {job}。',
  'orders.showDone': '显示已完成',
  'orders.newOrder': '为工程新建订单',
  'orders.tools': '准备订单、待决事项、供应商回复',
  'job.profit.misc.title': '杂项材料成本',
  'job.profit.misc.planned': '尚未订购',
  'job.profit.misc.ordered': '已订购',
  'job.profit.misc.received': '已收货',
  'job.profit.misc.estimate': '估算价格',
  'job.profit.misc.reference': '参考价格；发票价格未核实',
  'job.profit.misc.actual': '已核实采购成本',
  'job.profit.misc.replaced': '已计入对应采购单',
  'job.profit.gap.material_cost_unresolved': '部分材料成本尚未确定；利润信息仍不完整。',
  'job.profit.materialsNone': '尚无采购单或引擎成本',
  'job.profit.labour': '安装人工',
  'job.profit.labourBasis': '分包施工计入费用。自有团队工资已计入固定运营成本。',
  'job.profit.profit': '毛利',
  'job.profit.complete': '完整',
  'job.profit.partial': '部分 · 见缺口',
  'job.profit.noCrew': '排期上尚无施工日。',
  'job.profit.costUnresolved': '未设置费用',
  'job.profit.kind.crew': 'Artemis 自有团队',
  'job.profit.kind.subcontractor': '分包商 · 申报费率',
  'job.profit.kind.staff': '员工 · 不计项目成本',
  'job.profit.kind.unknown': '无人工计费依据的施工队',
  'job.profit.work.matched': '已匹配',
  'job.profit.work.quantity_missing': '缺少数量',
  'job.profit.work.unit_mismatch': '单位与费率不符',
  'job.profit.work.ambiguous': '多个费率可匹配',
  'job.profit.work.missing_rate': '无已批准费率',
  'job.profit.work.unmapped': '无对应服务',
  'job.profit.unassigned': '{count} 项工作尚未分配 · 未计人工成本',
  'job.profit.gap.no_accepted_quote': '此项目没有已接受的报价。',
  'job.profit.gap.no_material_cost': '尚无采购单：材料成本未知。',
  'job.profit.gap.orders_not_sent': '采购单仍为草稿；材料金额为计划值，未确认。',
  'job.profit.gap.labour_unresolved': '有安装项尚未设置费用：请选择费率或填写费用。在此之前人工为部分数值。',
  // 先估算后实际的成本（Jacky，2026年10月23日）。
  'job.profit.kind.estimate': '估算',
  'job.profit.gap.labour_estimated': '部分工作尚未分配施工队：其人工按 Artemis 标准分包费率估算。',
  'job.profit.gap.materials_estimated': '部分材料成本采用目录价格、参考价格或临时估算。',
  'job.profit.est': '估',
  'job.profit.estimate.title': '尚未分配 · Artemis 标准费率',
  'job.profit.estimate.total': '估算人工',
  'job.profit.estimate.noRate': '无标准费率',
  'job.profit.estimate.help': '分配施工队并设定费用后，以实际费用替换；自有施工队成本为 $0。',
  'job.profit.gap.labour_in_quote_cost': '引擎成本已含人工；施工队费率仅供参考，不再累加。',
  'job.profit.gap.work_unassigned': '部分工作尚未分配。',
  'job.profit.work.none': '已记录费用',
  'job.profit.basis.override': '手填',
  'job.profit.basis.rate_book': '费率表',
  'job.profit.basis.agreed': '约定的额外工作',
  'job.profit.basis.proposed': '按费率表建议',
  'job.profit.needsFee': '需设置费用',
  'job.profit.edit': '编辑',
  'job.profit.clear': '清除',
  'job.profit.save': '保存',
  'job.profit.cancel': '取消',
  'job.profit.pickRate': '选择费率…',
  'job.profit.feeInput': '费用（澳元，不含 GST）',
  'job.profit.removeReason': '在项目卡上清除了费用',
  'job.profit.addExtra': '添加应付师傅的费用行——描述、数量 × 单价——用于不向客户收费的工作（同时成为排期任务）',
  'job.profit.extraParty': '安装工',
  'job.profit.extraLabel': '描述',
  'job.profit.extraQty': '数量',
  'job.profit.extraUom': '单位',
  'job.profit.work.included': '已含在地板安装单价内',
  'job.profit.basis.included': '含在地板单价内',
  'job.profit.basis.standard': 'Artemis 标准单价',
  'job.profit.basis.pass_through': '按向客户收费直接转付',
  'job.profit.basis.own_crew': '自有团队 · 不计成本', // 16 Oct 2026: own crew always $0
  'job.profit.standardRate': 'Artemis 标准',
  'job.profit.qtyInput': '数量',
  'job.profit.assignOwn': '把工作分配给自有团队',
  'job.profit.assignOwnHelp': '自有团队无需确认费用。该项会加到他们在本工程的施工日上，或按排期首日新建一天；结果见“安装”标签。',
  'job.profit.assignBtn': '分配',
  'job.profit.assigned': '已分配给 {name}：无需确认',
  'job.profit.assignNeedsPlan': '请先在“安装”标签为本工程排期（未指明团队的施工日无法在此修改）。',
  'job.profit.ownCrewUnrated': '自有团队 · 未设标准单价',
  'job.profit.ownCrewNoRate': '自有团队 · 无需确认费用 · 未设标准单价',
  'job.profit.ownCrewCost': '自有团队 · 人工不计成本',
  'job.profit.gap.own_crew_unrated': '自有团队的某行尚无标准单价：请在 设置 → 安装标准单价 中设定。无需与团队确认；在此之前利润不含该项人工。',
  'job.profit.feeTitle': '安装师傅费用',
  'job.profit.payable': '应付 {name}',
  'job.profit.exGst': '不含 GST',
  'job.profit.pay.cash': '现金支付',
  'job.profit.pay.transfer': '银行转账',
  'job.profit.pay.unset': '安装师傅账户未登记付款方式',
  'job.profit.col.work': '工作',
  'job.profit.col.qty': '数量',
  'job.profit.col.rate': '单价',
  'job.profit.col.fee': '费用',
  'job.profit.col.basis': '依据',
  'job.profit.extraValue': '单价',
  'job.profit.extraLabelHint': '例如：搬冰箱和洗衣机',
  'job.profit.confirmFee': '确认费用并短信通知 {name}',
  'job.profit.confirm.needsLines': '每一行都有费用后才能通知师傅',
  'job.profit.confirm.preparing': '正在固定费用行并准备短信…',
  'job.profit.confirm.failed': '无法准备短信：{message}',
  'job.profit.confirm.title': '确认后将发送给师傅的短信',
  'job.profit.confirm.to': '收件人',
  'job.profit.confirm.hold': '静音时段：悉尼时间 {time} 发送',
  'job.profit.confirm.now': '立即发送',
  'job.profit.confirm.send': '确认 {total} 并发送',
  'job.profit.confirmed': '费用已于 {date} 确认 · {total} · 短信{state}',
  'job.profit.confirmedChanged': '此后费用行有变动；请再次确认以短信通知更新',
  'job.profit.recorded': '原记录 {quantity} · {fee}；现按报价书数量计算',
  'job.profit.comm.queued': '已排队',
  'job.profit.comm.sending': '发送中',
  'job.profit.comm.submitted': '已发送',
  'job.profit.comm.failed': '失败',
  'job.profit.comm.uncertain': '待核实',
  'job.profit.variation.open': '师傅于 {date} 提出异议',
  'job.profit.variation.proposed': '他们认为应为 {amount}',
  'job.profit.variation.accept': '接受',
  'job.profit.variation.decline': '不接受',
  'job.profit.variation.acceptDefault': '已接受；正在调整费用行',
  'job.profit.variation.declineNeedsReason': '不接受前请写明师傅会看到的理由',
  'job.profit.variation.notePlaceholder': '师傅会看到的回复…',
  'job.profit.variation.help': '接受本身不改变任何数字：调整费用行后再次确认，更正会以短信发出。不接受则保留已确认的费用，并把您的理由发到他们的门户。',
  'job.profit.variation.accepted': '异议已于 {date} 接受：请调整费用行并再次确认',
  'job.profit.variation.declined': '异议已于 {date} 不接受',
  'communications.discard': '放弃草稿',
  'communications.discardReason': '不再发送：在沟通台放弃',
  'job.savedQuotes': '已存报价历史',
  'job.workingUnavailable': '无法加载当前方案。',
  'job.workingNone': '尚无已存工作方案',
  'job.workingUnpriced': '价格暂不可用或更新中',
  'job.workingHelp': '最新已保存的工作范围。此处不显示未保存的修改。已接受报价和已记录工作金额分别保留。',
  'proposal.accept.revisionRule': '记录客户对工作版本 {version} 的同意。更新现有工作，不发送消息或私人链接。',
  'proposal.accept.confirmRevision': '记录修订接受',
  'proposal.accept.savedRevision': '修订方案已接受：{amount} + GST，更新现有工作 {ref}。',
  'proposal.accept.reviseJob': '将此次修订接受至现有工作 {ref}。保留原已接受报价的历史记录，并更新工作金额。',
  'proposal.source.title': '原始已存报价 · 历史记录',
  'proposal.source.explanation': '此处仅为历史记录。当前产品、底垫和数量请查看工作范围与价格及产品与定价。您的编辑不会改动原始已存报价。',
  'proposal.source.multiple': '此处记录了多份报价或版本。每份均为独立记录，不会合并为一张订单。',
  'proposal.source.empty': '原报价未记录施工范围或计价项目。系统未推测任何项目或价格。',
  'proposal.source.included': '已包含／不另收费',
  'proposal.source.totals': '小计 {subtotal} · GST {gst} · 合计 {total}',

  // ── 共用 ──────────────────────────────────────────────────────────────────
  'job.address.add': '添加地址',
  'job.address.label': '工程现场地址',
  'job.address.suggestions': '地址建议',
  'job.address.failed': '地址未保存，请重试。',
  'job.address.manual': '暂时无法获取地址建议，您仍可手动输入并保存地址。',
  'address.attribution': '地址数据 © Geoscape Australia',
  'address.resolving': '正在读取所选地址…',
  'property.show': '物业信息',
  'property.loading': '正在读取物业记录…',
  'property.noMatch': '没有与该地址匹配的物业记录。',
  'property.unavailable': '目前无法获取物业信息。',
  'property.notInPlan': '物业信息需要 Geoscape 的 Buildings 与 Land Parcels 产品，当前套餐未包含。',
  'property.matched': '物业记录：',
  'property.matchedExact': '物业记录（地址与区域均匹配）：',
  'property.matchedNearest': '最接近的物业记录——请确认是同一处房屋：',
  'property.dwelling': '建筑',
  'property.building': '建筑物',
  'property.noBuilding': '未记录建筑轮廓',
  'property.strata': '分契 / 多个地址',
  'property.floors': '楼层',
  'property.floorArea': '建筑面积',
  'property.footprint': '占地面积',
  'property.lotArea': '地块面积',
  'property.lotPlan': '地块 / 图则',
  'property.zoning': '规划用途',
  'property.roof': '屋顶',
  'property.pool': '泳池',
  'property.solar': '太阳能板',
  'property.addresses': '楼内地址数',
  'property.yes': '有',
  'property.no': '无',
  'common.save': '保存',
  'common.saving': '保存中…',
  'common.remove': '移除',
  'common.add': '添加',
  'common.close': '关闭',
  'common.none': '无',
  'common.notRecorded': '未记录',
  'common.quantity': '数量',
  'common.subtotal': '小计',
  'common.gst': '消费税（GST）',
  'common.total': '合计',
  'common.material': '材料',
  'common.labour': '人工',
  'common.sell': '售价',
  'common.from': '{amount} 起',
  'common.toBeConfirmed': '待确认',

  // ── 左侧导航 ──────────────────────────────────────────────────────────────
  'rail.sections': '功能分区',
  'rail.group.work': '日常工作',
  'rail.group.grow': '业务增长',
  'rail.group.supply': '供应',
  'rail.group.system': '系统',
  'rail.today': '运营总览',
  'rail.serviceLibrary': '服务库',
  'rail.website': '网站运营',
  'rail.emma': 'EmmA 工作台',
  'rail.leads': '线索收件箱',
  'rail.pipeline': '销售管道',
  'rail.catalogue': '产品目录',
  'rail.priceMyFloor': '地板报价',
  'rail.followUps': '跟进',
  'rail.contacts': '联系人',
  'rail.accounts': '账户',
  'rail.jobs': '工程',
  'rail.documents': '文档',
  'rail.calendar': '日历',
  'rail.marketing': '市场推广',
  'rail.members': '会员',
  "rail.hipages": "HiPages 机会",
  "hipage.title": "HiPages 订单机会",
  "hipage.lede": "EmmA 对监控看到的每个订单机会给出判断。仅供参考——自动接单已关闭，系统不会替您接单。",
  "hipage.autoAcceptOff": "自动接单已关闭",
  "hipage.window": "最近 {days} 天",
  "hipage.offers": "订单机会",
  "hipage.emmaYes": "EmmA：接",
  "hipage.emmaNo": "EmmA：不接",
  "hipage.emmaUnsure": "EmmA：不确定",
  "hipage.emmaPending": "等待 EmmA 判断",
  "hipage.potentialYes": "EmmA 建议接单的潜在收入",
  "hipage.plusGst": "{amount} + GST 税",
  "hipage.filter.all": "全部",
  "hipage.filter.yes": "EmmA 建议接",
  "hipage.filter.no": "EmmA 建议不接",
  "hipage.filter.pending": "等待 EmmA 判断",
  "hipage.credits": "{n} 积分",
  "hipage.potential": "潜在收入",
  "hipage.detail.brief": "工作描述",
  "hipage.detail.timing": "时间要求",
  "hipage.detail.action": "HiPages 操作",
  "hipage.detail.emmaReason": "EmmA 的理由",
  "hipage.detail.confidence": "把握 {pct}%",
  "hipage.detail.yourRuling": "您的判断",
  "hipage.detail.noRuling": "未判断",
  "hipage.reaction.median": "EmmA 反应时间中位数",
  "hipage.reaction.title": "EmmA 反应时间",
  "hipage.reaction.seconds": "{n} 秒",
  "hipage.reaction.minutes": "{n} 分钟",
  "hipage.reaction.hours": "{n} 小时",
  "hipage.reaction.days": "{n} 天",
  "hipage.reaction.caughtUp": "事后补判（{time}）",
  "hipage.reaction.lateCount": "{n} 个事后补判，未计入",
  "hipage.ruling.yes": "接",
  "hipage.ruling.no": "不接",
  "hipage.ruling.unsure": "不确定",
  "hipage.detail.noReason": "EmmA 尚未判断这个订单机会。",
  "hipage.basis.trade": "同工种 {n} 个历史 HiPages 工作的中位数（不含 GST）。这是同类工作以往的价值，不是报价。",
  "hipage.basis.all": "该工种历史工作太少——按全部 {n} 个历史 HiPages 工作的中位数（不含 GST）。不是报价。",
  "hipage.empty": "此期间没有订单机会。",
  "hipage.noBrief": "未记录描述。",
  "hipage.loading": "正在加载订单机会…",
  "hipage.loadError": "无法加载 HiPages 订单机会。请刷新后再使用本页。",
  'rail.suppliers': '供应商与库存',
  'rail.inventory': '库存',
  'rail.installers': '安装工',
  'rail.mail': '邮件',
  'rail.communications': '沟通中心',
  'communications.kicker': '办公室 · EmmA 准备，你来发出',
  'communications.title': '沟通中心',
  'communications.lede': '把客户、供应商和安装团队的沟通与工程、报价、采购单和安装安排放在一起。',
  'communications.loading': '正在载入沟通记录…',
  'communications.refresh': '刷新',
  'communications.filter.all': '全部',
  'communications.filter.customer': '客户',
  'communications.filter.supplier': '供应商',
  'communications.filter.installer': '安装团队',
  'communications.conversations': '对话',
  'communications.empty': '此视图暂无沟通记录。',
  'communications.choose': '请选择一项对话。',
  'communications.official': '正式记录',
  'communications.supporting': '辅助记录',
  'communications.review': '发送审核',
  'communications.noDraft': '这项对话目前没有已准备的草稿。',
  'communications.askDraft': '请 EmmA 起草',
  'communications.askRevise': '请 EmmA 修改',
  'communications.to': '收件人',
  'communications.channel': '渠道',
  'communications.subject': '主题',
  'communications.approveSend': '批准并发送',
  'communications.requestApproval': '发送此消息',
  'communications.requesting': '发送中…',
  'communications.sending': '正在排队…',
  'communications.queued': '已批准并进入发送队列。',
  'communications.notQueued': '消息未进入发送队列。请先查看当前状态再重试。',
  'mail.showMore': '再看 {n} 封',
  'mail.fileIt': '归档',
  'mail.armAndFile': '启用规则并归档',
  'mail.fileWhere': '归档到',
  'mail.done': '已处理',
  'mail.putBack': '放回队列',
  'mail.handled': '已处理',
  'mail.askDraft': '复制到聊天',
  'mail.copied': '已复制 ✓',
  'mail.draftLead': '请帮我起草这封邮件的回复：',
  'mail.copyFailed': '复制失败——请打开聊天自行粘贴。',
  'mail.kicker': '邮件 · EmmA 已替你读过',
  'mail.title': '来了什么，她怎么处理',
  'mail.lede': '所有信箱都已读取并打标。这里显示哪些需要你、哪些等你定规则、哪些已经归档。',
  'mail.loading': '正在读取她已经读过的邮件……',
  'mail.unavailable': '无法读取邮件：{message}',
  'mail.vocabularyUnavailable': '她的标签词表读不到，所以规则面板是空的而不是错的。下面的邮件仍是她的读取结果。',
  'mail.verdictNeed': '共收到 {total} 封，其中这些需要人来看。',
  'mail.verdictClear': '没有需要你的。其余都自动归档了。',
  'mail.allBoxes': '全部信箱',
  'mail.needsYou': '需要你',
  'mail.waitingRule': '等你定规则',
  'mail.noneNeed': '目前没有需要你的。',
  'mail.noneWaiting': '每个标签都有规则了——没有在等你的。',
  'mail.job': '工程',
  'mail.tag.supplier_order_confirmation': '供应商订单',
  'mail.tag.supplier_invoice': '供应商发票',
  'mail.tag.supplier_quote': '供应商报价',
  'mail.tag.supplier_product_update': '供应商更新',
  'mail.tag.customer_job_lead': '客户线索',
  'mail.tag.action_required_admin': '需要人处理',
  'mail.tag.marketing_newsletter': '推广邮件',
  'mail.tag.payment_confirmation': '付款',
  'mail.tag.needs_attention': '需要关注',
  'mail.filedByRule': '已按规则归档',
  'mail.needsRule': '还没有规则——请你决定',
  'mail.needsYourCall': '等你决定',
  'mail.nothingToDo': '已读，无需处理',
  'mail.armRule': '启用规则',
  'mail.why': '为什么在这里',
  'mail.whyNeeds': '她标记为需要人看——有钱、日期、问题或承诺在等。',
  'mail.whyFiled': '已启用的规则覆盖此标签，且这封邮件在你启用之后到达。',
  'mail.whyWaiting': '她已打标，但没有已启用的规则覆盖该标签——所以没有移动。',
  'mail.readBy': '读取方式',
  'mail.readByUnknown': '未记录',
  'mail.rules': '清理规则',
  'mail.rulesMeta': '由你启用',
  'mail.noFolder': '未设置文件夹',
  'mail.noRules': '还没有规则。她负责打标；规则才决定归档。',
  'mail.vocabulary': '她的词表',
  'mail.tagCount': '{n} 个标签',
  'mail.proposed': '她提出了 {n} 个新标签——“{tag}”。尚未用于任何邮件。',
  'mail.filedLine': '已自动归档 {n} 封',
  'mail.filedWhy': '其中没有需要人的',
  'rail.purchaseOrders': '订单',
  'rail.buildTracker': '构建记录',
  'rail.settings': '设置',

  // ── 顶栏 ──────────────────────────────────────────────────────────────────
  'topbar.newLead': '+ 新建线索',
  'topbar.casesWaiting': '有 {count} 个 EmmA 待办',
  'topbar.search': '搜索联系人、工程、区域或编号…',
  'topbar.mock': '演示',
  'topbar.live': '实时',
  'topbar.testSet': '测试集…',
  'topbar.production': '正式',
  'topbar.language': '语言',
  'topbar.languageSwitch': '切换语言',
  'topbar.textSize': '文字大小',
  // Bug / Improvement — 每页顶栏的按钮（Jacky，2026-10-12）。
  'feedback.button': '报错 / 改进',
  'feedback.title': '报错或改进建议',
  'feedback.intro': '用你自己的话说明哪里有问题或想要什么。当前页面会一并记录。',
  'feedback.kind.bug': '报错',
  'feedback.kind.improvement': '改进',
  'feedback.describe': '需要修复或改进的内容',
  'feedback.placeholder.bug': '发生了什么，以及本应发生什么……',
  'feedback.placeholder.improvement': '怎样能让这个页面更好……',
  'feedback.point': '在页面上指出来',
  'feedback.pointing': '点击你指的页面部分。按 Esc 停止。',
  'feedback.pointed': '已指出',
  'feedback.remove': '移除',
  'feedback.page': '页面',
  'feedback.send': '记录',
  'feedback.sending': '记录中……',
  'feedback.recorded': '已记录。EmmA 已收到。',
  'feedback.pending': '已记录，待在 EmmA 工作台审阅。',
  'feedback.failed': '未记录——{reason}',
  'feedback.recent': '最近的报告',
  'feedback.none': '还没有报告。',
  'feedback.loadFailed': '无法加载报告。',
  'feedback.status.open': '待处理',
  'feedback.status.acknowledged': '已查看',
  'feedback.status.done': '已完成',
  'feedback.status.declined': '已拒绝',
  'webTraffic.title': '网站流量',
  'webTraffic.meta': '匿名页面计数 · 无 Cookie，无个人数据',
  'webTraffic.range.7': '7 天',
  'webTraffic.range.30': '30 天',
  'webTraffic.range.90': '90 天',
  'webTraffic.pageViews': '页面浏览量',
  'webTraffic.perDay': '平均每天 {count} 次',
  'webTraffic.daily': '每日页面浏览量',
  'webTraffic.topPages': '热门页面',
  'webTraffic.topReferrers': '访客来源',
  'webTraffic.direct': '直接访问或未知',
  'webTraffic.devices': '设备',
  'webTraffic.device.phone': '手机',
  'webTraffic.device.tablet': '平板',
  'webTraffic.device.desktop': '电脑',
  'webTraffic.device.unknown': '未知',
  'webTraffic.actions': '网站操作',
  'webTraffic.event.book_submitted': '已提交的上门测量预约',
  'webTraffic.event.self_quote_carried': '转入预约的自助报价',
  'webTraffic.event.member_link_requested': '已申请的会员登录链接',
  'webTraffic.none': '此时间范围内还没有页面浏览计数。',
  'webTraffic.loading': '正在读取计数……',
  'webTraffic.failed': '无法读取网站流量：{reason}',
  'webTraffic.footnote': '每次页面加载计一次。不识别访客，因此这是页面总数，不是人数。已知的爬虫不计入。可以问 EmmA：“这周混合地板页面有多少访问”。',
  'textSize.compact': '紧凑',
  'textSize.standard': '标准',
  'textSize.comfortable': '舒适',
  'textSize.large': '大',
  'textSize.xlarge': '特大',
  'attention.explanation': '此处显示提醒数量所包含的审批、线索和逾期任务。',
  'attention.loading': '正在加载待处理事项…',
  'attention.failed': '部分待处理事项无法加载，列表可能不完整。',
  'attention.empty': '目前没有需要立即处理的事项。',
  'attention.leads': '待处理线索',
  'attention.openLead': '打开线索',
  'attention.overdue': '逾期跟进',
  'attention.openFollowUp': '打开跟进',
  'attention.allFollowUps': '查看所有跟进',
  'attention.materials': '待处理材料',
  'attention.discard': '放弃',
  'attention.discardReason': '不再发送：已从需立即处理中放弃',
  'topbar.immediateAttentionLabel': '需立即关注',
  'topbar.immediateAttention': '需立即关注 · {count} 项',

  // ── 方案编辑 ──────────────────────────────────────────────────────────────
  'proposal.kicker': '销售管道 · 报价方案',
  'proposal.lede': '客户需求 → 现场勘测 → 施工范围。下一步是定价。',
  'proposal.loading': '正在加载方案…',
  'proposal.unavailable': '方案无法打开',
  'proposal.pipeline': '销售管道',
  'proposal.unsaved': '有未保存的修改',
  'proposal.savedVersion': '已保存版本 {version}',
  'proposal.customerVersion': '客户版本 {version}',
  'proposal.privateDraft': '内部草稿',
  'proposal.saveDraft': '保存草稿',
  'proposal.layoutPreview': '仅界面预览 · 保存与客户链接已停用。',
  'proposal.extraWorkSummary': '附加工程 · {count} 项独立项目',
  // 按现有地板拆除（2026-10-16）：全场一种地板，只登记不同的房间。
  'removal.title': '现有地板',
  'removal.hint': '大多数现场全屋是同一种地板。设置一次，然后只添加不同的房间。',
  'removal.site': '全场现有地板',
  'removal.notRecorded': '未记录',
  'removal.none': '无（无需拆除）',
  'removal.exceptions': '现有地板不同的房间',
  'removal.sameAsSite': '与全场相同（{floor}）',
  'removal.remove': '移除',
  'removal.addRoom': '+ 添加一个不同的房间',
  'removal.pickRoom': '选择房间',
  'removal.pickFloor': '选择其现有地板',
  'removal.summary': '拆除项目：{groups}',
  'removal.noLine': '请在下方附加工程中添加“拆除与处理”；其项目将按这些地板生成。',
  'removal.unrecorded': '部分房间未记录现有地板，未计入拆除。',
  'proposal.materialAudit': '已保存材料计算 · 审核明细',
  'proposal.siteAddress': '施工地址',
  'proposal.siteAddressHint': '可直接输入，或从该客户已有地址中选择——客户地址、本工单工地，或我们曾上门的地点。',
  'proposal.tab.proposal': '方案',
  'proposal.tab.brief': '客户需求',
  'proposal.tab.assessment': '现场勘测',
  'proposal.tab.products': '产品与定价',
  'proposal.tab.share': '分享',
  'proposal.acceptedEyebrow': '已确认 v{version} · {when} · {who}',
  'proposal.acceptedCustomer': '客户',
  'proposal.acceptedCost': 'Artemis 成本合计',
  'proposal.acceptedMargin': '毛利 {rate}%（按售价计）',
  'proposal.acceptedTotal': '合计（不含 GST）',
  'proposal.acceptedTitle': '已确认的方案',
  'proposal.acceptedAmended': '确认后于 {when} 更改：{what}',
  'proposal.acceptedOpen': '打开已确认的副本',
  'proposal.acceptedJob': '工程 {ref}',
  'proposal.acceptedNote': '这就是协议：客户确认了这些明细和总价。下方的工作草稿不会改变它。同价更换颜色通过 EmmA 完成；其他更改需发布新版本由客户确认。',
  'proposal.acceptedDraftNote': '下方为工作草稿 — 不是协议。',
  'proposal.overview.blurb': '概述由客户需求与现场勘测生成。仅在客户有特殊要求时补充文字。',
  'proposal.overview.noSelection': '尚未选择产品或价格。',
  'proposal.overview.selection': '已选 {count} 个产品 — 具体金额见「产品与定价」。',
  'proposal.overview.selectionPlural': '已选 {count} 个产品 — 具体金额见「产品与定价」。',
  'proposal.customWording': '特殊要求 · 手动修改方案文字',
  'proposal.scope.brief': '您的需求',
  'proposal.scope.included': '施工范围',
  'proposal.scope.excluded': '不包含',
  'proposal.scope.customer': '客户需配合事项',
  'proposal.scope.assumptions': '需确认的前提',
  'proposal.scope.empty': '完成客户需求或现场勘测后，此处自动生成。',
  'proposal.customPreserved': '手动修改的文字会保留。关闭「特殊要求」编辑后，将重新采用最新的客户需求与现场勘测。',
  'proposal.overview.tail': '产品、选配、延米计价、报价单 PDF 与客户确认属于下一步定价讨论。销售预算与内部备注不会外泄。',
  'proposal.brief.blurb': '选填 · 仅销售内部可见。不确定的信息可以留空。',
  'proposal.share.title': '发布客户版本',
  'proposal.share.blurb': '客户看到的是介绍、现场勘测、施工范围，以及选定产品后的报价。销售简报、成本与所有毛利均不公开。对外发布的内容受白名单限制：只有售价，没有成本、没有供应商、没有毛利、没有 0 元行。',
  'proposal.share.saveFirst': '先保存草稿，再发布此版本。生成链接不会发送任何消息，也不会创建工程。',
  'proposal.share.ready': '报价已就绪 · {count} 个产品行',
  'proposal.share.readyPlural': '报价已就绪 · {count} 个产品行',
  'proposal.share.blocked': '尚有未定价的行，暂不能发布',
  'proposal.share.totalExGst': '金额：${amount} + GST',
  'proposal.share.fromExGst': '${amount} 起 + GST',
  'proposal.share.totalTbc': '合计待确认',
  'proposal.share.publish': '生成私密方案链接',
  'proposal.share.republish': '通过原私密链接发布新版本',
  'proposal.share.originalLink': '原客户链接仍然有效。请粘贴原链接，以便在此浏览器预览或再次发送。',
  'proposal.share.restoreLink': '使用原链接',
  'proposal.share.revoke': '撤销客户访问',
  // 按成本公开发布（2026-10-15）：成本取自产品目录，毛利仅在合计处计入一次。
  'proposal.share.openBook': '按成本公开发布（成本 + 20% 毛利）',
  'proposal.share.copied': '私密链接已复制，未发送任何消息。',
  'proposal.share.copyHint': '请选中上方链接并复制。',
  'proposal.share.copy': '复制链接',
  'proposal.share.view': '查看客户版本',
  'proposal.share.printPdf': '打印 / 保存 PDF',
  'proposal.share.pdfDrawing': '正在生成 PDF……',
  'proposal.pdfBlocked': 'PDF 已生成，但浏览器拦截了新窗口；请允许 CRM 的弹出窗口后重试。',
  'proposal.share.pdfFailed': '无法生成 PDF——{reason}',
  'proposal.share.pdfBlocked': 'PDF 已生成但无法在此打开；请在浏览器中打开 {url}。',
  'commercial.printDesktopHint': '桌面应用中无法打印。请先保存草稿，然后在“分享”步骤使用“打印 / 保存 PDF”。',
  'proposal.share.after': '报价单 PDF 与客户确认在下一步讨论。复制链接后，可用现有邮件／短信工具发送，发送前请自行核对内容。',

  // ── 2.1 产品选择 ──────────────────────────────────────────────────────────
  'pricing.selection.title': '将要铺设的产品',
  'pricing.selection.none': '尚未选择',
  'pricing.selection.count': '已选 {count} 项',
  'pricing.selection.countPlural': '已选 {count} 项',
  'pricing.selection.empty': '请从右侧产品库中选择产品。选择产品与定价是同一步 — 未定价的方案无法发送给客户。',
  'catalogue.documents': '产品文档',
  'catalogue.specialOrder': '特殊订购',
  'catalogue.openPdf': '打开 {title} PDF',
  // Catalogue specifications by family (25 Oct 2026): every field the website's spec tables record, gaps shown.
  'catalogue.spec.notRecorded': '未记录',
  'catalogue.spec.missing': '{count} 项未记录',
  'catalogue.spec.yes': '是',
  'catalogue.spec.no': '否',
  'catalogue.spec.thickness_mm': '厚度',
  'catalogue.spec.wear_layer_mm': '耐磨层',
  'catalogue.spec.length_mm': '板长',
  'catalogue.spec.width_mm': '板宽',
  'catalogue.spec.install_methods': '安装方式',
  'catalogue.spec.install_method': '安装方式',
  'catalogue.spec.subfloor_flatness': '地面平整度',
  'catalogue.spec.pattern': '铺贴图案',
  'catalogue.spec.warranty_residential_yrs': '住宅保修',
  'catalogue.spec.warranty_commercial_yrs': '商用保修',
  'catalogue.spec.species': '木种',
  'catalogue.spec.grade': '等级',
  'catalogue.spec.finish': '表面处理',
  'catalogue.spec.edge_profile': '边缘倒角',
  'catalogue.spec.slip_rating': '防滑等级',
  'catalogue.spec.roll_width_m': '卷宽',
  'catalogue.spec.antimicrobial': '抗菌',
  'catalogue.spec.fibre': '纤维',
  'catalogue.spec.fibre_branded_name': '品牌纤维',
  'catalogue.spec.pile_type': '绒头类型',
  'catalogue.spec.pile_weight_gsm': '绒头重量',
  'catalogue.spec.total_weight_gsm': '总重量',
  'catalogue.spec.backing_type': '背衬',
  'catalogue.spec.stain_treatment': '防污处理',
  'catalogue.spec.wear_rating': '耐磨评级',
  'catalogue.spec.tile_size_cm': '方块尺寸',
  'catalogue.spec.light_reflectance': '光反射率',
  'catalogue.spec.ac_rating': 'AC耐磨等级',
  'catalogue.spec.water_resistance': '防水性能',
  'catalogue.spec.underlay_attached': '自带底垫',
  'catalogue.spec.welded_seams': '焊缝',
  'catalogue.spec.years': '{n} 年',
  'pricing.catalogue.title': '产品库',
  'pricing.catalogue.costNote': '成本仅供员工查看，绝不向客户展示。',
  'pricing.catalogue.find': '查找产品',
  'pricing.catalogue.findPlaceholder': '名称、供应商、类型',
  'pricing.catalogue.reading': '正在读取产品库…',
  'pricing.catalogue.count': '显示 {shown} / {total} · {priced} 个可定价',
  'pricing.catalogue.colours': '客户可选 {count} 种颜色',
  'pricing.catalogue.coloursPlural': '客户可选 {count} 种颜色',
  'pricing.catalogue.perUnit': '每{unit}',
  'pricing.catalogue.manualAssessment': '需人工评估',
  'pricing.catalogue.noCost': '暂无成本',
  'pricing.catalogue.pricePerJob': '按工程定价',
  'pricing.catalogue.codBasis': 'COD 价基',
  'pricing.services.title': '拆除、清运与现场作业',
  'pricing.services.savedLegacy': '已保存的旧版定价服务',
  'pricing.services.blurb': '费率来自配置，不在此页面修改。',
  'pricing.services.empty': '本方案尚未添加服务项。',
  'pricing.services.addRate': '添加费率',
  'pricing.services.choose': '请选择…',
  'pricing.services.noSellPrice': '未设售价',
  'pricing.services.jobMinimum': '工程最低收费',
  'pricing.services.notApplied': '不适用',
  'pricing.services.customerRemoves': '客户自行拆除',
  'pricing.services.sellPer': '{amount}/{unit}',
  'pricing.services.accessTitle': '进场条件',
  'pricing.services.access': '进场方式',
  'pricing.services.house': '独立屋 — 地面层',
  'pricing.services.elevator': '电梯',
  'pricing.services.stairs': '楼梯',
  'pricing.services.above3': '三层以上楼梯',
  'pricing.services.weight': '产品重量 · 公斤',
  'pricing.services.parking': '卸货后再开去停车',
  'pricing.line.missingProduct': '该产品已不在产品库中',
  'pricing.line.missingProductHint': '请移除此行并重新选择现行产品',
  'pricing.line.colour': '颜色',
  'pricing.line.basis': '计价方式',
  'pricing.line.supplyInstall': '供货并安装',
  'pricing.line.supply': '仅供货',
  'pricing.line.install': '仅安装',
  'pricing.line.rangeNotice': '未选定颜色前，此报价为价格区间。',
  'pricing.line.noColours': '该产品未记录客户可选颜色，将按成本价基计价。',
  'pricing.line.onThisLine': '此行颜色：{colour}',
  'pricing.line.rooms': '房间',
  'pricing.line.roomsHint': '— 面积取自现场勘测，无需重复输入',
  'pricing.line.noRooms': '现场勘测尚未记录房间。',
  'pricing.line.allRooms': '全部房间',
  'pricing.line.comesWith': '随该产品配套——勾选即包含',
  'pricing.line.waste': '实测 {measured} m² ＋ {percent}% 损耗 = 计价 {charged} m²。最终按实际用量结算。',
  'pricing.line.areaBreakdown': '铺装面积与损耗计算',
  'pricing.line.netMeasured': '扣除不铺装区域后的面积',
  'pricing.line.wasteAllowance': '裁切及损耗 · {percent}%',
  'pricing.line.pricedArea': '计价面积',
  'pricing.line.quantityDiffers': '已存数量 {actual} 平方米与当前计算的 {expected} 平方米不一致。发布前请更新产品选择。',
  'customer.area.drawn': '各房间图示总面积',
  'customer.area.cutouts': '固定设施：地板铺设止于此处',
  'customer.area.nested': '另行计量的房间',
  'customer.area.frames': '门洞内地面',
  'customer.area.robes': '衣柜地面 — 实测 {area} 平方米，按 80% 计',
  'customer.area.measured': '扣除不铺装区域后的面积',
  'customer.area.wastage': '裁切及损耗 · {percent}%',
  'customer.area.charged': '计价面积',
  'customer.price.quantityRate': '{quantity} {unit} × 每{unit} {rate}',
  'customer.area.quotedQuantity': '报价产品数量',
  'customer.area.chargedBreakdown': '计价面积 — 实测 {measured} m² + 损耗 {waste} m²',
  'customer.proposal.historyMissing': '下方显示已接受的工作范围和价格。早期图纸及文件说明未保存在版本历史中。',
  'customer.carpet.reviewPending': '地毯裁切方案仍待现场最终复核。Artemis 会在接受前发出修正后的报价。',
  'customer.carpet.diagramMissing': '{floor}：此报价尚未保存实际裁切形状。以下长度只是裁切清单，并非图示。',
  'customer.carpet.reuse': '预留作楼梯模板的余料',
  'customer.carpet.unallocated': '未分配；损耗尚未核实',
  'customer.carpet.included': '已包含',
  // 按成本公开报价（2026-10-15）：每行背后的成本明细，以及合计处唯一一行毛利。
  'customer.openBook.items': '成本明细',
  'customer.openBook.item': '项目',
  'customer.openBook.qty': '数量',
  'customer.openBook.uom': '单位',
  'customer.openBook.unitCost': '单价',
  'customer.openBook.subtotal': '小计',
  'customer.openBook.costSubtotal': 'Artemis 成本合计',
  'customer.openBook.margin': '毛利 {percent}%（按售价计）',
  // 客户方案页面使用 Artemis 为该客户设定的语言（2026-10-15）。产品、供应商与客户资料一律不翻译。
  'customer.doc.title': '地板方案',
  'customer.doc.brandLine': 'Artemis Flooring · 悉尼',
  'customer.doc.preparedFor': '致',
  'customer.doc.you': '您',
  'customer.doc.toBeConfirmed': '待确认',
  'customer.doc.workIncluded': '本价格包含的工作',
  'customer.doc.floorProposed': '我们建议的地板',
  'customer.doc.perUnit': '{rate} / {unit}',
  'customer.doc.carpetCharged': '地毯计价数量：',
  'customer.doc.carpetChargedCalc': '{lm} 整米 × {width} 米整卷宽 =',
  'customer.doc.carpetCuts': '{cuts} 段实际裁切共 {total} 米，其中含 {allowance} 米铺装余量（{parts} 米）。按整米取整另加 {rounding} 米。',
  'customer.doc.measuredNote': '在您的现场逐间测量。最终按确认的测量结果与约定的材料订购量结算。',
  'customer.doc.included': '包含内容',
  'customer.doc.notIncluded': '不包含',
  'customer.doc.nothingExcluded': '上述范围之外没有书面排除项；当天发现的任何情况都会先向您说明，再进行处理。',
  'customer.doc.areasTitle': '铺装区域 — 已在您的平面图上标出',
  'customer.doc.legendFloor': '平面图上标出的铺装区域',
  'customer.doc.legendUChannel': 'U 型收边条 — 配色',
  'customer.doc.legendScotia': '压条 — 配色',
  'customer.doc.legendSkirting': '踢脚线',
  'customer.doc.legendCarpetTrim': '已测量的地毯收边',
  'customer.doc.legendNotIn': '不在本方案内',
  'customer.doc.carpetLayoutTitle': '地毯铺设与用料',
  'customer.doc.carpetLayoutIntro': '同一连通楼层保持同一绒向。每段裁切长度均含铺装余量；订购量按整米向上取整一次。',
  'customer.doc.cutA': '第 1 / 3 段',
  'customer.doc.cutB': '第 2 / 4 段',
  'customer.doc.carpetCut': '{label}：{length} 米 × {width} 米卷宽；含 {allowance} 米余量',
  'customer.doc.purchasedCarpet': '采购地毯',
  'customer.doc.flatFloorUsed': '平面用量',
  'customer.doc.awaitingArea': '待测量面积',
  'customer.doc.stairsFromOffcuts': '楼梯用余料',
  'customer.doc.awaitingStairs': '待核对楼梯样板',
  'customer.doc.remainingWaste': '剩余损耗',
  'customer.doc.wasteAfterStairs': '楼梯样板核对后计算',
  'customer.doc.options': '您的选项',
  'customer.doc.chooseOne': '请在下方高亮的选项中作出选择 — 或告诉我们"这次不需要"。在此之前不会提交任何内容。',
  'customer.doc.chooseMany': '请在下方 {count} 个高亮的选项中逐一作出选择 — 或告诉我们"这次不需要"。在此之前不会提交任何内容。',
  'customer.doc.optional': '可选',
  'customer.doc.chooseBadge': '请选择',
  'customer.doc.notThisTime': '这次不需要',
  'customer.doc.previewMoves': '客户选择时价格会这样变化。',
  'customer.doc.totalUpdates': '顶部的合计会随您的选择更新。在您确认之前不会产生任何费用。',
  'customer.doc.ifNeeded': '当天如有需要',
  'customer.doc.rateToConfirm': '单价待确认',
  'customer.doc.variationAbout': '（按 {qty} {unit} 约 {amount}）',
  'customer.doc.variationLine': '{label} — {rate}{about}，须先经您同意，仅在实际使用时收费。',
  'customer.doc.howTitle': '施工流程',
  'customer.doc.stepsUnconfirmed': '此已保存提案尚未确认施工步骤。安排施工前，请核对现场条件与约定范围。',
  'customer.doc.step1': '逐间拆除现有地面材料。',
  'customer.doc.step2': '检查基层，并告知您检查结果。',
  'customer.doc.step3': '经您确认 — 在您认可检查结果之前不铺装。',
  'customer.doc.step4': '需要的位置铺设防潮层。',
  'customer.doc.step5': '先铺底垫，再铺地板。',
  'customer.doc.step6': '安装配色的压条与收边条，然后清理现场。',
  'customer.doc.price': '您的价格',
  'customer.doc.carpetPurchasedRow': '采购地毯 — {lm} 整米 × {width} 米卷宽',
  'customer.doc.totalExGst': '合计（不含 GST）',
  'customer.doc.deposit': '订购材料前需支付 50% 定金。',
  'customer.doc.footer': 'Artemis Flooring · 联系人 Jacky Shao · 0432 399 295',
  'customer.doc.viewAria': '型材图片视图',
  'customer.doc.viewShape': '截面',
  'customer.doc.viewProduct': '产品',
  'customer.doc.viewInstalled': '安装效果',
  'customer.doc.illustrativeDrawn': '安装效果示意 — 绘制',
  'customer.doc.illustrative': '安装效果示意',
  'customer.doc.chooseHeight': '选择高度',
  'customer.doc.heightIncluded': '已包含在您当前的方案中',
  'customer.doc.heightDelta': '{perLm}/米 · 本方案 {total} + GST',
  'customer.doc.priceBeforeOrder': '订购前确认价格',
  'customer.doc.optionalLater': '现在可选 — Artemis 可在方案之后、施工期间与您确认。',
  'customer.page.signInFailed': '登录未能完成。您的私密链接仍然有效；请再试一次。',
  'customer.page.usePrivateLink': '请使用您的私密链接进行确认。',
  'customer.page.acceptFailed': '确认未完成，请再试一次。',
  'customer.page.requestFailed': '无法完成此请求',
  'customer.page.saveFailed': '保存未完成，请再试一次。',
  'customer.page.saved': '已保存到您的账户。这并不表示接受方案。',
  'customer.page.nameRequired': '请输入您的全名。',
  'customer.page.mobileInvalid': '请输入澳大利亚手机号，或以 + 开头的国际号码。',
  'customer.page.emailSent': '请查收邮件中的登录链接，并在本浏览器中打开以返回此页。若邮件中有验证码，请在下方输入。',
  'customer.page.codeSent': '请输入发送至 {destination} 的验证码。',
  'customer.page.signedIn': '已登录。请在下方保存此方案。',
  'customer.page.unavailable': '方案不可用',
  'customer.page.unavailableHint': '请使用您原来的私密链接，或向 Artemis 索取最新版本。',
  'customer.page.opening': '正在打开您的方案…',
  'customer.page.print': '打印或保存 PDF',
  'customer.page.latestStatus': '最新状态',
  'customer.page.jobAccepted': '工程已确认',
  'customer.page.choicesLocked': '您的选择已成为确认的施工范围，页面按已确认的内容显示。如需更改（包括颜色），请告诉 EmmA 或致电 Artemis，我们会与您一起更新。',
  'customer.page.jobRef': '工程编号：',
  'customer.page.schedule': '安装日程',
  'customer.page.datePending': '安装日期待定。可以排期时 Artemis 会与您联系。',
  'customer.page.emmaTitle': '对方案有疑问？问 EmmA',
  'customer.page.emmaPlaceholder': '例如：底垫包含在内吗？',
  'customer.page.emmaAttach': '添加照片',
  'customer.page.emmaPastRender': 'EmmA 为您生成的效果图',
  'customer.page.emmaAttachHint': '把房间的照片发给 EmmA（也可以直接粘贴），让她把本方案中的地板铺进照片里给您看。效果图需要几分钟，完成后会显示在这里。',
  'customer.page.emmaRemovePhoto': '移除照片',
  'customer.page.emmaExpand': '展开',
  'customer.page.emmaCollapse': '收起',
  'customer.page.emmaDemoYou': '您发来这样的照片',
  'customer.page.emmaDemoHer': 'EmmA 发回这样的效果图',
  'customer.page.emmaDemoSay': '您说："把 Living 铺成 White Oak 给我看看。" EmmA："好的，约三分钟后效果图会出现在这里。"',
  'customer.page.emmaWriting': 'EmmA 正在输入…',
  'customer.page.emmaSend': '发送',
  'customer.page.emmaNote': 'EmmA 是 AI 助理。价格以本方案所写为准；她无法回答的问题会转给 Artemis 办公室。',
  'customer.page.emmaRendering': 'EmmA 正在生成 {room} 的效果图，约 {minutes} 分钟，完成后会显示在这里。',
  'customer.page.emmaRenderFailed': '效果图没有生成成功，请让 EmmA 再试一次。',
  // The customer's plan page (17 Oct 2026): the plan, the tiles EmmA lays out on it.
  'customer.page.planLink': '您的户型图',
  'customer.plan.title': '您的户型图',
  'customer.plan.back': '返回您的方案',
  'customer.plan.saved': '根据建筑商图纸绘制 · 第 {save} 版',
  'customer.plan.backdrop': '显示建筑商原图',
  'customer.plan.fit': '整层',
  'customer.plan.zoomIn': '放大',
  'customer.plan.zoomOut': '缩小',
  'customer.plan.layouts': 'EmmA 画的铺砖图',
  'customer.plan.noLayouts': '问 EmmA 需要多少块砖——她会在这里把砖铺在您的户型图上。',
  'customer.plan.tiles': '共 {total} 块：整砖 {full} 块，切砖 {cut} 块',
  'customer.plan.smallest': '最小切边 {mm} 毫米',
  'customer.plan.reused': '{n} 块切砖用边角料',
  'customer.plan.walls': '墙面展开图',
  'customer.plan.wall': '第 {n} 面墙：{length} × {height} 毫米',
  'customer.plan.legendWhole': '整砖',
  'customer.plan.legendCut': '切砖',
  'customer.plan.loading': '正在加载您的户型图…',
  'customer.plan.noPlan': '您的户型图还没准备好，Artemis 会通知您。',
  'customer.plan.unavailable': '此页面已失效，请致电我们获取新链接。',
  'customer.plan.emmaTitle': '和 EmmA 一起规划铺砖',
  'customer.plan.emmaIntro': '告诉 EmmA 砖的尺寸和铺法，她会在您真实的户型图上算好，并把铺砖图画在这里。',
  'customer.plan.chip1': '卫生间地面需要多少块砖？',
  'customer.plan.chip2': '卫生间墙面也要贴砖',
  'customer.plan.chip3': '所有贴砖地面一共要几箱？',
  'customer.plan.placeholder': '例如：800 × 800 的砖，直铺',
  'customer.plan.note': '数量按建筑商图纸计算，下单前请让铺砖师傅确认。',
  'customer.plan.chip4': '踢脚线需要买多少？',
  'customer.plan.skirtingSummary': '{lm} 延米 · {lengths} 根 {stock} 毫米长料',
  'customer.plan.cutPlan': '踢脚线下料图',
  'customer.plan.length': '第 {n} 根',
  'customer.plan.left': '余 {mm} 毫米',
  'customer.plan.legendInt': '内角（斜切）',
  'customer.plan.legendExt': '外角（斜切）',
  'customer.plan.legendReturn': '收口端',
  'customer.plan.legendDoor': '到门边止',
  // Architrave on the plan page (17 Oct 2026).
  'customer.plan.chip5': '门套线需要买多少？',
  'customer.plan.architraveSummary': '{lm} 延米 · {openings} 个门洞 · {lengths} 根 {stock} 毫米长料',
  'customer.plan.cutPlanArchitrave': '门套线下料图',
  'customer.plan.legendArchitrave': '每个门和门洞：两根 {height} 毫米的立边加一根横头，上角斜切',
  // 铺装视图（2026-10-29）：按真实瓷砖比例显示待铺房间；客户自行尝试方案，并与 EmmA 一起确定。
  'customer.plan.laying': '铺装视图',
  'customer.plan.layoutsMode': 'EmmA 的排版',
  'customer.plan.fitRoom': '整个房间',
  'customer.plan.trueScale': '{room} 按真实比例显示：{tile} 毫米瓷砖铺在您的实测轮廓上',
  'customer.plan.tryIt': '试一试',
  'customer.plan.border': '边框',
  'customer.plan.borderYes': '带边框',
  'customer.plan.borderNo': '不带边框',
  'customer.plan.borderWidth': '边框宽度 {mm} 毫米',
  'customer.plan.borderWidthMm': '边框宽度（毫米）',
  'customer.plan.borderWidthNote': '四周同一宽度，每个转角斜接的两边等长。按毫米微调，用来平衡切割尺寸。',
  'customer.plan.stripToggle': '沿 {run} 米这段铺黑色饰条：填平 {step} 毫米的台阶，边框一直线通过',
  'customer.plan.stripCount': '黑色饰条：{lm} 延米，宽 {mm} 毫米',
  'customer.plan.nibNote': '墙上 {mm} 毫米的凸角不深于边框，边框直接通过，不绕它铺。',
  'customer.plan.legendStrip': '黑色饰条',
  'customer.plan.borderColour': '边框颜色',
  'customer.plan.colourOrder': '起始砖',
  'customer.plan.pattern': '铺法',
  'customer.plan.patternDiagonal': '斜铺',
  'customer.plan.patternStraight': '直铺',
  'customer.plan.datum': '起点',
  'customer.plan.datumCentre': '房间中心',
  'customer.plan.datumCorner': '墙角',
  'customer.plan.edgeFinish': '沿墙收口',
  'customer.plan.scotia': '阴角线',
  'customer.plan.skirting': '踢脚线',
  'customer.plan.legendGoldLine': '金色线条（倒角边）',
  'customer.plan.legendGrout': '缝',
  'customer.plan.legendTrim': '开放边和门口处的过渡压条',
  'customer.plan.counts': '此方案用量',
  'customer.plan.areaLine': '{room}：地面 {area} 平方米',
  'customer.plan.countLine': '{colour}：整砖 {full} 片，切割 {cut} 片',
  'customer.plan.borderCount': '边框：{pieces} 条 {width} 毫米宽，共 {lm} 延米，约 {tiles} 片砖',
  'customer.plan.trimCount': '过渡压条：{lm} 延米',
  'customer.plan.skirtingCount': '{kind}：沿墙 {lm} 延米',
  'customer.plan.countsNote': '用量按您的实测轮廓计算，未加损耗。您确定方案后，由 EmmA 核实订购数量。',
  'customer.plan.keep': '就这个，问问 EmmA',
  'customer.plan.keepMessage': '请保留{room}的这个方案：{pattern}，{order}起铺，{border}，从{datum}开始。每种颜色各需要多少片？是否符合铺装规则？设置：{key}',
  'customer.plan.borderOn': '{colour} {width} 毫米边框',
  'customer.plan.borderOff': '不带边框',
  'customer.plan.olderLayouts': '有 {n} 个方案是在较早版本的图纸上绘制的，未显示。',
  // 户型图页面上 EmmA 的铺装运算（2026-10-29，PR 4）：页面跟随她的每一轮。
  'customer.plan.run.title': 'EmmA 的铺装运算',
  'customer.plan.run.started': 'EmmA 正在排布…',
  'customer.plan.run.pass': '第 {pass} 轮，共 {of} 轮',
  'customer.plan.run.drawn': '{total} 片砖：整砖 {full} 片，切割 {cut} 片 · 最小切割 {mm} 毫米',
  'customer.plan.run.ruleOk': '本轮符合铺装规则。',
  'customer.plan.run.ruleFail': '尚未达标：{list}。EmmA 会调整后再试。',
  'customer.plan.run.passed': '完成：此方案符合铺装规则。',
  'customer.plan.run.budget': 'EmmA 在 {pass} 轮后停止，并保留了最佳方案。',
  'customer.plan.run.failed': 'EmmA 未能完成本次运算：{error}',
  'customer.plan.run.see': '在图纸上查看 EmmA 的方案',
  'customer.plan.run.busy': 'EmmA 已在排布这个房间，显示的是该运算。',
  'customer.plan.run.unavailable': 'EmmA 暂时无法开始运算，请稍后再试。',
  'customer.plan.run.noPlan': '您的户型图尚未保存，EmmA 无法排布。',
  'customer.plan.run.picture': '请 EmmA 出一张效果图',
  'customer.plan.run.pictureMessage': '请用我的瓷砖，按这个方案给我看看{room}地面的效果图。',
  'customer.page.drawings': '图纸',
  'customer.page.drawingsNote': '方案的补充资料：计价以上方的测量面积为准。',
  'customer.page.skirtingTitle': '您工程的踢脚线',
  'customer.page.skirtingSupplyInstall': 'Artemis 供货并安装',
  'customer.page.skirtingInstallOnly': '您供货，Artemis 安装',
  'customer.page.confirmed': '已确认：',
  'customer.page.confirmedWith': '与 {name} 确认。',
  'customer.page.profileToConfirm': '订购前需确认型材与高度。',
  'customer.page.buyingGuide': '采购参考（可选）',
  'customer.page.guideMeasured': '已测量',
  'customer.page.guideExtra': '（另加 {pct}%）',
  'customer.page.guideTotal': '建议共 {lm} 米',
  'customer.page.guideNoLabour': '这不会增加安装人工。',
  'customer.page.guideMissing': '显示采购参考前仍需确认的踢脚线测量数据。Artemis 不会按房间周长估算。',
  'customer.page.happy': '满意吗？',
  'customer.page.happyNote': '确认即表示请我们安排施工。此处不收取任何费用，我们会与您联系约定日期。',
  'customer.page.chooseFirstOne': '请先在上方高亮的选项中作出选择 — 它需要您的答复，包括"这次不需要"。',
  'customer.page.chooseFirstMany': '请先在上方高亮的选项中逐一作出选择 — 每一项都需要您的答复，包括"这次不需要"。',
  'customer.page.thanksAt': '，金额 {amount} + GST',
  'customer.page.thanks': '谢谢 — 您的方案已确认{amount}。我们会与您联系约定日期。',
  'customer.page.fullName': '您的全名',
  'customer.page.confirm': '确认此方案',
  // 按成本公开方案上的逐房间选择（2026-10-15）。产品名、颜色与数字均为数据，不翻译。
  'customer.choose.aria': '逐房间选择您的地板',
  'customer.choose.floorArea': '铺装面积',
  'customer.choose.roomsCount': '{count} 个房间',
  'customer.choose.current': '当前选择',
  'customer.choose.customMix': '自定义搭配',
  'customer.choose.finishing': '收口',
  'customer.choose.skirtingOn': '{size}mm 踢脚线',
  'customer.choose.skirtingOff': '不含踢脚线',
  'customer.choose.archOn': '门套线',
  'customer.choose.archOff': '不含门套线',
  'customer.choose.disposalArtemis': 'Artemis 清运',
  'customer.choose.disposalSkip': '建筑商垃圾箱',
  'customer.choose.presetsTitle': '两种方案',
  'customer.choose.presetChosen': '已选择',
  'customer.choose.presetChoose': '选择此方案',
  'customer.choose.planTitle': '您的平面图 — 点选房间看面积与成本',
  'customer.choose.tapHint': '点选房间，查看该房间的面积、所铺地板与仅此房间的成本。',
  'customer.choose.notIn': '不在本方案内',
  'customer.choose.typeCarpet': '地毯',
  'customer.choose.typeHardFloor': '金刚板',
  'customer.choose.atCost': '成本',
  'customer.choose.rollRun': '卷长 {m} 米',
  'customer.choose.chosen': '已选 · 用于整个工程',
  'customer.choose.useAll': '用于整个工程',
  'customer.choose.changeBoard': '换一款金刚板',
  'customer.choose.jobCost': '整个工程 · Artemis 成本',
  'customer.choose.mainPlank': '{product} · 板材每平米成本',
  'customer.choose.roomCost': '仅此房间 · Artemis 成本',
  'customer.choose.closeRange': '收起',
  'customer.choose.rangeTitle': '金刚板系列 — 整个工程用同一款，按 Artemis 成本',
  'customer.choose.recommends': 'Artemis 推荐',
  'customer.choose.orientation': '铺设方向',
  'customer.choose.orientationHouse': '铺设方向 — 全屋同向，一通到底',
  'customer.choose.alongX': '横向',
  'customer.choose.alongY': '纵向',
  'customer.choose.leastWaste': '损耗最少',
  'customer.choose.installerDecides': '由安装师傅决定',
  'customer.choose.chip1': '这些板材有什么区别？',
  'customer.choose.chip2': '每平米成本是怎么算的？',
  'customer.choose.chip3': '地毯为什么按卷长计算？',
  'customer.choose.chip4': '哪个颜色配我的厨房和门？',
  'customer.choose.emmaDesignRole': 'Artemis Flooring · 板材、铺向、配色',
  'customer.choose.emmaDesignIntro': '告诉 EmmA 房子里已定的东西 — 橱柜、台面、墙漆、门、瓷砖 — 以及您想要的风格。她会说哪款板材和颜色相配、朝哪个方向铺，并在您要求时直接在这里替您选好。',
  'customer.choose.noRoomTitle': '在平面图上点选一个房间',
  'customer.choose.noRoomText': '每个房间会显示面积、所铺地板及其成本。',
  'customer.choose.colourTitle': '颜色',
  'customer.choose.colourNote': '每款产品整个工程使用同一颜色。屏幕上的颜色仅供参考，安装前我们会带样板上门。',
  'customer.choose.skirtingTitle': '踢脚线 — 供货并安装',
  'customer.choose.skirtingRun': '按您的平面图测得 {lm} 米，已扣除门洞',
  'customer.choose.skirtingExcluded': '；不含 {rooms}',
  'customer.choose.skirtingIntro': '地板铺好后安装，紧贴地面。转角斜切拼角；直线段直接对接；开放端做斜切回头收边。顶缝用白色可上漆填缝胶填平。',
  'customer.choose.none': '不需要',
  'customer.choose.skirtingOpt': '{size}mm · 成本 {rate}/米',
  'customer.choose.profileTitle': '款式',
  'customer.choose.archTitle': '门套线 — 门框双面',
  'customer.choose.archIntro': '与踢脚线同一板材，沿每扇门两侧立边及横头安装，45° 斜切拼角。新建房屋：无需拆除。',
  'customer.choose.archRule': '{doors} 扇门安装门套线（{size}mm）。开放式门洞、入户门和宽推拉门不含。',
  'customer.choose.archYes': '需要 · {doors} 扇门',
  'customer.choose.disposalTitle': '边角料清运',
  'customer.choose.disposalIntro': '使用现场建筑商的垃圾箱不产生费用。仅在没有垃圾箱时选择由 Artemis 清运。',
  'customer.choose.disposalSkipRow': '使用现场建筑商的垃圾箱',
  'customer.choose.disposalArtemisRow': '由 Artemis 清运边角料 · {rate}/m²',
  'customer.choose.changesTitle': '与推荐方案的差异',
  'customer.choose.reset': '恢复推荐方案',
  'customer.choose.noChanges': '所有房间均为 {product}。',
  'customer.carpet.measuredCoverage': '实测地面覆盖面积',
  'pricing.line.areaUnknown': '{rooms} 缺少面积 — 请在现场勘测中录入尺寸，才能为此行定价。',
  'pricing.line.rollLength': '订购卷长 · 延米（LM）',
  'pricing.line.rollLengthHint': '按整延米订货。每一刀先计入一次 0.10 米预留，再把总长度向上取整。',
  'pricing.line.rollWidth': '整卷宽度 · 米',
  'pricing.line.purchasedArea': '客户计价数量：{whole} 整延米 × {width} 米 = {area} 平方米订货面积',
  'pricing.line.floorArea': '铺设面积 · 平方米',
  'pricing.line.area': '面积 · 平方米',
  'pricing.line.areaHintCarpet': '地垫与胶水按此面积计价',
  'pricing.line.areaHint': '此类地板数量与面积相同',
  'pricing.line.layMethod': '铺设方式',
  'pricing.line.fromCatalogue': '按产品库 · {rule}',
  'pricing.line.notDetermined': '未确定',
  'pricing.line.thickerUnderlay': '升级加厚地垫',
  'pricing.line.role': '此行属于',
  'pricing.line.recommended': '推荐',
  'pricing.line.option': '客户可选',
  'pricing.line.group': '子类别',
  'pricing.line.noGroup': '未分组',
  'pricing.line.groupHint': '每个子类别选一个 — 同组的其他产品即为客户的可替代选项。',
  'pricing.margin.title': '客户可选产品的加价率',
  'pricing.margin.note': '仅适用于可选产品清单；推荐产品仍按产品库价格。',
  'pricing.margin.kindGm': '毛利率（占售价）',
  'pricing.margin.kindMarkup': '成本加成率',
  'pricing.margin.both': '毛利率 {gm}（占售价）＝ 成本加成 {markup}',
  'pricing.margin.none': '尚未设置加价率 — 可选产品按产品库价格列示。',
  'pricing.margin.unset': '本客户尚未设置',
  'pricing.margin.placeholder': '例如 40',
  'pricing.margin.needed': '有 {count} 个可选产品等待您设定毛利率 — 设定后即按该比率列价。',
  'priced.options.title': '客户可选清单',
  'priced.options.empty': '暂无可选产品。将某行设为「客户可选」，即会按您设定的加价率显示在此处。',
  'priced.options.listedAt': '按 {rate} 列价',
  'priced.options.notInTotal': '可替代选项不计入总价 — 客户选定其一后再重新计价。',
  'priced.options.choose': '选为推荐',
  'priced.options.chosen': '已锁定 {rate} — 此行现为推荐产品，且保留其列示价格。',
  'priced.options.swapNote': '选定其一后，同组的另一项将按您的加价率回到可选清单。',
  'priced.col.rate': '单价',
  'priced.gm.column': '毛利率',
  'priced.gm.title': '毛利率按售价占比计算，且人工按外包安装计价。第二个数字为 ArtemIS 自有班组可额外带来的毛利。',
  'priced.gm.headline': '外包安装毛利率 {gm}',
  'priced.gm.bonus': 'Artemis 班组：毛利率 {gm} — 额外 {points} 个点，但班组产能有限。',
  'priced.gm.note': '以外包安装的数字为准。调整上方任一价格，两者同步变化。',
  'priced.assistedPrice': '您的单价',
  'priced.assistedPriceHint': '此行价格由引擎计算。输入您自己的单价后即以此为准，毛利率会双向同步更新。',
  'priced.assistedTotal': '行计 {amount}',
  'priced.rollWidthNeeded': '待确认卷宽',
  'pricing.selection.add': '添加产品',
  'pricing.selection.addHint': '每添加一个产品即为独立一行 — 各自颜色、各自房间、各自价格。卧室铺地毯、客餐厅铺复合地板，就是两行。',
  'pricing.line.number': '产品 {n}',
  'canvas.floorPlanSummary': '{rooms} 个房间 · {area}',
  'canvas.recordedReadings': '{count} 项已锁定现场读数 · 米',
  'canvas.carpetLayout': '地毯裁切方案',
  'canvas.carpetOrder': '{cuts} 刀 · 含裁切预留共 {metres} 米 → {whole} 整延米 × {width} 米 = {area} 平方米订货面积',
  'canvas.carpetReviewNeeded': '已找到地毯房间。请先在工作画布保存实际裁切方案；不会用房间面积猜测订货量。',
  'canvas.importCarpetOnly': '使用已保存的地毯裁切方案',
  'canvas.importCarpetOnlyHelp': '保留已评估的房间和现场资料。保存草稿后，地毯订货量和报价会按这些裁切重新计算。',
  'canvas.carpetOnlyImported': '已将保存的地毯裁切加入此草稿。请核对订货量和价格，然后保存草稿。',
  'canvas.carpetCandidateImport': '将 29 延米候选方案加入私人草稿',
  'canvas.carpetCandidateHelp': '四刀方案取自现场画布第 13 次保存。现场核对尚未完成；加入草稿不会向客户发布方案或下单。',
  'canvas.carpetCandidateImported': '29 延米候选方案已加入私人草稿。请保存草稿；完成现场核对后方可向客户发布。',
  'canvas.carpetCandidateStatus': '暂定方案 · 待现场核对',
  'canvas.robeIncluded': '其中衣柜地面 {area}',
  'canvas.fixtureExcluded': '已扣除 {area} 平方米固定设施面积 · 地板铺设止于此处',
  'canvas.robe': '衣柜',
  'canvas.robeLabel': '衣柜 {area}',
  'canvas.floorPlan': '{name} · {rooms} 个房间 · {area} m²',
  'canvas.floorPlanAlt': '画布上绘制的 {name}，共 {rooms} 个房间',
  'work.seed.doorTrim': '门体裁切——{count} 扇门（来自图纸）',
  'work.standalone': '单独计价',
  'work.standaloneHint': '若随某产品包含，请在该产品内勾选。',
  'work.stairNote': '按 {product} 逐级计价。安装当天经你确认后按实际计费。',
  'work.carpetStair.standard': '标准楼梯地毯安装',
  'work.carpetStair.standardNote': '地毯在楼梯鼻下垂落，不包裹或固定在鼻边。楼梯地毯材料已计入整卷采购量。',
  'work.carpetStair.lips': '楼梯鼻包边地毯安装',
  'work.carpetStair.lipsNote': '地毯包裹楼梯鼻并固定在鼻边。楼梯地毯材料已计入整卷采购量。',
  'work.carpetStair.step': '级',
  'work.carpetStair.materialIncluded': '仅收安装费；楼梯地毯已计入采购材料量。',
  'assessment.laserLocked': '蓝牙激光读数已在此锁定。如需更正，请在画布中修改现场读数、保存新版本，再导入此草稿。',
  'carpet.defaults.title': '标准地毯安装配套',
  'carpet.defaults.note': '已勾选项目包含在地毯产品内。数量来自实测平面图及地毯采购量；仅修改此工程需要调整的项目。',
  'carpet.defaults.underlay': '包含 8mm 地毯垫 · 按平方米覆盖',
  'carpet.defaults.orderPending': '待确认采购面积',
  'carpet.defaults.smoothedge': '包含新装或更换地毯钉条',
  'carpet.defaults.trims': '包含地毯收口条',
  'carpet.defaults.removal': '包含拆除并清运现有地毯',
  'carpet.defaults.stairs': '楼梯安装 · {steps} 级，另行计价',
  'carpet.defaults.standard': '标准鼻下安装 · $25/级',
  'carpet.defaults.lips': '包裹楼梯鼻 · $35/级',
  'carpet.defaults.toMeasure': '长度待测',
  'work.planksNote': '楼梯额外消耗的地板——已含在楼梯工程内，不重复计费。',
  'work.title': '其他工作——非产品部分',
  'work.quickPickTitle': '常用服务',
  'work.quickPickNote': '一键加入已登记服务及测量数量；仅在此工程有所不同时修改。',
  'work.manageServices': '管理服务库',
  'work.quickPickRate': '${rate} / {unit} · 未含 GST',
  'work.quickPickRateUnset': '单价待确认 / {unit}',
  'work.blurb': '拆除、清运、踢脚线、楼梯及你新增的项目。数量来自画布已有的测量，单价来自 Artemis 服务价目。',
  'work.seed.stairs': '楼梯——{count} 级（来自图纸）',
  'work.seed.remove': '拆除并清运（按面积）',
  'work.remove.freeCarpet': '免费 — 现有地面为地毯',
  'work.remove.freeCarpetNote': '仅适用于现场评估确认现有地面为地毯的区域。',
  'work.remove.charged': '拆除并清运 — 按平方米收费',
  'work.remove.free': '免费 · $0.00/平方米',
  'accessory.carpet.smoothedge': '地毯钉条',
  'accessory.carpet.trims': '地毯过渡压条',
  'accessory.carpet.trimRateNeeded': '输入实测长度，然后选择此压条是否包含在地板价格内或单独收费。',
  'accessory.trim.pricing': '计价方式',
  'accessory.trim.included': '包含在地板价格内',
  'accessory.trim.separate': '单独收费',
  'work.seed.removeOnly': '仅拆除——客户提供垃圾箱',
  'work.seed.skirting': '踢脚线——{run} 延米（图纸实测）',
  'work.seed.accessories': '带入图纸涉及的五金辅料',
  'work.none': '尚未添加其他工作。可从图纸带入，或新增自定义服务。',
  'work.addCustom': '新增自定义服务',
  'work.customName': '自定义服务',
  'work.name': '项目名称',
  'job.depositReceived': '已收定金',
  'job.depositAmount': '金额 · 澳元',
  'job.depositPercent': '占成交金额百分比',
  'job.depositUnrecorded': '金额未记录',
  'job.depositDate': '日期未记录',
  'job.depositHelp': '可输入澳元金额或已记录成交金额的百分比。只有百分比而无金额属于历史设置，并非付款凭证。',
  'job.depositRecorded': '定金记录 {date} · {amount}',
  'commercial.mergeReview': '检查有冲突的修改',
  'commercial.mergeHelp': '另一个已保存版本修改了相同内容。您的输入已保留，两个版本中不冲突的修改都会保留。',
  'commercial.mergeMine': '您的输入',
  'commercial.mergeLatest': '最新保存的内容',
  'commercial.mergeKeepMine': '保留我的内容并保存合并修改',
  'commercial.mergeReturn': '返回未保存的草稿',
  'commercial.newOffer': '新建空白方案',
  'skirting.fromQuote': '已保存的报价包含踢脚线。',
  'skirting.selectionSaved': '已保存选择。仅在变更时修改，无需重复确认。',
  'skirting.selectionOptional': '仅在选择或变更款式或供货安排时打开详情。',
  'skirting.editDetails': '选择 / 修改踢脚线',
  'skirting.sourceDetails': '来源详情',
  'proposal.accept.priceFirst': '先输入已商定的范围及价格，再记录客户接受。',
  'proposal.accept.directRule': '记录客户对工作版本 {version} 的同意。将创建工作单，不发送消息或私密链接。',
  // 按已发布版本接受房间选择或公开成本方案（2026年10月23日，Minto）
  'proposal.accept.releasedCopy': '此方案按已发布版本接受：v{version}，即客户看到的版本，按客户自己保存的选择计价。',
  'proposal.accept.laterEdits': 'v{version} 之后保存的修改不包括在此次接受中。如客户已同意这些修改，请先发布。',
  'proposal.accept.customerChoice': '客户保存的选择计价为 {amount} + GST：有 {count} 个房间与我们推荐的 {recommended} 不同。',
  'proposal.release.customerChoice': '客户保存的选择使其页面计价为 {amount} + GST，而不是推荐的 {recommended}：有 {count} 个房间不同（{rooms}）。客户页面显示的是客户自己的选择。',
  'commercial.finalTotal': '最终金额 · 不含 GST',
  'commercial.roundingDiscount': '取整优惠',
  'commercial.finalTotalInvalid': '不含 GST 的最终金额必须介于零与优惠前的范围金额之间。',
  'pricing.rail.floorScope': '此处仅估算所选地板。独立服务及配件不计入本视图；整单成本及毛利率请查看完整提案成本审阅。此处未纳入的成本不等于成本记录缺失或冲突。',
  'pricing.rail.includedAccessory': '随地板包含的配件增加该地板成本而不加售价，因此降低其毛利率。',
  'commercial.finalTotalUnavailable': '设置最终金额前，请先解决缺失价格或价格范围。',
  'commercial.roundingTooLarge': '范围金额现低于取整优惠，请清除或修改优惠。',
  'work.skirtingRooms': '需要踢脚线的房间',
  'work.skirtingRoomHelp': '取消勾选由客户或其他施工方处理的房间。仅修改踢脚线范围，也可直接输入报价延米。',
  'work.quantity': '数量',
  'work.unit': '单位',
  'work.rate': '单价',
  'work.rateUnset': '未设置',
  'work.optional': '可选——由客户选择',
  'work.variation': '如使用才计费——现场确认',
  'share.step.build': '1 · 生成客户版本',
  'share.step.preview': '2 · 预览——已生成的提案 v{version}（未发送）',
  'share.step.message': '3 · 讯息内容与发送方式',
  'share.askAgain': '请 EmmA 重新撰写',
  'share.previewNote': '这就是客户打开的提案本身——已发布的文件，不是摘要。生成与预览都不会发送任何内容。',
  'share.previewTitle': '已生成的提案，与客户所见完全一致',
  'share.emmaWriting': 'EmmA 正在阅读提案并撰写讯息…',
  'share.emmaRead': 'EmmA 阅读了 {rooms} 个房间、{options} 个选项及所含内容（{included}）后撰写。',
  'share.none': '暂无',
  'share.channel.email': '邮件',
  'share.channel.sms': '短信',
  'share.subject': '主题',
  'share.body': '讯息内容',
  'share.bodySms': '讯息内容 · {count} 字',
  'share.smsSubject': '您的 Artemis Flooring 提案',
  'share.needEmail': '这位客户还没有电邮地址。请填写——会保存到客户资料，并用于本次发送。',
  'share.needPhone': '这位客户还没有手机号码。请填写——会保存到客户资料，并用于本次发送。',
  'share.saveToContact': '保存到客户资料',
  'share.contactSaved': '已保存到客户资料。',
  'share.savedBothUses': '同时保存到客户资料，并作为本次的收件人。',
  'share.toEmail': '将以邮件发送至 {to}。',
  'share.toPhone': '将以短信发送至 {to}。',
  'case.context': '背景与决策详情',
  'case.message': '待发送内容',
  'case.emmaAsks': 'EmmA 请示',
  'case.yourAction': '你的操作',
  'case.awaiting': '等待批准，尚未发送。',
  'case.checkStatus': '决定已记录。请查看上方或通信页面的发送结果。',
  'case.missingRecipient': '缺少收件人，请先要求修改。',
  'delivery.submitted': '已提交发送服务，尚未确认收件人收到。',
  'delivery.held': '已批准 — 受短信免打扰时段限制，将于悉尼时间 {time} 发送。',
  'delivery.queued': '已批准并排队，尚未发送。',
  'delivery.sending': '正在发送，等待服务确认。',
  'delivery.failed': '发送失败。',
  'delivery.uncertain': '发送结果不确定，重新发送前请先核查。',
  'delivery.declined': '已拒绝，未发送。',
  'delivery.revision': '已要求修改，未发送。',
  'delivery.checking': '正在核查已保存的发送结果…',
  'delivery.unavailable': '无法核实发送结果，请先查看通信记录再重试。',
  'delivery.open': '打开消息及状态',
  'case.approveSms': '批准：发送此短信',
  'case.approveEmail': '批准：发送此邮件',
  'case.approveMessage': '批准：发送此消息',
  'share.approveEmail': '审核待发送邮件',
  'share.approveText': '审核待发送短信',
  'share.approveNote': '请在审批卡中核对收件人与完整内容，批准该卡后才开始发送。',
  'share.queuedEmail': '邮件已排队发送至 {to}。',
  'share.queuedText': '短信已排队发送至 {to}。',
  'share.sendFailed': '无法将讯息加入发送队列',
  'share.draftNotSaved': '草稿未保存，因此没有加入队列。',
  'share.sequence.first': '这是就此提案给该客户的第一封讯息 — EmmA 会作介绍。',
  'share.sequence.revised': '提案已修订 — 之前已发 {count} 封讯息，最近一封在 {days} 天前。EmmA 会针对变化来写，而不是重新介绍。',
  'share.sequence.resend': '与上次讯息为同一版本（已发 {count} 封，最近一封在 {days} 天前）— EmmA 写的是跟进，而不是新报价。',
  'share.changes': '自上次讯息以来的变化：{changes}',
  'share.reply': '客户回复：{summary}',
  'share.instruction': '告诉 EmmA 要改什么',
  'share.instructionHint': '例如：说明踢脚线改为 92 mm、取消了拆除费；控制在三行以内。',
  'share.rewrite': '重写',
  'share.rewriting': 'EmmA 正在重写…',
  'share.rewritten': 'EmmA 已重写讯息。发送前请检查。',
  'share.finalMessage': '将要发送的内容（已发布版本 {version}）',
  'share.noReleasedAmount': '已发布的副本没有总价。请重新发布报价方案后再发送。',
  'share.saveRecipientFirst': '请先保存到客户资料——只能发送到已保存的邮箱或号码。',
  'share.releaseFirst': '您有尚未发布的修改。客户链接仍显示上一个版本——请先发布最新修改，再发送。',
  'work.variationRate': '如使用——每{unit} {rate}，现场确认',
  'work.quantityAuto': '由图纸量得——请在图纸上修改，不在此处填写',
  'work.ifNeeded': '如需要',
  'work.subOptions': '提供 {count} 个选项',
  'work.optionUnpriced': '尚未定价',
  'work.includedHeight': '此报价包含的高度',
  'work.includedHeightHint': '其他高度只显示价差。',
  'work.height.standard': '66–67 毫米',
  'work.height.medium': '92 毫米',
  'work.height.tall': '138–140 毫米',
  'work.fromCanvas': '数量来自图纸',
  'work.fromConfig': '来自服务价目',
  'work.custom': '自定义项目',
  'work.unpriced': '尚无已批准单价——该行暂不能报价',
  'work.accessories': '辅料',
  'work.accessoriesBlurb': '按整户或按楼层勾选所提供的辅料。与产品绑定的辅料，对客户显示为已含在产品价格内。',
  'work.scope': '适用范围',
  'work.wholeSite': '整户',
  'work.colour': '颜色／选项',
  'work.boundTo': '随产品包含',
  'work.boundToHint': '产品编号——留空则单独计价',
  'work.boundNote': '客户看到的是一条缩进行：已含在价格内，不显示金额。',
  'work.accessory.nosing': '楼梯防滑条',
  'work.accessory.planks': '楼梯额外地板',
  'work.accessory.withStairs': '随楼梯工程提供 — 已包含在其价格内，不另行计费。',
  'work.accessory.scotia': '收边条——按已确认的独立绘制长度',
  'work.accessory.trims': '收口条——画布已绘制',
  'work.accessory.underlay': '地垫——整户',
  'work.accessory.moisture': '防潮膜——整户',
  'work.addAccessory': '添加辅料',
  'work.chooseFamily': '选择类别…',
  'work.included': '已含',
  // 产品下方的辅料开关：这款地板包含哪些辅料，以及每种辅料的型号。
  'accessory.title': '这款地板包含的辅料',
  // 图纸上的收边条（2026-10-16），包含在地板价格内。
  'accessory.drawnTrims': '图纸上已画：{list}',
  'accessory.addDrawnTrims': '添加图纸上的收边条（包含在地板价格内）',
  'accessory.hint': '勾选类别并选择型号。此处勾选的辅料会在客户副本中以缩进行显示，视为已含在该产品价格内。',
  'accessory.chooseType': '选择型号…',
  'accessory.qty': '数量',
  'accessory.carried': '成本 {cost}／{unit}，已计入产品价格',
  'accessory.clear': '移除',
  'accessory.addTrim': '添加收边条',
  'accessory.addUnderlay': '添加另一种地垫',
  'accessory.assessmentNeed': '现场评估要求：{requirement}',
  'accessory.measuredUnit': '按延米实测；保存后计算采购根数。',
  'accessory.stockPieceCost': '供应商成本：每根 {length} 米 {cost}',
  'assessment.field.underlay': '地垫要求',
  'material.title': '收边条与收口条采购',
  'material.saveToRefresh': '保存方案以更新此仅供员工查看的计算。',
  'material.acceptedCarry': '此已保存计算会在发布时冻结，并传递至已接受的工程及采购单。',
  'material.calculation': '实测 {measured} 延米 + 安全余量 {safety} 延米，按每根 {piece} 米计算，共 {pieces} 根。',
  'material.provisionalCalculation': '目前图纸描出 {measured} 延米（暂定）。测量和裁切规则确认前，采购支数未定。',
  'material.source': '供应商来源：{source} · 生效日期 {date}',
  'material.unresolved': '暂不可下单：仍有测量、供应商资料或裁切规则需要确认。',
  'accessory.kind.underlay': '地垫',
  'accessory.kind.moisture_barrier': '防潮膜',
  'accessory.kind.scotia': '收边条／封边',
  'accessory.kind.trims': '收口条',
  'accessory.kind.adhesive': '胶水',
  'accessory.kind.levelling': '找平',
  'accessory.kind.stair': '楼梯配件',
  'work.scotiaNote': '画布有已确认的收边线长度，与踢脚线分开计量。',
  'work.trimsNote': '收口条已在画布上绘制，长度取自图纸。',
  'canvas.title': '画布图纸',
  'canvas.noJob': '此提案尚未关联工单，因此没有可导入的图纸 — 画布图纸挂在工单下。',
  'canvas.noJobHint': '可以在下方手动录入房间，或先打开工单在画布中测绘 — 之后此面板可一键导入全部房间、绘制面积与楼层。',
  'canvas.noDrawingHint': '该工单尚未使用画布。请打开工单进行绘制，或在下方手动录入房间。',
  'canvas.openJob': '打开工单',
  'canvas.openSiteCanvas': '打开现场画布',
  'carpetRun.open': '地毯裁切',
  'canvas.fromJob': '来自工单的实测图纸',
  'canvas.fromOpportunity': '来自 {ref} 上描摹的图纸',
  'canvas.plansUntraced': '{ref} 已有 {count} 份图纸，但尚未描摹房间（{floors}）。在机会的画布中描摹后，即可一键导入全部房间、绘制面积与楼层。',
  'canvas.noPlan': '此项工作尚未附图纸，也没有绘图记录。请在机会中附上图纸，或在下方手动录入房间。',
  'canvas.saveFirst': '先保存草稿，即可导入房间与图纸。',
  'canvas.assetsNote': '图纸本身 — 在报价旁展示给客户，不参与任何价格计算。',
  'canvas.attach': '在客户页面显示',
  'canvas.attached': '已在客户页面',
  'canvas.attachedCount': '客户页面已有 {count} 张图纸。',
  'canvas.captionFloor': '图纸 · {floor}',
  'canvas.ready': '可以导入',
  'canvas.sharedRevision': '共享现场勘测版本',
  'canvas.bindOnly': '使用此版本 — 保留方案内容',
  'canvas.bound': '已选择版本',
  'canvas.bindHelp': '将此已保存的勘测版本关联为参考，不替换房间、测量、价格或方案文字。导入房间是下方的独立操作。',
  'canvas.bindingSelected': '已选择勘测版本。方案内容保持不变。请保存草稿以保留此关联。',
  'canvas.sharedRevisionOption': '{owner} · 第 {number} 次保存 · {date}',
  'canvas.sharedRevisionSource': '共享工作记录版本 · {owner} 第 {number} 次保存',
  'canvas.legacyFallback': '当前为旧资料只读后备来源。发布新报价前，请先把此现场勘测保存到共享工作记录。',
  'pricing.line.noRoomsCta': '尚无房间，因此没有可计价的面积。请导入图纸或录入房间。',
  'pricing.line.goToAssessment': '前往现场勘测',
  'canvas.reading': '正在读取图纸……',
  'canvas.none': '{job} 尚未记录图纸。',
  'canvas.summary': '版本 {revision} · {rooms} 个房间 · {area} 平方米 · {laser} 个现场实测',
  'canvas.wholeSite': '全屋',
  'canvas.planOnly': '其中 {count} 个来自图纸而非现场实测 — 每个房间实测前，地毯用量仍为估算。',
  'canvas.modeReplace': '替换现有勘测',
  'canvas.modeAppend': '追加到勘测',
  'canvas.import': '从图纸导入 {count} 个房间',
  'canvas.importReplace': '用图纸替换这 {count} 个房间',
  'canvas.replaceWarning': '此操作将替换勘测中已有的 {count} 个房间，以图纸为准。',
  'pricing.line.shapeConfirmed': '已确认带尺寸的形状',
  'pricing.line.accessories': '辅料 · 成本',
  'pricing.line.sellPer': '每{unit}售价',
  'pricing.line.estimateOnly': '现场勘测尚未为每个房间记录带尺寸的形状，此行地毯仍为估算。',

  // ── 2.2 报价明细 ──────────────────────────────────────────────────────────
  'priced.title': '报价明细',
  'priced.empty': '选择产品后，定价引擎会在此给出金额。未选择产品时不会有任何定价。',
  'priced.staffTitle': '报价明细 · 员工视图',
  'priced.staffNote': '金额由定价引擎计算 — 销售预算与内部成本不会进入客户版本',
  'priced.updating': '正在更新合计… 输入框保持可编辑',
  'priced.pricing': '正在计价…',
  'priced.failed': '此选择无法定价：{message}',
  'priced.col.line': '项目',
  'priced.col.sell': '售价',
  'priced.col.yourPrice': '您的单价',
  'priced.col.notes': '备注',
  'priced.codStaffOnly': 'COD 价基 · 仅员工可见',
  'priced.notYetDecided': '尚未确定',
  'priced.zeroDropped': '0 元 — 已从客户版本中移除',
  'priced.subtotalFrom': '小计（区间）',
  'priced.incomplete': '不完整',
  'priced.internalTitle': '内部数据 — 绝不向客户展示',
  'priced.profit.title': '预计盈利能力',
  'priced.profit.staffOnly': '仅员工可见 · 绝不向客户展示',
  'priced.profit.externalProfit': '预计利润 · 外部安装团队',
  'priced.profit.externalMargin': '预计毛利率 · 外部安装团队',
  'priced.profit.crewProfit': '预计利润 · Artemis 自有团队',
  'priced.profit.crewMargin': '预计毛利率 · Artemis 自有团队',
  'priced.profit.complete': '估算采用定价引擎中每个已计价项目的材料成本与人工成本。销售金额不含 GST。',
  'priced.profit.partial': '部分估算：{total} 个项目中有 {priced} 个具备售价、材料与人工数据；未确定项目不计入。',
  'priced.profit.waiting': '只要有一个项目具备售价、材料成本与人工估算，此处就会显示盈利能力。',
  'priced.externalLabour': '外部人工',
  'priced.contribution': '毛利贡献',
  'priced.decidedBlurb': '只要有一行未确定，引擎就不会给出材料与毛利贡献合计。以下为已确定各行的数据：',
  'priced.decidedSell': '售价 · {count} 行',
  'priced.decidedSellPlural': '售价 · {count} 行',
  'priced.excluded': '未计入上述数据：',
  'priced.excludedReason': '{label} — {reason}',
  'priced.notPriced': '未定价',
  'priced.decidedNote': '这是您要求的「仅看已确定行」视图，不是引擎自身的合计。引擎合计在每一行都确定之前不会给出 — 客户版本也据此把关。',
  'priced.customerTitle': '客户版本 · 报价明细',
  'priced.customerExact': '与客户通过链接看到的完全一致',
  'priced.customerNotReleasable': '尚不可发布',
  'priced.blockedTitle': '有未定价的行，暂不能发布此报价：',
  'priced.blockedNote': '未定价的内容绝不会展示给客户。解决上方该行后，客户版本即会生成。',
  'priced.zeroNote': '因不收费而未向客户展示：{lines}。',
  'priced.notice.undecidedColour': '有一行或多行取决于尚未选定的颜色。选定颜色后即为最终价格。',
  // 裁切损耗说明（按铺法）：{percent} 取自配置中的损耗比例，改比例无需改翻译。
  'priced.waste.straight': '已含 {percent}% 的裁切损耗；最终按实际用量结算。',
  'priced.waste.pattern': '已含 {percent}% 的裁切损耗（拼花铺法）；最终按实际用量结算。',
  'pricing.colour.undecided': '客户尚未选定',
  'pricing.colour.catalogueGroup': '产品库颜色',
  'pricing.colour.specialOrderGroup': '特殊订购 — 建筑商专供，不在网站展示',
  'pricing.line.specialOrderNote': '特殊订购颜色：不在网站和标准价目表上。发布前请与供应商确认供货与价格。',

  // ── 顶栏中的 EmmA（横条与抽屉） ──────────────────────────────────────────
  'emma.history': '对话',
  'emma.historyEmpty': '尚无对话。',
  'emma.newThread': '开始新的对话',
  'emma.untitled': '（无标题）',
  'emma.branchCarried': '已开启新对话——她从「{title}」把刚才的内容带过来了。',
  'emma.branchAsk': '要在这里开一个新对话吗？刚才做的事留在「{title}」里，她会把上下文带过来。',
  'emma.branchStart': '开新对话',
  'emma.branchCancel': '暂不',
  'emma.branchMark': '已分叉',
  'emma.historyFailed': '这段对话没有加载出来——没有丢失，内容还在她那边。',
  'emma.historyNoSession': '登录已过期，所以这段对话没有加载出来。内容没有丢失。',
  'emma.historyRetry': '重试',
  'emma.emptyWhich': '当前对话 {id}——她那边返回 {count} 轮。',
  'emma.turns': '{count} 轮',
  'emma.stripWaiting': '有 {count} 项等你处理 — 点击展开',
  'emma.stripIdle': '暂无待你处理的事项',
  'emma.open': '展开',
  'emma.close': '收起',
  'emma.tabChat': '与 EmmA 对话',
  'emma.tabWaiting': '等你处理',
  'emma.desk': '完整工作台',
  'emma.empty': '直接吩咐她——当前页面会随问题一起发出，所以「这个工程」就是屏幕上的这个工程。',
  'emma.placeholder': '告诉她做什么，或问她已知道什么…',
  'emma.send': '发送',
  'emma.working': '处理中…',
  'emma.thinking': '正在处理…',
  'emma.howIDidIt': '我是怎么做的',
  'emma.showDetails': '显示详情',
  'emma.noWords': '已完成。',
  'emma.unreachable': '无法联系到 EmmA。没有做任何更改，请重试。',
  'emma.stopped': 'EmmA 还没回答就停下了——没有做任何更改。{reason}',
  'emma.signIn': '登录已过期。请重新登录后再说一次——没有做任何更改。',
  'emma.openProposal': '查看她准备的内容',
  'emma.nothingWaiting': '暂无待你处理的事项。',
  'emma.reviewDraft': '查看措辞后再发出 →',
  'emma.queueNote': '这些是原本就未结的事项，不是新增工作。',
  'emma.attach': '＋ 文件',
  'emma.attachTitle': '把 PDF 或照片附到这个对话——从现在起她每一轮都会读它',
  'emma.attachKeeping': '正在保存 {name}…',
  'emma.attachPending': '随你的下一条消息发出',
  'emma.attachOpen': '打开 {name}',
  'emma.attachRemove': '这条消息不标记 {name}',
  'emma.attachNote': '{kind} · {size}',
  'emma.attachKind.pdf': 'PDF',
  'emma.attachKind.photo': '照片',
  'emma.attachKind.text': '文本',
  'emma.attachUnknownType': '这种类型',
  'emma.attachRefuse.type': '我能读 PDF、照片（JPEG、PNG、WebP）和纯文本文件，读不了 {mime}。',
  'emma.attachRefuse.heic': '我能读 PDF、照片（JPEG、PNG、WebP）和纯文本文件，读不了 {mime}。请先导出为 JPEG 或 PNG。',
  'emma.attachRefuse.empty': '这个文件是空的。',
  'emma.attachRefuse.big': '这个文件有 {mb} MB；EmmA 最多读 {max} MB 的文件。',
  'emma.attachRefuse.many': '这个对话已有 {max} 个文件，是她一个对话里保留的上限。要再加请开新对话。',
  'emma.attachOpenFailed': '这个文件打不开——{reason}',
  'emma.placeholderFile': '就这个文件问她…',

  // ── 工程白板 ─────────────────────────────────────────────────────────────
  'schedule.date': '排期日期',
  'schedule.crew': '施工班组',
  'schedule.assignee': '施工班组或员工',
  'schedule.crewChoose': '请选择班组……',
  'schedule.crewUnknown': '不在安装商名录中 — 将按输入内容记录。',
  'schedule.external': '外包',
  'schedule.notes': '需要班组知晓的事项',
  'schedule.book': '确认排期',
  'schedule.booking': '正在排期……',
  'schedule.booked': '已将 {team} 排期至 {date}。状态、下次日期由排期自动推导，工单阶段随之更新。',
  'schedule.days': '施工日',
  'schedule.duration': '共 {days} 个日期卡片 · {count} 个班组安排',
  'schedule.addDay': '+ 添加施工日',
  'schedule.addSameDay': '+ 添加班组',
  'schedule.addNextDay': '+ 添加下一天',
  'schedule.currentProposal': '最新保存方案 · 第{version}版',
  'schedule.quoteTasks': '最新保存方案 / 报价中的工作项目',
  'schedule.chooseTask': '请为当天工程至少选择一个报价工作项目。当天所有班组共用此工作范围。',
  'schedule.combinationInvalid': '每一项都必须有有效的悉尼日期、施工人员/班组及报价工作。同一“班组 + 日期”只能出现一次。',
  'schedule.dayWork': '当日工作安排',
  'schedule.dayWorkHint': '例如：准备、安装、收尾',
  'schedule.datesInvalid': '每个施工日都必须填写不同的有效日期。',
  'schedule.savePlan': '保存安装计划',
  'schedule.saving': '正在保存计划……',
  'schedule.saved': '已保存 {count} 个“人员/班组 + 日期”组合。日历与工程现已采用此准确计划。',
  'schedule.inactive': '只有处于“待排期”或“已安排安装”的工程才能排期。',
  'schedule.multipleInstalls': '此工程有多条安装记录。请先选择并核对正确的安装记录，再更改施工日。',
  'schedule.pending': '已提交审批 — 批准前不会排期。',
  'schedule.refused': '未排期：{reason}',
  'schedule.failed': '未排期：{message}',
  'schedule.replacedWork': '此项已排工作已不在确认的报价中。请取消勾选，并在上方选择已同意的替代工作后保存。',
  'schedule.quoteLocksConflict': '工程与安装记录关联了不同的已确认报价。请先确认已同意的工作范围再排期。',
  'schedule.quoteUnavailable': '无法读取已确认的报价。请先恢复其关联再排期。',
  'schedule.quoteAmbiguous': '记录中有多个主报价。请先选择已确认的报价再排期。',
  'schedule.installAmbiguous': '记录中有多个安装计划。请先选择要排期的安装记录。',
  'schedule.quoteRequired': '请先选择主报价，再安排其中的工作。',
  'schedule.portal': '班组在安装商门户中查看此排期',
  'schedule.destinations': '安装班组使用安装商门户；员工使用 Artemis 手机应用',
  'schedule.derived': '状态、下次日期与工单阶段均为自动推导，无需手填。',
  'job.openInMaps': '在地图中打开',
  'job.reference': '工程编号',
  "closedJob.title": "恢复此工程",
  "closedJob.lede": "此工程当前为 {stage}。请选择恢复方式；每种方式都会保留报价、备注和历史记录。",
  "closedJob.liveOnly": "这些选项仅在正式工作区可用。",
  "closedJob.loading": "正在核对工程阶段历史…",
  "closedJob.unavailable": "无法加载恢复选项。请先刷新，再更改此工程。",
  "closedJob.retry": "重试",
  "closedJob.restore.title": "恢复上一阶段",
  "closedJob.restore.titleTo": "恢复上一阶段（{stage}）",
  "closedJob.restore.line": "撤销搁置：恢复到工程自身历史记录中的上一阶段。无需客户重新确认；安装记录保持不变。",
  "closedJob.restore.action": "恢复上一阶段",
  "closedJob.restore.done": "已恢复到 {stage}",
  "closedJob.opportunity.title": "退回商机",
  "closedJob.opportunity.line": "作为未结询价放回商机管道，适用于阶段历史不一致的旧工程。",
  "closedJob.opportunity.lineTo": "作为未结询价（{stage}）放回商机管道，适用于阶段历史不一致的旧工程。",
  "closedJob.opportunity.action": "退回商机",
  "closedJob.opportunity.done": "已退回商机管道",
  "closedJob.reopen.title": "凭客户确认重新开启",
  "closedJob.reopen.line": "客户已再次接受已保存的报价：同一工作从“待排期”重新开始。",
  "closedJob.reason": "原因（至少 10 个字符）",
  "closedJob.reasonHint": "发生了什么，例如误设为搁置",
  "closedJob.cancel": "取消",
  "closedJob.why.not_closed": "只有搁置或流失的工程可以使用此操作。",
  "closedJob.why.no_history": "没有可信的上一阶段：没有阶段变更记录说明此工程如何到达当前阶段。请使用“退回商机”或“重新开启”。",
  "closedJob.why.unknown_stage": "没有可信的上一阶段：记录的阶段已停用或无法识别。请使用“退回商机”或“重新开启”。",
  "closedJob.why.paid_date": "已记录付款日期，只能恢复到“已全额付款”。请先核对付款记录。",
  "closedJob.why.close_date": "已记录结算（开票）日期，只能恢复到“已开票”或“已全额付款”。请先核对财务记录。",
  "closedJob.why.aiw_origin": "此工程由 AIW 中已接受的报价生成，该接受记录无法安全撤回。请使用“恢复上一阶段”或“重新开启”。",
  "closedJob.why.aiw_acceptance": "此工程带有 AIW 接受记录或商机记录。请使用“恢复上一阶段”或“重新开启”。",
  "closedJob.why.installation": "此工程有安装记录，不会出现在商机管道中。请使用“恢复上一阶段”或“重新开启”。",
  "closedJob.why.money": "此工程已有款项记录（定金、付款或结算日期、未作废发票或现金账），不能退回为商机。",
  'workTabs.label': '工作栏目',
  'workTabs.overview': '概览',
  'workTabs.canvas': '现场画布',
  'workTabs.opening': '正在打开现场画布…',
  'workTabs.proposal': '方案',
  'workTabs.installation': '安装',
  'workTabs.documents': '文件',
  'quote.open': '查看报价',
  'job.installBelow': '施工日在下方',
  'job.completionPhotos': '查看完工照片',
  'job.completionReview': '安装人员报告',
  'job.completionReviewError': '无法读取安装人员报告。',
  'job.completionReviewEmpty': '此工程尚未提交安装报告。',
  'job.dayReport': '当日完工报告',
  'job.scopeCompleted': '已完成分配的施工范围',
  'job.setOutReport': '放线检查',
  'job.issueReport': '现场问题',
  'job.progressReport': '进度报告',
  'job.wholeWorkOrderReported': '所有计划施工日均已报告',
  'job.officeConfirmTitle': '办公室安装确认',
  'job.officeConfirmHelp': '请根据办公室掌握的证据确认实际施工完成。安装人员报告和付款另行记录。',
  'job.officeNotes': '办公室核实了什么？',
  'job.officeUpload': '添加完工照片',
  'job.officeUploading': '正在上传照片…',
  'job.officeConfirmAction': '确认安装完成',
  'job.officeConfirmed': '办公室已确认安装完成',
  'job.officeConfirmError': '办公室确认未保存。',
  'job.officeReadError': '无法读取办公室确认。',
  // 批量上传（2026-11-01）：一张被拒绝的照片不再中断其余照片；提示写明哪些未保存。
  'job.officePhotoPartial': '已保存 {saved}/{total} 张照片。未保存：{names}。请重新添加这几张，其余已在工单上。',
  'job.officePhotoReadError': '无法读取办公室照片。',
  'job.installerUploadedPhotos': '安装人员上传的照片',
  'job.officeUploadedPhotos': '办公室上传的照片',
  'job.installerPhotoReadError': '无法读取安装人员照片。',
  'job.noInstallerPhotos': '此工作尚无安装人员上传的照片。',
  'job.photoCount': '{count} 张照片',
  'job.documentActions': '准备客户文件',
  'job.documentsTitle': '工程文件',
  'job.documentsDraftHint': '已保存草稿在审核并发送前保持私密。',
  'job.documentsLoading': '正在加载已保存草稿…',
  'job.documentsLoadFailed': '无法加载已保存草稿。',
  'job.documentsDraft': '草稿',
  'job.documentsOpen': '打开 PDF',
  'job.documentsEmpty': '此工程尚未保存完工报告或保修声明草稿。',
  'job.documentsOpenFailed': '无法打开已保存的 PDF。请在通讯中心查看草稿。',
  'job.showPhotoCopies': '显示与安装人员照片重复的 {count} 张办公室副本',
  'job.hidePhotoCopies': '隐藏重复的 {count} 张办公室副本',
  'job.documentNeedsCompletion': '完工报告需要已保存的安装人员当日报告或办公室安装确认；保修声明不需要。',
  'job.documentNeedsCustomer': '请先关联客户联系人，再准备文件草稿。',
  'job.documentReviewHint': '先预览 PDF，再保存为沟通草稿。发送仍须人工审核。',
  'job.documentProduct': '供应产品',
  'job.documentCompletionDate': '完工日期',
  'job.documentDateFromOffice': '已按办公室确认预填。若实际完工日不同，请修改。',
  'job.documentDateFromInstaller': '已按安装人员当日报告预填。若实际完工日不同，请修改。',
  'job.documentDateFromSchedule': '已按最后一个排期施工日预填。若实际完工日不同，请修改。',
  'job.documentDateUnknown': '请输入安装完成的日期。',
  'job.documentSuppliedProduct': '供应产品',
  'job.documentProductUnknown': '此工单的提案或报价中没有记录供应产品，请在下方输入。',
  'job.documentWarrantyOnFile': '存档的制造商保修文件',
  'job.documentWarrantyMissing': '{product} 没有存档的制造商保修 PDF。声明将引用制造商公布的保修条款；请在产品目录中为该产品添加 PDF 以便附上。',
  'job.documentWarrantyUnavailable': '无法读取此工单的供应产品及其保修文件。',
  'job.strataTitle': '物业（Strata）申请资料包',
  'job.strataHint': '根据本工单已接受的销售记录、已保存的图纸、产品存档的供应商文件和公司证书生成。保存在“已准备文件”下；此处不发送任何内容。',
  'job.strataUnavailable': '无法读取此工单的物业申请资料包。',
  'job.strataUnderlay': '底垫',
  'job.strataMissing': '未存档：',
  'job.strataPrevious': '已保存 {count} 个早期版本；最新为第 {revision} 版。',
  'job.strataPrepare': '准备物业申请资料包（第 {revision} 版）',
  'job.strataOpenDocuments': '打开已准备文件',
  'job.strataKept': '物业申请资料包第 {revision} 版（{pages} 页）已准备并保存在本工单下。发送给物业经理前请逐页审核。',
  'job.strataNotKept': '资料包已准备但无法保存到工单下：{reason}。如需要请保存预览。',
  'job.strataFailed': '无法准备物业申请资料包。',
  'job.documentManufacturerLimit': '存档的制造商文件超出邮件附件限制（最多四份 PDF，连同声明总计 10 MB）。',
  'job.documentAttachments': '此草稿中的文件',
  'job.prepareCompletion': '准备完工报告',
  'job.prepareWarranty': '准备保修声明',
  'job.previewDocument': '预览 PDF',
  'job.saveDocumentDraft': '保存沟通草稿',
  'job.documentPrepared': 'PDF 已可供审核。',
  'job.documentPending': '文件草稿正在等待审核。',
  'job.documentFailed': '无法准备文件。',
  'job.documentNeedsAddress': '请先保存工地地址，再准备客户文件。',
  'job.documentPhotosUnavailable': '部分已保存的完工照片无法读取。请先检查档案，再准备报告。',
  'job.documentNeedsProduct': '请先确认供应产品，再准备保修声明。',
  'job.documentDraftSaved': '草稿已保存，请审核后发送。',
  'job.openCommunications': '打开沟通中心',
  'job.deliverables': '工程完工文件',
  'job.deliverablesEmpty': '此工程尚未出具完工报告或保修声明。',
  'job.deliverablesError': '无法读取已保存的完工文件。',
  'job.completionReport': '完工报告',
  'job.warrantyStatement': '保修声明',
  'wb.title': '白板',
  'wb.loading': '读取中…',
  'wb.version': '版本 {version} · {count} 条记录',
  'wb.history': '历史（{count}）',
  'wb.hideHistory': '收起历史',
  'wb.historyNote': '每次写入都会保留上一版完整内容——这些版本都可以恢复：',
  'wb.empty': '白板上还没有内容。它可以承载任何形式——文字、表格、图纸、照片、PDF，按工程需要。',
  'wb.unavailable': '无法读取白板：{message}',
  'wb.untitled': '（无标题）',
  'wb.unknownKindHint': '当前版本还不会渲染这种类型——它会原样保存并显示，而不是被隐藏。',
  'wb.rawShape': '查看原始数据',
  'wb.superseded': '已移除：{reason}',
  'wb.assets': '{count} 个附件',
  'wb.addTitle': '写点什么',
  'wb.addNote': '在顶部横条直接吩咐 EmmA，她也会写在这里——这个输入框是给你自己想写的时候用的。',
  'wb.kind': '类型',
  'wb.kind.note': '文字',
  'wb.kind.table': '表格',
  'wb.kind.checklist': '清单',
  'wb.kind.drawing': '图纸',
  'wb.kind.photo': '照片',
  'wb.kind.pdf': 'PDF',
  'wb.titlePlaceholder': '这条是关于什么的？',
  'wb.bodyPlaceholder': '任何内容——尺寸、客户的要求、现场情况…',
  'wb.save': '记到白板',
  'wb.saveNote': '只保存在该工程，不会发送到任何地方。',
  'wb.saved': '已添加，上一版本已保留。',
  'wb.pending': '正在 EmmA 工作台等待批准。',
  'wb.notConfirmed': '未能确认结果——可用同样内容安全地重试。',

  // ── EmmA 工作台：来自她的存储的对话 ──────────────────────────────────────
  'desk.conversation': '对话记录',
  'desk.session': '会话 {id}',
  'desk.noSession': '还没有对话——从顶部横条问她一句，记录就会出现在这里',
  'desk.refresh': '刷新',
  'desk.startFresh': '开始新的对话',
  'desk.loadingHistory': '正在读取对话…',
  'desk.emptyHistory': '尚无对话。在任意页面从顶部横条打开她即可。',

  // ── 管道与商机页 ────────────────────────────────────────────────────────
  'pipe.title': '管道',
  'pipe.lede': '正在跟进的工作。客户接受后，同一段历史会带入工程。',
  'pipe.search': '搜索管道',
  'pipe.searchPlaceholder': '客户或区域',
  'pipe.leadInbox': '线索收件箱',
  'pipe.acceptedJobs': '已接受工程',
  'pipe.loading': '正在加载管道…',
  'pipe.totals': '{count} 项 · 记录金额 {value}。这不是已收到的收入。',
  'pipe.siteTbc': '工地待确认',
  'pipe.notPriced': '未定价',
  'pipe.openOpportunity': '打开商机',
  'pipe.openWork': '打开工作',
  'pipe.prepareProposal': '准备报价书',
  'pipe.prepareAnother': '再准备一份报价书',
  'pipe.none': '此视图暂无工作。',
  'pipe.phase.allActive': '全部进行中',
  'pipe.phase.enquiry': '询价',
  'pipe.phase.preparing': '准备报价中',
  'pipe.phase.quoteSent': '已发报价',
  'pipe.phase.accepted': '已接受——创建工程',
  'pipe.phase.stalled': '停滞',
  'pipe.stalledNote': '停滞的工作已暂停：客户暂缓，可能会回来。不计入“全部进行中”。点击“回到销售管道”即可恢复。',
  // Move to Stalled / Back to Pipeline on a Pipeline card (12 Oct 2026)
  'pipe.moveToStalled': '移至停滞',
  'pipe.stallReason': '为什么停滞？',
  'pipe.stallPlaceholder': '客户暂缓到假期之后',
  'pipe.stallDone': '已移至停滞',
  'pipe.backToPipeline': '回到销售管道',
  'pipe.backReason': '为什么恢复？',
  'pipe.backDefault': '客户重新联系了',
  'pipe.backDone': '已回到销售管道',
  'pipe.reasonHint': '一行，至少 3 个字符。',
  'pipe.cancel': '取消',
  'lead.disposition.stalled': '停滞',
  'lead.disposition.stalledHint': '暂停，并非丢失——客户暂缓，可能会回来。仍保留在管道的“停滞”中。',
  'lead.disposition.wentQuietHint': '失联即视为结束——会离开管道。如客户可能回来，请用“停滞”。',
  'lead.noAnswer.button': '未接通 — 发短信',
  'lead.noAnswer.busy': '正在请 EmmA 处理…',
  'lead.noAnswer.hint': '记录这次未接通的来电，并由 EmmA 起草一条附有空闲时间和预约链接的短信。',
  'lead.noAnswer.drafted': 'EmmA 已起草这条短信，正在“审批 / 沟通”中等待您发送。',
  'lead.noAnswer.failed': 'EmmA 未能起草短信：{reason}',
  'lead.noAnswer.unreachable': '无法连接 EmmA，请重试。',
  'opp.title': '商机',
  'opp.kicker': '商机 · {ref}',
  'opp.untitled': '客户商机',
  'opp.preJob': '成单前工作',
  'opp.siteTbc': '工地待确认',
  'opp.unavailable': '商机不可用',
  'opp.notInWorkspace': '本工作区没有该编号。',
  'opp.loading': '正在加载…',
  'opp.customerSite': '客户与工地',
  'opp.editDetails': '编辑客户与工地',
  'opp.saveDetails': '保存资料',
  'opp.cancelEdit': '取消',
  'opp.detailsSaved': '客户与工地资料已保存。',
  'opp.loadingDetails': '正在加载客户与工地资料…',
  'opp.detailsFailed': '无法加载客户与工地资料。请刷新后再编辑。',
  'opp.contactFailed': '无法加载关联客户。请刷新后再编辑。',
  'opp.contact': '联系人',
  'opp.phone': '电话',
  'opp.email': '邮箱',
  'opp.site': '工地',
  'opp.siteAddress': '施工地址',
  'opp.siteAddressPlaceholder': '单元、门牌号和街道名',
  'opp.suburb': '城区',
  'opp.postcode': '邮编',
  'opp.phoneInvalid': '非空电话号码必须至少包含一个数字。',
  'opp.siteOwnership': '施工地址属于这个商机，不会取代客户的住址或账单地址。',
  'opp.notRecorded': '未记录',
  'opp.brief': '客户需求',
  'opp.noBrief': '未提供需求说明。',
  'opp.acceptedJob': '已接受工程',
  'opp.register': '把已知信息记下来',
  'opp.registerNote': '在报价出现之前，这个商机的记录就是你看到和说过的内容——上门、通话、客户的要求。它记在客户名下，也是下一个人会读到的内容。',
  'opp.channel': '方式',
  'opp.direction': '方向',
  'opp.dirOut': '我们联系客户',
  'opp.dirIn': '客户联系我们',
  'opp.whatHappened': '发生了什么',
  'opp.whatPlaceholder': '例如：已测量客厅、三间卧室与走廊；原有地毯为直铺；客户希望在 30 日前完成。',
  'opp.recordIt': '记录',
  'opp.nextStep': '下一步',
  'opp.nextWhat': '要做什么',
  'opp.nextPlaceholder': '例如：把楼梯的报价发出去',
  'opp.nextWhen': '时间',
  'opp.scheduleIt': '安排',
  'opp.recorded': '已记录内容',
  'opp.loadingTouches': '正在读取记录…',
  'opp.touchesFailed': '无法读取记录。',
  'opp.nothingRecorded': '尚无记录——第一条备注就是开始。',
  'opp.channel.site_visit': '上门',
  'opp.channel.phone': '电话',
  'opp.channel.email': '邮件',
  'opp.channel.sms': '短信',
  'opp.channel.wechat': '微信',
  'opp.channel.other': '其他',
  'opp.touchSaved': '已记入客户名下。未发送任何内容。',
  'opp.nextSaved': '下一步已安排。',
  'opp.needsApproval': '正在 EmmA 工作台等待你的批准。',
  'opp.notConfirmed': '未能确认结果——可安全地用同样内容重试。',
  'opp.plan': 'iPad 描绘用图纸',
  'opp.planOnJob': '该商机已关联工程。请在工程中继续绘制；商机的原始文件会保留。',
  'opp.planNote': '编号 {ref} 会一直跟着这份图纸、描绘与报价书。',
  'opp.planUploadNote': '上传原始 PNG、JPG 或 PDF。尺寸与描绘在 iPad 上记录；客户图纸尺寸仍为未核实。',
  'opp.planReserved': '已保留原件：{name}。若上传中断，请重试同一文件。',
  'opp.plansFailed': '无法加载图纸。',
  'opp.groundFloor': '一层',
  'opp.firstFloor': '二层',
  // Plans held outside the gf/1f slots (merged Opportunities, raw plans).
  'opp.otherPlans': '其他平面图',
  'opp.otherPlan': '平面图文件：{name}',
  'opp.openProposal': '打开报价书',
  'opp.prepareProposal': '准备报价书',
  'opp.proposalQuiet': '报价书是带价格的文件；上面记录的内容才是它背后的依据。',
  'opp.nextAction': '下一步',
  'opp.nextJob': '打开已接受的工程',
  'opp.nextJobNote': '此机会已被接受。请进入工程继续安排日程、材料和交付。',
  'opp.nextProposalNote': '先准备带价格的报价书，或在下方预约测量。其他记录功能需要时再展开。',
  'opp.contactAndSite': '联系人及现场资料',
  'opp.activityAndFollowUp': '活动及跟进',
  'opp.plansAndFiles': '平面图、报价历史及文件',
  'opp.retry': '重试',
  'opp.uploading': '正在上传并校验…',

  // ── Artemis 仓库库存 ────────────────────────────────────────────────────
  'inv.kicker': '库存',
  'inv.title': 'Artemis 仓库库存',
  'inv.lede': '货架上有多少、已承诺给哪个工程、在途多少，以及真正可用的数量。',
  'inv.meta': '货号 · 产品 · 库位 · 类别 · 单位 · 现有 · 已承诺 · 在途 · 可用',
  'inv.search': '搜索货号、产品名称或描述…',
  'inv.allLocations': '全部库位',
  'inv.shortOnly': '仅不足',
  'inv.shortTitle': '有 {count} 项不足以覆盖已承诺数量',
  'inv.by': '缺 {qty}',
  'inv.col.sku': 'Artemis 货号',
  'inv.col.supplierSku': '供应商货号',
  'inv.col.product': '产品',
  'inv.col.location': '库位',
  'inv.col.category': '类别',
  'inv.col.uom': '单位',
  'inv.col.onHand': '现有',
  'inv.col.committed': '已承诺',
  'inv.committedHint': '已承诺给工程的数量——点击数字查看具体工程',
  'inv.col.ordered': '在途',
  'inv.col.available': '可用',
  'inv.availableHint': '现有减去已承诺。负数表示承诺了货架上没有的材料。',
  'inv.col.job': '工程',
  'inv.col.qty': '数量',
  'inv.col.when': '时间',
  'inv.col.kind': '变动',
  'inv.col.ref': '关联',
  'inv.none': '尚未记录任何库存——先做入库以开始台账。',
  'inv.footnote': '可用 = 现有 − 已承诺。这里每个数字都来自出入库台账，因此纠错是一笔变动，而不是直接改数。',
  'inv.intel': '这个数字的来源',
  'inv.commitments': '已承诺给',
  'inv.movements': '出入库记录',
  'inv.noCommitments': '暂无承诺。',
  'inv.noMovements': '暂无变动记录。',
  'inv.rackTitle': '地毯货架',
  'inv.rackMeta': 'A 与 B · 每架 3 层 × 3 位',
  'inv.levelPos': '第 {level} 层 · 第 {position} 位',
  'inv.rackFootnote': 'General Storage（大件区）与 Trims Rack（线条架）是独立库位。点击货位可筛选列表。',
  'inv.noSupplierSku': '该物料尚未记录供应商货号',
  'inv.unavailable': '无法读取库存：{message}',
  'inv.loading': '正在读取出入库记录…',

  // ── 安装工台账 ──────────────────────────────────────────────────────────
  'crew.add': '新增班组',
  'crew.addTitle': '新增安装班组',
  'crew.edit': '编辑此班组',
  'crew.editTitle': '编辑 {name}',
  'crew.name': '班组名称',
  'crew.kind': '班组类型',
  'crew.internal': 'Artemis 自有班组',
  'crew.external': '外包班组',
  'crew.mobile': '手机号（门户登录用）',
  'crew.needPhone': '请输入此人用于登录的手机号码。',
  'crew.accountName': '登录姓名（选填；默认使用班组名称）',
  'crew.accountRole': '账号角色',
  'crew.accountWorker': '安装人员',
  'crew.accountManager': '团队经理（不参与安装）',
  'crew.phoneNote': '手机号即登录方式：门户会向该号码发送验证码。',
  'crew.priceBasis': '计价基准',
  'crew.basisList': '按我方价目表',
  'crew.basisCustom': '按其自报价格',
  'crew.labourMode': '结算方式',
  'crew.labour.team_artemis_zero': '自有班组 — 不计人工费',
  'crew.labour.catalog_lab_cost': '按产品库人工成本',
  'crew.labour.declared_rates': '按其自报费率',
  'crew.abn': 'ABN（澳洲商业号）',
  'crew.bankName': '账户名称',
  'crew.bsb': 'BSB（银行分行代码）',
  'crew.account': '账号',
  'crew.paymentPreference': '付款方式',
  'crew.pay.unset': '— 未登记',
  'crew.pay.cash': '现金',
  'crew.pay.transfer': '银行转账',
  'crew.categories': '可施工品类',
  'crew.categoriesNote': '勾选该班组能施工的品类。列表来自产品库实际在售的品类。',
  'crew.categoriesLoading': '正在读取产品库……',
  'crew.languages': '工作语言',
  'crew.lang.en': '英语',
  'crew.lang.zh': '中文',
  'crew.lang.en+zh': '英语和中文都会',
  'crew.calendar': '班组日历',
  'crew.calendarWhy': '把链接加到手机日历一次即可，之后我们排的每一天都会自动更新。',
  'crew.calendarNone': '尚未生成日历链接。',
  'crew.calendarIssue': '生成日历链接',
  'crew.calendarRotate': '更换链接',
  'crew.calendarRotateWhy': '旧链接会立即失效。若链接发错人，请更换。',
  'crew.calendarRevoke': '停用链接',
  'crew.calendarRevoked': '已于 {date} 停用——其手机中的链接已失效。',
  'crew.calendarIssued': '签发时间',
  'crew.calendarRotated': '更换时间',
  'crew.calendarExpires': '有效期',
  'crew.calendarNoExpiry': '不过期——在你停用之前一直有效',
  'crew.calendarToken': '令牌',
  'crew.calendarCopy': '复制链接',
  'crew.calendarCopied': '已复制链接',
  'crew.calendarGoogle': '添加到 Google 日历',
  'crew.calendarPaste': '或把此链接粘贴到 Apple / Outlook 日历：',
  'crew.calendarNeverRead': '尚无手机读取过该链接——打开订阅后才算生效。',
  'crew.calendarRead': '最近读取：{date}（共 {count} 次）。',
  'crew.calendarBusy': '处理中……',
  'crew.calendarFailed': '无法更改链接：{message}',
  'crew.archiveReason': '归档原因',
  'crew.archivePlaceholder': '已不再合作 · 已由…替代',
  'crew.archive': '归档',
  'crew.saving': '保存中……',
  'crew.save': '保存班组',
  'crew.needName': '请填写班组名称。',
  'crew.needReason': '请填写归档原因。',
  'crew.added': '{name} 已加入名录。填写手机号后即可登录门户。',
  'crew.saved': '{name} 已更新。',
  'crew.archived': '{name} 已归档 — 不再出现在排期中，其已完成的工作记录保留原名。',
  'crew.pending': '已提交审批 — 批准前不会生效。',
  'crew.refused': '未保存：{reason}',
  'crew.failed': '未保存：{message}',
  'inst.kicker': '安装工',
  'inst.title': '安装工',
  'inst.lede': '我们用谁、他们怎么收费、以及我们实际付了多少。',
  'inst.meta': '姓名 · 代号 · 队伍 · 费率 · 语言 · 已付工程数 · 已付金额 · 笔数 · 最近',
  'inst.summary': '台账共 {total} 位 · 其中 {subs} 位为外包',
  'inst.col.name': '安装工',
  'inst.col.crew': '队伍',
  'inst.col.rates': '本人工费率',
  'inst.col.ratesHint': '记录在安装工本人身上的费率，而不是价目表中的',
  'inst.col.account': '门户账号',
  'inst.col.language': '语言',
  'inst.col.unitRate': '单价',
  'inst.col.fee': '费用',
  'inst.col.basis': '依据',
  'inst.payable': '应付 {amount}（不含 GST）',
  'inst.feeComplete': '费用已齐',
  'inst.editFeesOnJob': '在工作卡上编辑费用行',
  'inst.feeConfirmed': '已于 {date} 确认 · {total} · 短信{state}',
  'inst.feeConfirmedChanged': '此后费用行有变动',
  'inst.feeNotConfirmed': '尚未向师傅确认',
  'inst.col.profile': '档案',
  'inst.col.dayRate': '最低日费（1 人）',
  'inst.dayRateHint': '最低日费条件——短暂一天仍需支付的费用',
  'inst.col.phone': '电话',
  'inst.col.abn': 'ABN',
  'inst.col.bank': '银行账户',
  'inst.col.priceBasis': '计价方式',
  'inst.col.paidBy': '付款方式',
  'inst.col.categories': '可做工种',
  'inst.col.category': '工种',
  'inst.col.version': '版本',
  'inst.col.effective': '生效',
  'inst.complete': '完整',
  'inst.gaps': '待补 {count} 项',
  'inst.notRecorded': '未记录',
  'inst.notAgreed': '未约定',
  'inst.list': '价目表',
  'inst.custom': '按项目计价',
  'inst.col.jobs': '工程数',
  'inst.col.jobsHint': '应付该团队费用的工作：已排给他们，或登记了约定费用',
  'inst.col.payable': '应付',
  'inst.col.payableHint': '这些工作的安装费合计（不含 GST）：已登记、按价目表提议或约定的额外工作。打开团队查看明细。',
  'inst.col.paidHint': '旧平台登记的付款',
  'inst.col.paid': '已付',
  'inst.col.last': '最近',
  'inst.col.date': '日期',
  'inst.col.job': '工程',
  'inst.col.amount': '金额',
  'inst.col.reference': '备注',
  'inst.col.confirmed': '已确认',
  'inst.noRates': '暂无价目表或规则',
  'inst.none': '台账中尚无安装工。',
  'inst.loading': '正在读取安装工…',
  'inst.unavailable': '无法读取安装工：{message}',
  'inst.gap': '只有缺少门户登录手机号需要处理。ABN 和银行资料属于可选的办公室记录。',
  'inst.back': '全部安装工',
  'inst.ledger': '人工台账',
  'inst.ledgerMeta': '每一个排期施工日及每一笔已付费用，均链接到对应工程',
  'inst.ledgerNote': '施工日属于工作记录，包括 Artemis 内部班组。外包每工程中位费用 {median} · 有 {twice} 个工程分次支付。「应付」目前未记录，因此不予显示。',
  'inst.allocated': '分摊',
  'inst.paymentRow': '付款',
  'inst.crewDay': '班组施工日',
  'inst.dayScheduled': '已排期',
  'inst.dayCompleted': '已完成',
  'inst.confirmed': '已确认',
  'inst.unconfirmed': '未确认',
  'inst.rateBook': '费率卡',
  'inst.col.status': '状态',
  'inst.rateNone': '尚无费率卡。',
  'inst.rateNoneArtemis': 'Artemis 自有班组 — 不计人工费，因此没有需要约定的费率卡。',
  'inst.rateNoneNote': '费率卡必须先创建并审批通过，才能据此向该班组付款。产品库中的工序价格表并不是费率卡——它只是将来编制费率卡的来源，此处不再当作已约定的费率展示。',
  'inst.rateNotApproved': '未审批',
  'inst.rateNotApprovedNote': '已提交，尚未审批。未经办公室审批，此处任何金额都不算约定。',
  'inst.rateApproved': '已审批',
  'inst.rateApprovedNote': '办公室已批准用于工程成本估算；承包商确认属另一项事实。以后变更费率须建立新版本。',
  'inst.approvalRecord': '办公室审批：{date} · {by} · {note}',
  'inst.approveExactVersion': '批准第 {version} 版费率',
  'inst.approvalHelp': '办公室仅批准此版本所列的费率。未报价的服务和每日最低收费仍属未知。',
  'inst.approvalEvidence': '办公室接受所列费率用于工程成本估算；未报价服务和每日最低收费仍待确认。',
  'inst.approvalSaving': '正在批准…',
  'inst.approvalSaved': '此版本费率已获批准，可用于工程成本估算。',
  'inst.approvalPending': '审批正在处理。',
  'inst.approvalFailed': '无法批准此版本费率。',
  'inst.rateBookMeta': '该班组已登记的费率；已提交和已批准版本均可查看',
  'inst.jobPricing': '工程工作与费率对照',
  'inst.jobPricingMeta': '把分配给该班组的已接受工程工作，与其当前登记费率卡逐项对照',
  'inst.jobPricingNone': '目前尚无 Artemis 工程工作分配给该班组。',
  'inst.jobPricingNote': '匹配小计仅为人工费，未含 GST。同一已接受工作即使跨多个施工日也只计价一次。费率不明确或缺失时保持待处理。',
  'inst.scopeGapTitle': '提案中有未进入已接受工程范围的项目',
  'inst.scopeGapHelp': '这些项目出现在已发布提案中，但不在已接受的工程报价或分配给班组的施工日内。材料数量不等于安装人工数量。分配或计价前请核实客户协议并测量施工量。',
  'inst.openProposal': '打开提案',
  'inst.openJobDays': '打开工程排期',
  'inst.col.work': '工程工作',
  'inst.col.qty': '数量',
  'inst.col.registeredRate': '登记费率',
  'inst.col.expected': '预计人工费',
  'inst.matchedSubtotal': '已匹配小计 {amount}',
  'inst.unresolvedCount': '{count} 项待处理',
  'inst.rateMatched': '已匹配',
  'inst.quantityMissing': '施工数量待核实',
  'inst.unitMismatch': '计量单位待核实',
  'inst.rateAmbiguous': '请选择费率',
  'inst.rateMissing': '缺少费率',
  'inst.rateUnmapped': '工作尚未映射',
  'inst.rateIncluded': '含在地板单价内',
  'inst.rateCandidate': '{activity} · {rate}/{uom} · {status}',
  'inst.rateLines': '已登记 {count} 行',
  'inst.pdfRegistered': '已登记 PDF',
  'inst.formRegistered': '表单提交',
  'inst.sourceDocument': '来源文件',
  'inst.sourceFingerprint': '来源文件 SHA-256：{hash}',
  'inst.originalUnavailable': '费率及文件指纹已登记，但系统中没有可下载的 PDF 原件。',
  'inst.rateTerms': '条款',
  'inst.ratePerUnit': '{rate} / {uom}',
  'inst.assistedOpen': '为此班组填写费率',
  'inst.assistedTitle': 'Artemis 协助填写安装服务报价',
  'inst.assistedHelp': '办公室人员或公司负责人均可填写此表。空白服务表示尚未报价，并非不提供；保存后建立待审核的新版本，不代表已批准付款费率。',
  'inst.assistedScope': '此班组可安装的地板类别',
  'inst.assistedService': '服务',
  'inst.assistedUnit': '单位',
  'inst.assistedRate': '未含 GST 费率',
  'inst.assistedOneCrew': '单组每日最低收费',
  'inst.assistedTwoCrew': '双组每日最低收费',
  'inst.assistedEffective': '生效日期',
  'inst.assistedMinimumHelp': '如尚未提供每日最低收费，请将两项都留空。不要用零代替未知金额。',
  'inst.assistedSource': '费率来源及仍需确认的事项',
  'inst.assistedGst': '我确认输入的费率均未含 GST',
  'inst.assistedSubmit': '保存待审核费率版本',
  'inst.assistedSaving': '保存中…',
  'inst.assistedSaved': '待审核费率版本已保存，尚未批准。',
  'inst.assistedPending': '费率提交正在等待审核。',
  'inst.assistedFailed': '无法提交费率。',
  'inst.assistedNotApproved': '此表仅为办公室记录，并非已批准的应付协议。用于工程前须确认未明确的工作范围和条款。',
  'inst.profile': '档案',
  'inst.missingTitle': '缺少门户登录手机号',
  'inst.missingNote': '填写手机号后，该班组即可登录安装工门户。',

  // ── 采购订单（AIW-PO） ────────────────────────────────────────────────────
  'po.title': '采购订单',
  'po.lede': '已订什么、供应商回了什么，以及施工队进场前还必须到货的部分。',
  'po.find': '查找采购订单',
  'po.findPlaceholder': '单号、工程、供应商或区域',
  'po.showClosed': '显示已收货与已取消',
  'po.count': '显示 {shown} / {total}',
  'po.col.po': '采购单',
  'po.col.job': '工程',
  'po.col.supplier': '供应商',
  'po.col.status': '状态',
  'po.col.sent': '已发出',
  'po.col.delivered': '已收货',
  'po.col.lines': '行数',
  'po.col.total': '合计（未含税）',
  'po.none': '没有符合条件的采购订单。',
  'po.unavailable': '无法读取采购订单：{message}',
  'po.loading': '正在读取采购订单…',
  'po.source.platform': '旧平台',
  'po.source.crm': 'CRM',
  'po.notRecorded': '未记录',
  'po.mustLand': '必须到货',
  'po.mustLandNote': '{sent} 发出、尚无确认、{install} 进场',
  'po.detail': '采购订单',
  'po.detail.supplier': '供应商',
  'po.detail.deliverTo': '送货地址',
  'po.detail.sent': '已发出',
  'po.detail.acknowledged': '已确认',
  'po.detail.promised': '承诺交期',
  'po.detail.delivered': '已收货',
  'po.detail.installDays': '施工日',
  'po.detail.email': '订货邮箱',
  'po.detail.creditTerms': '账期',
  'po.detail.legacyNote': '这是原平台订单。供应商凭证与提货记录登记在此，不会替换原始订单。',
  'po.line.item': '明细',
  'po.line.forJob': '用于工程',
  'po.line.toStock': '入仓库存',
  'po.line.ordered': '已订',
  'po.line.required': '测算所需',
  'po.line.unitCost': '单价',
  'po.line.total': '金额',
  'po.line.check': '已订 与 所需',
  'po.line.covers': '覆盖测算 +{percent}%',
  'po.line.short': '少 {amount}',
  'po.line.over': '超出 {percent}% — 请核对损耗',
  'po.line.unknown': '无测算数量',
  'po.noAmount': '未记录金额',
  'po.verify.title': '已订与所需',
  'po.verify.note': '只有不足或超出损耗上限才会标记；缺少测量数量的项目保持未核实。',
  'po.noneYet': '本工程尚未生成采购订单。',
  'job.materials.title': '材料到货与排期',
  'job.materials.none': '本工程尚未关联采购订单，暂无可核对内容。订单到达后请记录供应商的说法。',
  'job.materials.verify': '材料状态必须以采购订单为准核对。',
  'job.materials.viewPo': '打开采购订单',

  // ── 工程踢脚线 ──────────────────────────────────────────────────────────
  'skirting.panelTitle': '踢脚线',
  'skirting.panelMeta': '已接受范围 · 履约',
  'skirting.loading': '正在加载踢脚线范围…',
  'skirting.rule': '仅使用已接受范围及获批测量依据。客户自购预留绝不改变人工长度。',
  'skirting.quoteDetected': '已检测到接受报价：{quote}。请在下方核对是否由 Artemis 供应、$19/$20 单价及已包含高度。',
  'skirting.linkUnlock': '请先记录已接受范围。记录后，本表格下方会立即显示「建立客户网址」。',
  'skirting.ready': '资料已齐全——记录此范围即可启用独立客户网址。',
  'skirting.missing.summary': '仍需填写：{items}',
  'skirting.missing.acceptedEvidence': '已接受报价或方案依据',
  'skirting.missing.net': '有效的实测净米',
  'skirting.missing.measureSource': '测量来源',
  'skirting.missing.measureEvidence': '测量依据',
  'skirting.missing.height': '所选款式的高度',
  'skirting.missing.confirmedBy': '款式确认人',
  'skirting.saveFailed': '踢脚线范围未记录。请检查上方提示后重试。',
  'skirting.scope': '范围',
  'skirting.scope.supply': 'Artemis 供应并安装',
  'skirting.scope.install': '客户自购，Artemis 安装',
  'skirting.source': '接受依据',
  'skirting.source.proposal': '已接受方案',
  'skirting.source.quote': '已接受报价',
  'skirting.source.legacy': '已审核迁移工程',
  'skirting.evidence': '依据',
  'skirting.evidence.placeholder': '方案版本或已审核报价',
  'skirting.net': '实测净米',
  'skirting.net.placeholder': '获批前保持空白',
  'skirting.measureSource': '测量来源',
  'skirting.measureSource.placeholder': '已批准现场测量',
  'skirting.measureEvidence.placeholder': '文件及字段或版本',
  'skirting.profileHeight': '款式与高度',
  'skirting.profileRule': '可留空并标记「订货前确认」。不得伪造客户选择。',
  'skirting.height': '高度',
  'skirting.confirmedBy': '确认人',
  'skirting.channel.inPerson': '当面',
  'skirting.channel.phone': '电话',
  'skirting.channel.link': '客户链接',
  'skirting.channel.other': '其他',
  'skirting.guide': '自购参考（可选）',
  'skirting.guide.math': '实测净长 {net} LM + 预留 {extra} LM（{pct}%）= 建议购买 {recommended} LM。',
  'skirting.guide.pieces': '{pieces} 根 × {length} 米已核实库存长度 = {total} LM。',
  'skirting.noMeasure': '尚无获批净长度：保持待解决，不以房间周长猜算。',
  'skirting.correct': '记录更正',
  'skirting.record': '记录踢脚线范围',
  'skirting.revision': '版本 {version} · 记录人 {actor}。早期版本保留在审计历史中。',
  'skirting.savedCorrection': '踢脚线更正已记录',
  'skirting.savedScope': '踢脚线范围已记录',
  'skirting.discountPct': '折扣 %（作为该项下方单独一行）',
  'skirting.roundDown': '报价向下取整到 $',
  'skirting.discount.help': '该项的数量与单价不变。折扣在其下方单独成行；取整金额计入底部的抹零折扣行。',
  'skirting.acceptedRate': '已接受报价单价',
  'skirting.includedBand': '该单价包含的高度',
  'skirting.band.standard': '66–67 毫米 · 已包含',
  'skirting.band.medium': '92 毫米 · 已包含',
  'skirting.band.tall': '138–140 毫米 · 已包含',
  'skirting.acceptedBasis': '这是已接受报价的基准。其他高度只向客户显示相对此基准的差价。',
  'skirting.guide.title': '踢脚线指南——以电邮发送 PDF（默认方式）',
  'skirting.guide.supplyHelp': '打印每种款式的按比例截面图及安装实拍照片，标出已接受报价所包含的高度，其他高度只显示差价。客户以文字回复后，在上方以「电邮」渠道记录其选择。',
  'skirting.guide.installHelp': '以同样的图纸打印选购指南，附已批准净长度及余量。不显示 Artemis 供应价格。',
  'skirting.quote.applied': '已自动加入已接受报价：{lm} 延米 × {rate}/延米 = {amount} + GST，新报价 {quote}。客户确认即为接受凭证。',
  'skirting.quote.skipped': '已记录，但未加入报价：{reason}',
  'skirting.noneQuoted': '此工单报价中没有踢脚线。仍可在下方提供门套线。',
  'skirting.architrave.title': '门套线——单独文件',
  'skirting.architrave.help': '打印每扇门的价格（单面或双面，按宽度及厚度，任何款式同价），以及每种款式在斜接顶角的近景图。只需工单及客户电邮。',
  'skirting.architrave.send': '以电邮发送门套线指南（PDF）',
  'skirting.architrave.drafted': '门套线指南草稿已建立',
  'skirting.architrave.record': '记录通过短信、电话或当面收到的确认——会自动加入已接受报价',
  'skirting.architrave.recordAction': '记录并加入报价',
  'skirting.architrave.recorded': '门套线已记录',
  'skirting.architrave.profile': '款式',
  'skirting.architrave.size': '尺寸',
  'skirting.architrave.applied': '已自动加入已接受报价：{doors} 扇门 × 每扇 {rate} = {amount} + GST，新报价 {quote}。',
  'skirting.architrave.answer': '客户已在线选择（{who}）：{what} · {total}',
  'skirting.architrave.one': '单面',
  'skirting.architrave.both': '双面',
  'skirting.architrave.doors': '扇门',
  'skirting.architrave.customerSupplies': '客户自备板材',
  'skirting.architrave.artemisSupplies': 'Artemis 供应',
  'skirting.guide.urlToo': '电邮同时附有在线选择的专属网址。再次发送会生成新网址；之前的网址在您撤销前仍然有效。',
  'skirting.guide.language': '电邮文字',
  'skirting.guide.send': '以电邮发送踢脚线指南（PDF）',
  'skirting.guide.drafted': '踢脚线指南草稿已建立',
  'skirting.guide.review': '草稿已保存并附上 PDF。请在「通讯」中审阅后发送——目前尚未发出任何邮件。',
  'skirting.guide.open': '打开通讯',
  'skirting.link.alternative': '备选方式：独立客户网址',
  'skirting.link.title': '独立客户链接',
  'skirting.link.supplyHelp': '客户从 Artemis 套餐选择款式及高度。链接清楚显示已包含高度及每米 +$4/+5 的差价。',
  'skirting.link.installHelp': '客户可查看选购参考，并记录将提供新踢脚线、重装现有踢脚线或尚未决定。不显示材料供应价格。',
  'skirting.link.create': '建立客户网址',
  'skirting.link.replace': '建立替代网址',
  'skirting.link.revoke': '撤销网址',
  'skirting.link.copy': '复制',
  'skirting.link.copyNow': '请现在复制此网址。系统只保存安全哈希，因此稍后无法恢复同一个明文网址。',
  'skirting.link.status': '最新链接：{status}',
  'skirting.link.created': '客户踢脚线网址已建立',
  'skirting.link.revoked': '客户踢脚线网址已撤销',

  // ── 员工记录客户在门户外作出的接受决定 ────────────────────────────────
  'proposal.accept.title': '记录客户接受',
  'proposal.accept.loading': '正在加载接受记录…',
  'proposal.accept.unavailable': '无法读取接受记录。',
  'proposal.accept.releaseFirst': '请先发布含价格的客户版本，再记录接受。',
  'proposal.accept.rule': '客户已经同意。选择确认方式，然后接受已发布版本 {version}。',
  'proposal.accept.recorded': '方案已接受',
  'proposal.accept.jobCreated': '工程 {ref} 已建立。',
  'proposal.accept.jobProcessing': '工程正在完成建立。',
  'proposal.accept.channel': '方式',
  'proposal.accept.sms': '短信',
  'proposal.accept.phone': '电话',
  'proposal.accept.inPerson': '当面',
  'proposal.accept.email': '电邮',
  'proposal.accept.confirm': '记录接受并创建工作单',
  'proposal.accept.saved': '方案已按 {amount} 接受。工程 {ref} 已进入待排期。',

  // ── 引擎发给客户的文字，按英文原文精确匹配 ────────────────────────────────
  'engine.notice.selectColour': '最终价格取决于您所选择的颜色。',
  'engine.notice.promotion': '促销价 — 请联系我们确认库存。',
  "po.office.office": "采购办公室",
  "po.office.findJob": "查找工作",
  "po.office.findJobHint": "客户、地址或工作编号",
  "po.office.onlyMissing": "仅显示尚无采购单",
  "po.office.completedJobs": "包括已付清工作",
  "po.office.chooseJob": "选择工作",
  "po.office.noPo": "尚无采购单",
  "po.office.existingCount": "已有 {count} 张采购单",
  "po.office.missingCount": "{count} 项工作尚无采购单",
  "po.office.jobListFailed": "无法完整加载工作和订单。请刷新后再确认采购单状态。",
  "po.office.noJobs": "没有符合筛选条件的工作。",
  "po.office.loadingPlan": "正在加载已保存的材料计划…",
  "po.office.noMaterials": "此已保存报价没有可订购的供应商材料。",
  "po.office.linkedOrders": "已关联采购单",
  "po.office.draft": "从已接受的工作准备采购单",
  "po.office.job": "工作编号",
  "po.office.supplier": "供应商编号",
  "po.office.required": "所需日期",
  "po.office.requiredHint": "安装日 {date} 前 {days} 个工作日",
  "po.office.noInstallDate": "尚无安装日期，请选择日期。",
  "po.office.deliveryPickup": "自提（我们取货）",
  "po.office.deliveryWarehouse": "送货至 {label}",
  "po.office.deliveryCustom": "送货至其他地址",
  "po.office.customAddress": "送货地址",
  "po.materials.fromFloorArea": "数量取自报价地面面积；方案中此项未保存数量。",
  "po.office.destination": "送货地址／提货地点",
  "po.office.prepare": "读取已保存的材料计划",
  "po.materials.hint": "已确认的材料已预先选好。请核对数量，只需处理下方异常。",
  "po.materials.coverage": "现有采购单已覆盖",
  "po.materials.coverageHint": "追加采购前请先检查现有订单。已覆盖的材料不会再次预选。",
  "po.materials.coveredLines": "已覆盖 {count} 行",
  "po.materials.ready": "可以订购",
  "po.materials.exceptions": "需要处理 · {count}",
  "po.materials.exceptionHard": "处理后才能订购",
  "po.materials.exceptionReview": "请核对",
  "po.exception.unquoted": "此测量工作不在已接受的方案中，订购前请确认。",
  "po.exception.missingVariant": "请选择并确认准确的供应商产品及规格。",
  "po.exception.missingPrice": "需要当前供应商采购价。",
  "po.exception.missingUnit": "需要供应商采购单位。",
  "po.exception.invalidQuantity": "请输入有效的正数订购数量。",
  "po.exception.missingMeasurement": "缺少所需测量数据。",
  "po.exception.review": "订购前请核对此材料。",
  "po.exception.existingCoverage": "现有订单已覆盖此材料。",
  "po.exception.acceptedSnapshotQuantity": "已接受材料的采购数量尚未确定。",
  "po.exception.acceptedSnapshotLine": "已接受材料已不再匹配唯一的当前订单行。",
  "po.exception.acceptedSnapshotUnit": "请核对已接受材料与供应商采购单位。",
  "po.office.deliveryTiming": "此订单何时需要？",
  "po.office.timing.required_by": "指定所需日期",
  "po.office.timing.asap": "尽快",
  "po.office.timing.hold": "暂停——请勿发送",
  "po.office.holdSendBlocked": "此订单已暂停。请先更改时间要求再发送。",
  "po.materials.source": "当前方案第{version}版及最新保存的范围内现场测量。客户已确认的价格与记录保持不变。",
  "po.materials.floorStairs": "地面 {floor} m² + 楼梯 {stairs} m² = {total} m²",
  "po.materials.floorStairsSteps": "地面 {floor} m² + 楼梯 {steps} 级 × {rate} m²（{stairs} m²）= {total} m²",
  "po.materials.required": "所需数量",
  "po.materials.assessment": "最新范围内测量",
  "po.materials.unquoted": "图纸上已测量，报价中未包含",
  "po.materials.proposal": "已保存方案／报价",
  "po.materials.savedQuantity": "原保存数量",
  "po.materials.floorOverage": "订购数量已含地板 {pct}% 余量；未用的整箱可退",
  "po.materials.floorSpare": "订购数量在报价面积与损耗之上另加 {extra} m² 备用，再按整箱取整。",
  "po.materials.supplierItem": "供应商产品及规格",
  "po.materials.choose": "选择供应商规格",
  "po.materials.orderQuantity": "订购数量",
  "po.materials.stockLength": "每根长度 {length} 米",
  "po.materials.cartons": "{count} 箱 × 每箱 {size} {unit} = {total} {unit}",
  "po.materials.freeStock": "可用库存 {quantity} {unit}；不会自动扣减。",
  "po.materials.unpriced": "缺少供应商产品或采购价。此需求保留在清单中。",
  "po.materials.ambiguousFloor": "此辅料涉及不同的地板产品、规格或颜色。订购前请确认其地板范围和供应商产品。",
  "po.materials.incomplete": "请选择有采购价的供应商产品并填写订购数量。",
  "po.materials.barrier": "通常由安装方采购。仅在 Artemis 采购时勾选。",
  "po.materials.runs": "查看 {count} 段测量",
  "po.materials.selected": "已选 {count} 项 · ${total} 未含 GST",
  "po.materials.orderReason": "该项目追加选定材料",
  "po.materials.saveSelected": "保存已选材料 — {supplier}",
  "po.materials.target": "保存到",
  "po.materials.newOrder": "新建追加采购单",
  "po.materials.existingDraft": "现有草稿 {id} · 第{version}版",
  "po.materials.openSaved": "打开已保存采购单",
  "po.materials.refresh": "材料服务正在更新。保存采购单前请刷新此页面。",
  "po.office.create": "保存草稿",
  "po.office.additional": "追加订单原因",
  "po.office.stock": "减少订购数量前必须分配可用库存。",
  "po.office.pickup": "提货编号",
  "po.office.provisional": "暂定——等待正式邮件",
  "po.office.conflict": "来源不一致——提货前请核对",
  "po.office.register": "登记供应商凭证",
  "po.office.kind": "凭证类型",
  "po.office.value": "编号／日期／详情",
  "po.office.channel": "来源渠道",
  "po.office.manual": "办公室记录",
  "po.office.email": "电子邮件",
  "po.office.wechat": "微信",
  "po.office.mailbox": "邮箱",
  "po.office.message": "邮件编号",
  "po.office.attachment": "附件名称",
  "po.office.note": "原因／来源详情",
  "po.office.save": "保存凭证",
  "po.office.history": "凭证历史",
  "po.office.referenceNote": "提货编号不代表已付款、可提货或已提取。",
  "po.office.pickup_reference": "提货编号",
  "po.office.invoice_reference": "发票编号",
  "po.office.confirmation": "供应商确认",
  "po.office.promised_date": "承诺日期",
  "po.office.ready": "可提货",
  "po.office.collected": "已提取／收到",
  "po.office.supersedes": "更正之前的凭证",
  "po.office.new": "新凭证",
  "po.office.copied": "已复制",
  "po.office.copy": "复制",
  "po.office.saved": "已保存",
  "po.office.pending": "请在下方审核；尚未执行。",
  "po.office.failed": "无法保存：{message}",
  "po.office.busy": "处理中…",
  "po.office.reviews": "订单审核",
  "po.office.edit": "修改草稿",
  "po.office.reason": "为何修改？",
  "po.office.quantity": "数量",
  "po.office.cost": "单位成本",
  "po.office.recipient": "供应商订单邮箱",
  "po.office.cc": "抄送邮箱，以逗号分隔",
  "po.office.reviewSend": "发送采购单",
  "po.office.subject": "邮件主题",
  "po.office.requiredAsap": "留空表示尽快发货。",
  "po.office.emailPreview": "将要发送的邮件",
  "po.office.to": "收件人",
  "po.office.ccShort": "抄送",
  "po.office.pdfShow": "查看采购单 PDF",
  "po.office.pdfLoading": "正在生成 PDF…",
  "po.office.pdfTitle": "采购单 PDF 预览",
  "po.office.pdfOpen": "在新标签页打开 PDF",
  "po.office.pdfUnavailable": "暂时无法预览 PDF。",
  "po.office.pdfFirst": "发送前请先查看 PDF",
  "po.office.body": "给供应商的留言",
  "po.office.sendNote": "保存草稿不会发送。请核对准确的订单和邮件后按“发送”，这一下就是您的批准，不会再要求您审批。",
  "po.office.inbox": "待审核供应商回复",
  "po.office.link": "将回复关联至订单",
  "po.office.selectOrder": "选择采购订单",
  "po.office.emptyInbox": "没有待审核的供应商回复。",
  "po.office.supply": "供应",
  "po.office.refreshing": "正在刷新",
  "po.office.mustLandCount": "{count} 个订单需要在安装前到货",
  "po.office.yes": "是",
  "po.office.packs": "{count} 包",
  "po.office.lines": "{count} 行",
  "po.office.status.draft": "草稿",
  "po.office.status.approved": "已批准",
  "po.office.status.sent": "已发送",
  "po.office.status.acknowledged": "已确认",
  "po.office.status.paid": "已付款",
  "po.office.status.delivered": "已送达",
  "po.office.status.received": "已收到",
  "po.office.status.cancelled": "已取消",
  'po.office.no': '否',
"po.office.cancelDelivery":"请求取消发送",
"po.office.checkDelivery":"再次检查已保存邮件",
  'po.office.saveFirst': '请先保存修改再审核',
  'calendar.busy': '忙碌',
  'calendar.allDay': '全天',
  'calendar.actionNew': "新预约",
  'calendar.actionOpen': "打开预约",
  'calendar.appointmentEditor': "预约 · 悉尼时间",
  'calendar.actionTitle': "简短标题",
  'calendar.actionLocation': "地点",
  'calendar.actionStart': "开始 · 悉尼",
  'calendar.actionEnd': "结束 · 悉尼",
  'calendar.actionNotes': "备注",
  'calendar.actionCheck': "核查已保存的请求",
  'calendar.actionSave': "保存预约",
  'calendar.actionCancel': "取消预约",
  'calendar.actionClose': "关闭",
  'calendar.actionChecking': "正在核实保存的日历记录…",
  'calendar.actionSaved': "已保存并重新读取 Jacky 日历。",
  'calendar.actionError': "无法核实保存结果。请重新打开此表单核查同一请求，勿重复提交。",
  'calendar.subscribeInstall': "订阅安装日历",
  'calendar.kicker': '工作',
  'calendar.title': '日历',
  'calendar.lede': '安装、现场报价及其他预约 · 悉尼时间',
  'calendar.internal': '自有班组',
  'calendar.external': '外包班组',
  'calendar.unassigned': '班组待确认',
  'calendar.staff': 'Artemis 员工手机',
  'calendar.mixed': '多个安装班组',
  'calendar.siteQuotation': '现场报价',
  'calendar.otherVisit': '其他到访／预约',
  'calendar.previous': '上一个{mode}',
  'calendar.next': '下一个{mode}',
  'calendar.today': '今天',
  'calendar.month': '月',
  'calendar.week': '本周及下周',
  'calendar.view': '日历视图',
  'calendar.legend': '日历颜色说明',
  'calendar.monthGrid': '月历',
  'calendar.weekGrid': '两周日历',
  'calendar.showDay': '查看 {date}',
  'calendar.installers': '{count} 名安装人员',
  'calendar.installer': '{count} 名安装人员',
  'calendar.installationJobs': '{count} 个安装工作',
  'calendar.installationsUnavailable': '无法读取安装安排',
  'calendar.loadingInstallations': '正在加载安装安排…',
  'calendar.liveSalesExcluded': '此工作区不包含 Jacky 的实时日历',
  'calendar.salesAppointments': '{count} 个 Jacky 预约',
  'calendar.loadingWeeks': '正在加载其他周…',
  'calendar.checkingChanges': '正在检查更新…',
  'calendar.updateFailed': '更新失败；已保留保存的项目',
  'calendar.salesLastChecked': 'Jacky 日历上次检查：{date}',
  'calendar.refresh': '刷新日历',
  'calendar.sourceError': '有一个日历来源无法更新。已保存的项目仍会显示，但可能缺少变更或预约。请刷新重试。',
  'calendar.invalidDates': '有 {count} 个安装日期无法放入日历。请检查来源工作单。',
  'calendar.more': '另有 {count} 项',
  'calendar.dayDetails': '所选日期详情',
  'calendar.installerDetails': '安装人员详情',
  'calendar.crewNotRecorded': '未记录班组',
  'calendar.taskNotRecorded': '未记录工作内容',
  'calendar.completed': '已记录完工',
  'calendar.scheduled': '已排期；尚未确认到场',
  'calendar.openWork': '打开安装工作',
  'calendar.addressNotRecorded': '未记录地址',
  'calendar.bookingUnconfirmed': 'Jacky 日历；预约不代表已确认到场',
  'calendar.waiting': '正在等待完整日历数据。',
  'calendar.emptyDay': '已加载的来源中，此日没有项目。',
  'calendar.footnote': '安装项目使用已保存的工作单日期。所有预约仅使用 jacky@artemisflooring.com。其他员工只能看到私人时段为忙碌；已取消项目不会显示。现场报价按已保存的类别或标题识别；其余项目保留为其他预约。无法匹配的班组会显示为待确认。',
  'operations.kicker': 'Artemis Flooring · 悉尼时间',
  'operations.title': '运营总览',
  'operations.lede': '优先查看安装安排：本周、下周，以及仍在等待可靠日期的工作。',
  'operations.openCalendar': '打开完整日历',
  'operations.scheduleTitle': '安装日程',
  'operations.scheduleNote': '显示安装记录中已保存的班组工作日，不会虚构缺失日期。',
  'operations.refreshing': '正在刷新……',
  'po.exception.savedQuotePrice': '此价格来自已保存的报价；订购前请核对供应商当前价格。',
  'operations.scheduleUnavailable': '无法加载安装日程。其他部分分别加载。',
  'operations.thisWeek': '本周安装',
  'operations.nextWeek': '下周安装',
  'operations.dayCount': '{count} 个工作日',
  'operations.loadingSchedule': '正在加载安装安排……',
  'operations.loadingWork': '正在加载工作……',
  'operations.crewTbc': '班组待确认',
  'operations.taskTbc': '工作范围待确认',
  'operations.noInstallations': '本周没有已记录的安装工作。',
  'operations.delayedTitle': '安装日期已过——请核查',
  'operations.delayedNote': '最近记录的安装日期已过，工程可能已完成，也可能需要另定日期。',
  'operations.lastRecordedDate': '原记录日期：{date}',
  'operations.noDelayed': '没有所有安装日期均已过的已排期工程。',
  'operations.unscheduledTitle': '未排期阶段及工程',
  'operations.unscheduledNote': '尚未记录下一日期的进行中的工作，只在日期确定后安排。',
  'operations.nextPhaseUnknown': '进行中的工程 · 下一阶段日期未记录',
  'operations.installDateUnknown': '等待排期 · 安装日期未记录',
  'operations.noUnscheduled': '没有等待日期的工作。',
  'operations.itemCount': '{count} 项',
  'operations.financialSummary': '每月财务表现',
  'month.kicker': '业绩 · {month} · 悉尼时间',
  'month.title': '本月',
  'month.ledeAttention': '有 {count} 项需要关注，已与下方保存的跟进事项合并显示。',
  'month.ledeClear': '查看本月业绩、已确认工程、全部已排期与待排期工程，以及营收与毛利的累积。',
  'month.topLine': '营收 · 本月至今',
  'month.operatingProfit': '营运利润',
  'month.operatingBasis': '工程毛利减去每月固定成本目标 {target}',
  'month.grossMargin': '毛利率',
  'month.grossProfit': '扣除固定成本前毛利 {amount}',
  'month.recognisedJobs': '已确认收入工程',
  'month.recognitionBasis': '已开票及已全额付款 · 按结算日期',
  'month.installValue': '安装金额',
  'month.scheduledWindow': '已排期',
  'month.pendingSchedule': '待排期',
  'month.coverageLine': '毛利 {profit} · 覆盖 {target} 的 {coverage}% · {count} 个工程',
  'month.coverageIncomplete': '毛利暂不可用 · {total} 个工程中 {known} 个已记录成本',
  'month.opportunityValue': '进行中商机金额',
  'month.opportunityCoverage': '毛利 {profit} · 覆盖 {target} 的 {coverage}% · {opportunities} 个商机 · 30天内 {leads} 条线索',
  'month.opportunityCoveragePartial': '已记录毛利至少 {profit} · {priced} 个已报价商机中 {known} 个具备毛利 · {opportunities} 个商机 · 30天内 {leads} 条线索',
  'month.drillDown': '查看详情 →',
  'month.detailKicker': '业绩详情',
  'month.close': '关闭',
  'month.profitAmount': '毛利 {amount}',
  'month.noDetailRows': '此数据目前没有对应记录。',
  'month.recordedOpportunityValue': '已记录的进行中商机金额',
  'month.leadsLast30': '过去30天登记的已接受线索',
  'month.unreferencedOpportunity': '商机编号待定',
  'month.legacySource': '旧系统销售管线',
  'month.aiwSource': 'AIW 销售管线',
  'month.valueUnavailable': '金额未记录',
  'month.profitUnavailable': '毛利未记录',
  'month.costsIncomplete': '成本待完善 · {count} 项',
  'month.profitIncomplete': '毛利暂不可用 · {total} 条记录中 {known} 条已记录成本',
  'month.performanceTitle': '本月业绩',
  'month.performanceMeta': '{month} · 已确认收入工作',
  'month.loadingPerformance': '正在载入本月业绩…',
  'month.performanceUnavailable': '无法载入本月业绩，系统不会推测金额。',
  'month.noRecognisedJobs': '本月尚未确认任何收入。',
  'month.job': '工程',
  'month.closed': '确认日期',
  'month.revenue': '营收',
  'month.profit': '毛利',
  'month.total': '本月合计',
  'month.financialNote': '工程进入已开票或已全额付款后确认收入，并按保存的结算日期计入月份。营运利润为已确认工程毛利减去保存的每月固定成本目标，并非已收现金。',
  // 本月累积图与“日期已过”标记（Jacky，2026年10月23日：已排期不再限30天）。
  'month.datePassed': '日期已过',
  'month.datePassedCount': '{count} 个日期已过 · 尚未开票',
  'month.buildUpTitle': '累积',
  'month.buildUpMeta': '已确认 → + 已排期 → + 待排期 → + 商机 · 不含GST',
  'month.buildUp.recognised': '已确认',
  'month.buildUp.scheduled': '已排期',
  'month.buildUp.pending_schedule': '待排期',
  'month.buildUp.opportunity': '商机',
  'month.buildUpRevenue': '营收',
  'month.buildUpProfit': '毛利（已有成本）',
  'month.buildUpBase': '前面各步累积',
  'month.buildUpUnpriced': '{count} 个无成本',
  'month.buildUpTarget': '固定成本 {amount}',
  'month.buildUpAria': '营收与毛利累积：{summary}',
  'month.buildUpOpportunityNote': '商机尚未成交，以浅色显示。',
  'month.est': '估',
  'month.estimatedCount': '{count} 个为估算',
  'month.estimatedHint': '该工程部分成本仍为接单时的估算：未下单材料按产品目录成本，未分配施工队的工作按 Artemis 标准费率。',
  'month.buildUpEstimated': '基于估算的毛利',
  'month.followUps': '跟进 · 已包含需立即关注事项',
  'month.openQueue': '打开完整队列',
  'month.newEnquiries': '有 {count} 个新咨询需要回复',
  'month.newEnquiriesDetail': '记录一次联系尝试后会清除此提醒；客户有新回复时会再次提醒。',
  'month.reviewNow': '立即查看',
  'month.materialRisk': '材料风险 · {name}',
  'month.openJob': '打开工程',
  'month.followUpsUnavailable': '无法载入跟进事项。这不代表队列为空。',
  'month.loadingFollowUps': '正在载入跟进事项…',
  'month.nothingNeedsAttention': '目前没有需立即关注的事项，也没有已保存的跟进。',
  'month.late': '已逾期 {age}',
  'month.previousValue': '上月 {amount}',
  'month.upFromPrevious': '较上月上升 {percent}%（{amount}）',
  'month.downFromPrevious': '较上月下降 {percent}%（{amount}）',
  'customer.proposal.lastUpdated': '您的方案 · 最后更新 {date}',
  'customer.print.preparedFor': '致 {name}',
  'customer.print.address': '现场地址：{address}',
  'customer.print.lastUpdated': '最后更新 {date}',
  'customer.print.draftSaved': '草稿保存于 {date}',
  'customer.print.openDraft': '请从员工工作空间打开已保存草稿进行审阅。',
  'customer.print.draftTitle': '工作草稿 · 内部审阅 · 版本 {version}',
  'customer.print.draftNotice': '此草稿尚未发布。价格均不含 GST；对外金额标注“+ GST”。',
  'customer.print.draftBlockers': '发布前待确认',
  'customer.print.aboutFloor': '关于您的地板',
  'customer.print.brand': '品牌：{brand}',
  'customer.print.colour': '颜色：{colour}',
  'customer.print.specs': '产品规格',
  'customer.print.unavailable': '此方案打印版暂不可用。',
  'customer.pay.title': '{kind}付款',
  'customer.pay.deposit': '定金',
  'customer.pay.balance': '尾款',
  'customer.pay.loading': '正在加载您的付款信息…',
  'customer.pay.unavailable': '此付款链接不可用',
  'customer.pay.unavailableHint': '请向 Artemis Flooring 索取新的链接。',
  'customer.pay.job': '工单 {job}',
  'customer.pay.invoice': '税务发票 {invoice}',
  'customer.pay.viewInvoice': '查看发票',
  'customer.pay.amountDue': '应付{kind}',
  'customer.pay.paid': '已收到付款 — 谢谢',
  'customer.pay.paidHint': '已于 {date} 登记。此请求无需再付款。',
  'customer.pay.cancelled': '此付款请求已由 Artemis Flooring 撤回。如您仍需付款，请与我们联系。',
  'customer.pay.transferTitle': '银行转账付款',
  'customer.pay.accountName': '账户名称',
  'customer.pay.bsb': 'BSB（银行分行号）',
  'customer.pay.account': '账号',
  'customer.pay.reference': '付款备注',
  'customer.pay.referenceNote': '转账时请在付款说明中填写您的工单编号 {job}，以便我们核对您的付款。',
  'customer.pay.onInvoice': '相同的账户信息也印在您的税务发票上。',
  'customer.pay.cardTitle': '银行卡付款',
  'customer.pay.cardButton': '用银行卡支付 {amount}',
  'customer.pay.cardHint': '将在新标签页打开 ANZ Worldline 安全支付页面。',
  'customer.pay.contact': '关于此付款的问题：{issuer} · {phone} · {email}',
  'attachments.loading': '正在加载附件…',
  'attachments.open': '在新标签页打开此文件',
  'payreq.open': '发票及付款请求',
  'payreq.title': '发票及付款请求',
  'payreq.close': '关闭',
  'payreq.loading': '正在加载付款请求…',
  'payreq.failed': '无法加载付款请求，请重试。',
  'payreq.agreement': '约定金额（不含 GST）',
  'payreq.gst': 'GST（商品及服务税）',
  'payreq.payable': '应付',
  'payreq.received': '已收',
  'payreq.receivedFromXero': '来自 Xero 发票 {invoice}；工单登记为 {recorded}。',
  'payreq.balance': '未付余额',
  'payreq.invoice': 'Xero 发票',
  'payreq.noInvoice': '尚未开具',
  'payreq.issueInvoice': '在 Xero 开具发票',
  'payreq.issued': '发票 {invoice} 已在 Xero 开具。',
  'payreq.invoiceOpen': '打开在线发票',
  'payreq.linkExisting': '关联已有的 Xero 发票',
  'payreq.linkNumber': 'Xero 发票编号',
  'payreq.linkExistingHint': '该发票已在 Xero 中手工开具。按编号从 Xero 读取并记录到本工单；Xero 中不作任何更改，也不会发送任何内容。',
  'payreq.linkAcceptDifference': '即使发票金额与协议不一致也关联',
  'payreq.linked': '已从 Xero 关联发票 {invoice}。',
  'payreq.linkedUnder': '已从 Xero 关联发票 {invoice}（抬头：{contact}）。',
  'payreq.notConfigured': '后台尚未连接 Xero 开票。',
  'payreq.cashHint': '此工单为现金约定：请使用收款及收据。',
  'payreq.legacyHint': '迁移工单：收款记录保存在此；工单卡上的定金仍由员工手动编辑。',
  'payreq.requests': '付款请求',
  'payreq.none': '尚无定金或尾款请求。',
  'payreq.raise': '发起付款请求',
  'payreq.kind': '请求',
  'payreq.kind.deposit': '定金',
  'payreq.kind.balance': '尾款',
  'payreq.basis': '金额方式',
  'payreq.basis.percent': '约定金额的 %',
  'payreq.basis.amount': '金额（不含 GST）',
  'payreq.value': '数值',
  'payreq.preview': '应付 {amount}（{ex} + GST）',
  'payreq.channel': '发送方式',
  'payreq.channel.email': '电子邮件',
  'payreq.channel.sms': '短信',
  'payreq.link': 'Worldline 付款链接（可选）',
  'payreq.linkHint': '在 ANZ Worldline 商户门户（Create payments）按此金额创建 Pay by Link，并将其网址粘贴到这里。留空则仅银行转账。',
  'payreq.note': '备注',
  'payreq.raised': '请求已发起，消息已准备好等待发送审核。',
  'payreq.copyLink': '复制付款页链接',
  'payreq.linkCopied': '付款页链接已复制。',
  'payreq.state.requested': '等待中',
  'payreq.state.paid': '已付',
  'payreq.state.cancelled': '已取消',
  'payreq.paidLine': '{date} 通过{method}收到 {amount}',
  'payreq.markReceived': '标记已收款',
  'payreq.method': '收款方式',
  'payreq.method.transfer': '银行转账',
  'payreq.method.card': '银行卡',
  'payreq.amount': '收到金额',
  'payreq.date': '收款日期',
  'payreq.reference': '银行 / Worldline 参考号',
  'payreq.register': '登记收款',
  'payreq.registered': '收款已登记。',
  'payreq.attachLink': '附加 Worldline 链接',
  'payreq.linkAttached': '付款链接已保存。',
  'payreq.cardLink': '银行卡链接',
  'payreq.cardLink.none': '无',
  'payreq.void': '作废发票',
  'payreq.reread': '从 Xero 重新读取',
  'payreq.reread.done': '已从 Xero 重新读取发票；没有更新的内容可记录。',
  'payreq.reread.deposit': '已从 Xero 重新读取发票；定金 {amount} 已记录到工单。',
  'payreq.reread.paid': '已从 Xero 重新读取发票；已于 {date} 全额付清。',
  'payreq.voidHint': '在 Xero 中作废该已授权发票（尚无收款登记），以便重新开具正确的发票。新发票会列出方案中每一行。',
  'payreq.voided': '发票已在 Xero 作废，请重新开具。',
  'payreq.notify': '通知客户',
  'payreq.notify.sms': '短信 — 已收到付款',
  'payreq.notify.email': '电子邮件 — 已收到付款',
  'payreq.notify.none': '不发送消息',
  'payreq.registeredNotify': '收款已登记。客户通知已准备好，等待您点击发送。',
  'payreq.registeredNoMobile': '收款已登记。客户没有手机号码，未准备短信。',
  'payreq.registeredNoEmail': '收款已登记。客户没有电子邮件地址，未准备消息。',
  'payreq.cancel': '取消请求',
  'payreq.cancelReason': '原因',
  'payreq.cancelled': '请求已取消。',
  'payreq.reopen': '重新开启请求',
  'payreq.reopenHint': '同一请求恢复为待付款：客户已持有的链接重新生效。不会发送任何信息。',
  'payreq.reopened': '请求已重新开启，客户的链接已恢复。',
  'payreq.review': '发送审核',
  'payreq.balanceHint': '安装完成后再发起尾款请求。',
  "site.nav.main": "主导航",
  "site.nav.home": "Artemis Flooring 首页",
  "site.nav.floors": "探索地板",
  "site.nav.commercial": "商业空间",
  "site.nav.services": "服务",
  "site.nav.contact": "联系我们",
  "site.nav.myHome": "我的家",
  "site.nav.book": "预约上门测量",
  "site.nav.bookShort": "预约",
  "site.nav.price": "估算我的地板价格",
  "site.nav.menu": "菜单",
  "site.nav.language": "语言",
  "site.hero.eyebrow": "美好地板 · 日常生活",
  "site.hero.title1": "让家，多一份",
  "site.hero.title2": "心动与温暖。",
  "site.hero.intro": "喜欢的风格，合适的预算。找到心仪的地板，先看参考价格，决定之前我们先上门测量。",
  "site.hero.explore": "寻找我的地板",
  "site.hero.note": "从您的房间开始，无需注册。",
  "site.hero.inPicture": "图中地板",
  "site.hero.tier.signature": "臻选",
  "site.hero.tier.premium": "优选",
  "site.hero.tier.essential": "精选",
  "site.hero.slide": "显示第 {n} 张，共 {total} 张",
  "site.hero.slides": "精选地板",
  "site.hero.scroll": "向下寻找灵感",
  "site.promise.1": "美好选择，合理预算",
  "site.promise.2": "决定之前，先上门测量",
  "site.promise.3": "从最初构想到安装完成的专业帮助",
  "site.proof.installs": "2026 年已完成的安装项目",
  "site.proof.reviews": "{count} 条 Google 评价",
  "site.proof.note": "依据已完成的安装记录统计。Google 评分核实日期：2026 年 9 月 23 日。",
  "site.look.eyebrow": "寻找家的感觉",
  "site.look.title1": "每个家都有故事。",
  "site.look.title2": "从脚下开始。",
  "site.look.copy": "周日清晨的宁静，朋友相聚的欢笑。找到符合您风格、也适合日常生活的地板。",
  "site.look.tabs": "风格",
  "site.look.warm": "温暖自然",
  "site.look.character": "个性魅力",
  "site.look.soft": "柔软宁静",
  "site.look.warmTitle": "让阳光住进来。",
  "site.look.warmCopy": "柔和色调，自然纹理。为宁静、通透的家找到美好的起点。",
  "site.look.characterTitle": "一眼心动的细节。",
  "site.look.characterCopy": "人字拼赋予空间独特节奏，让日常生活的每个角落更有魅力。",
  "site.look.softTitle": "享受柔软的周日。",
  "site.look.softCopy": "脚下的舒适，生活中的片刻宁静。为放慢脚步的房间选择地毯。",
  "site.look.photo": "供应商空间实景图",
  "site.look.cta": "查看这款地板",
  "site.edit.eyebrow": "地板精选",
  "site.edit.title": "好看的地板，更多的可能。",
  "site.edit.collections": "{count} 个系列",
  "site.aiVisual": "AI 效果图",
  "site.emma.open": "和 EmmA 聊天",
  "site.emma.close": "关闭聊天",
  "site.emma.role": "Artemis Flooring 的地板顾问",
  "site.emma.greeting": "你好，我是 EmmA。地板的事都可以问我：产品、颜色、安装流程或预约上门测量。不用一次全懂，我们慢慢一起选。",
  "site.emma.placeholder": "输入消息",
  "site.emma.send": "发送",
  "site.emma.typing": "EmmA 正在输入",
  "site.emma.ai": "EmmA 是 AI 助手。确认价格以上门测量为准。",
  "site.emma.whatsapp": "在 WhatsApp 继续",
  "site.emma.whatsappText": "你好 EmmA，我在 Artemis Flooring 网站上，有个问题。",
  "site.emma.error": "我暂时无法回复。请在 WhatsApp 给我留言，或致电 {phone}。",
  "site.emma.busy": "短时间内消息有点多。请稍等，或在 WhatsApp 继续。",
  "site.emma.tooLong": "这条消息有点长，可以精简一下吗？",
  "site.emma.whatsappCta": "在 WhatsApp 给 EmmA 留言",
  "site.edit.collectionOne": "1 个系列",
  "site.edit.note": "空间图片均由各品牌提供。",
  "site.edit.all": "查看全部地板",
  "site.cat.carpet": "地毯",
  "site.cat.carpet-tile": "方块地毯",
  "site.cat.engineered-timber": "实木复合地板",
  "site.cat.hybrid": "复合防水地板",
  "site.cat.laminate": "强化地板",
  "site.cat.solid-timber": "实木地板",
  "site.cat.vinyl-plank": "乙烯基地板",
  "site.cat.vinyl-tile": "乙烯基地砖",
  "site.cat.vinyl-sheet": "卷材地板",
  "site.work.eyebrow": "真实地板，Artemis 安装实景",
  "site.work.title": "从一个想法，到家的日常。",
  "site.work.copy": "我们完成的地板安装现场照片，并标注所用产品。",
  "site.work.oak": "实木复合地板",
  "site.work.carpet": "地毯",
  "site.work.laminate": "强化地板",
  "site.work.stairs": "强化地板楼梯",
  "site.reviews.title": "听听客户怎么说。",
  "site.reviews.copy": "以下为 Google 公开评价的英文原文节选。可在 Google 查看完整评价。",
  "site.reviews.read": "阅读 Google 客户评价",
  "site.quote.eyebrow": "规划您的地板",
  "site.quote.title1": "让美好构想",
  "site.quote.title2": "遇见合适预算。",
  "site.quote.copy": "美好的地板始于清晰的计划。把您的房间、喜欢的风格与预算放在一起考虑。",
  "site.quote.step1": "告诉我们您的房间，选择地板。",
  "site.quote.step2": "马上看到参考价格。",
  "site.quote.step3": "预约免费测量，确认施工范围与报价。",
  "site.quote.cta": "从我的房间开始",
  "site.quote.example": "示例",
  "site.quote.cardTitle": "您的家，渐渐成形。",
  "site.quote.cardSub": "先了解房间与地板，再预约测量。",
  "site.quote.living": "客厅",
  "site.quote.dining": "餐厅",
  "site.quote.hall": "走廊",
  "site.quote.rowFloor": "您的地板",
  "site.quote.rowRooms": "您的房间",
  "site.quote.rowRoomsValue": "3 个房间 · 60 m²",
  "site.quote.rowNext": "下一步",
  "site.quote.rowNextValue": "免费上门测量",
  "site.quote.open": "走进“我的家”",
  "site.closing.eyebrow": "从最初的想法，到踏出第一步",
  "site.closing.title": "让家，更像您的家。",
  "site.footer.tagline": "为悉尼家庭用心挑选地板：先测量，再决定，用心铺装。",
  "site.footer.residential": "家居",
  "site.footer.commercial": "商业空间",
  "site.footer.help": "帮助",
  "site.footer.showroom": "展厅",
  "site.footer.hours": "周一至周五 · 上午 9:00 – 下午 5:00",
  "site.footer.tollFree": "免费电话",
  "site.footer.herringbone": "人字拼与鱼骨拼",
  "site.footer.place": "新南威尔士州悉尼",
  "site.jobPage.preview": "只读预览。此视图不会记录客户访问，颜色选择和聊天已禁用。",
  "site.footer.privacy": "隐私与网站存储",
  "site.analytics.title": "帮助我们改善您的选购体验",
  "site.analytics.copy": "是否允许使用 Google Analytics Cookie 了解公开页面的浏览情况？您可自由选择。广告追踪保持关闭，不采集客户私密页面。",
  "site.analytics.details": "隐私说明",
  "site.analytics.accept": "接受分析",
  "site.analytics.reject": "拒绝分析",
  "site.analytics.close": "关闭",
  "site.analytics.settings": "分析偏好",
  "site.analytics.accepted": "当前已接受分析，您可以在下方撤回。",
  "site.analytics.rejected": "当前已拒绝分析。",
  "site.analytics.privacyTitle": "可选的 Google Analytics",
  "site.analytics.privacyCopy": "只有您同意后才加载 Google Analytics。它使用第一方 Cookie 衡量产品目录、Price my floor 和咨询等公开页面的访问及活动。Google 会收到 IP 地址和浏览器等技术信息，并可能在澳大利亚境外处理。我们仅发送公开页面路径与来源网站，不发送网址查询或片段、表单内容、聊天消息、房间信息或客户私密链接。广告个性化、Google signals 和自动表单追踪均关闭。您的选择最多保存 180 天，分析 Cookie 在同一期限内到期。您可通过页脚的“分析偏好”更改选择。撤回后会停止后续采集、清除我们的分析 Cookie 并重新加载页面；这不会删除 Google 已收到的数据。独立的匿名页面统计不受该选择影响。",
  "site.analytics.googlePrivacy": "Google 如何使用合作网站的信息",
  "site.privacy.title": "隐私与网站存储",
  "site.privacy.description": "Artemis Flooring 如何处理网站咨询、聊天、匿名浏览统计及浏览器中保存的信息。",
  "site.privacy.updated": "更新于 2026 年 11 月 1 日",
  "site.privacy.intro": "本说明介绍您浏览网站、估算地板价格、联系我们、预约测量或与 EmmA 交流时，信息的处理方式。",
  "site.privacy.providedTitle": "您主动提交的信息",
  "site.privacy.provided": "咨询及预约表单会将您填写的信息发送给 Artemis Flooring，以便我们回复、处理地板咨询并安排您要求的服务。这可能包括姓名、联系方式、现场地址、备注和参考估价。已确认的测量预约会记录在预约系统和日历中。",
  "site.privacy.chat": "您发送给 EmmA 的消息会由我们的助理服务及其 AI 提供商处理，以生成回复。对话记录和使用信息会保存在我们的系统中。请仅提供与地板咨询相关的信息。",
  "site.privacy.counterTitle": "匿名网站统计",
  "site.privacy.counter": "我们统计页面浏览量和少量网站操作，以了解网站哪些部分有用。统计器记录页面路径、来源网站主机名、大致设备类别及有限的数字或是／否信息。它不保存 Cookie、访客标识、IP 地址、完整来源网址、咨询文本或聊天消息。这些限制仅适用于统计器；表单、聊天和技术服务日志另行处理。",
  "site.privacy.storageTitle": "浏览器记住的信息",
  "site.privacy.storageIntro": "Cookie 和浏览器存储是网站记住信息的不同方式。我们的页面统计器不使用这两种方式。网站其他功能会出于以下目的使用会话存储或本地存储。",
  "site.privacy.estimateTitle": "估价与预约恢复",
  "site.privacy.estimate": "您的房间信息、地板选择和估价可从 Price my floor 带入预约表单。未完成的预约请求可能保留在标签页中，以便重试时恢复同一请求。浏览器中的副本并不代表预约已确认。",
  "site.privacy.chatTitle": "保持聊天连续性",
  "site.privacy.chatStorage": "浏览器标签页会保存聊天会话标识、最近消息和聊天面板状态，以便您浏览网站时继续对话。",
  "site.privacy.accessTitle": "客户私密访问",
  "site.privacy.access": "如果您使用客户私密链接或登录，浏览器存储可能保存访问及返回页面所需的信息。部分较早的预约摘要也可能留在设备上。请妥善保管私密链接，并在使用共享设备后退出登录。",
  "site.privacy.attributionTitle": "咨询来源",
  "site.privacy.attribution": "着陆页网址中的推广标签和广告点击标识（例如 utm、gclid 或 fbclid 值）可能保存在会话存储中，并在您提交咨询或预约时一并发送，以帮助我们了解咨询来源。这些信息与匿名页面统计器分开处理。",
  "site.privacy.providersTitle": "支持网站运行的服务",
  "site.privacy.providers": "我们使用服务提供商来支持托管、数据存储、登录、日历预约、地址建议和 AI 辅助回复。技术请求可能包含提供和保护这些服务所需的 IP 地址、设备或请求信息。根据提供商的情况，信息可能在澳大利亚境外处理。网站还会加载 Google Fonts 字体。",
  "site.privacy.external": "如果您点击 WhatsApp 链接或打开外部支付或其他服务，该提供商的隐私处理方式也将适用。",
  "site.privacy.choicesTitle": "您的选择与已保存记录",
  "site.privacy.choices": "您可以在浏览器设置中清除或阻止网站存储。会话存储通常保留至标签页或浏览器会话结束，但浏览器可能将其恢复。本地存储可能持续保留，直至被清除。阻止或清除存储可能删除未完成的估价、聊天记录或登录状态，并影响恢复功能，但不会删除已经发送给我们的咨询、预约或对话。",
  "site.privacy.contactTitle": "隐私问题或请求",
  "site.privacy.contact": "如需了解我们持有的信息、申请访问或更正、询问删除事宜或提出隐私问题，请联系我们。我们会结合记录保存义务考虑您的请求。清除浏览器数据与这些请求不同。",
  "site.privacy.contactLink": "联系 Artemis Flooring",
  "site.footer.disclaimer": "网站价格仅供参考；以 Artemis Flooring 的正式报价为准。",
  "site.footer.example": "示例目录 · 未连接实时数据",
  "measure.title": "免费上门测量",
  "measure.lede": "Jacky 日历上的空闲时间，点一下即可预约。",
  "measure.loading": "正在读取 Jacky 的日历…",
  "measure.failed": "无法读取 Jacky 的日历，因此不显示任何空闲时间。未做任何预约。",
  "measure.none": "未来两周没有空闲时间。",
  "measure.near": "附近",
  "measure.nearHint": "附近：当天附近已有另一个测量预约，可以顺路。",
  "measure.booking": "正在预约…",
  "measure.booked": "已预约测量：{label}",
  "measure.held": "日历写入暂缓：预约操作功能已关闭，此预约已记录在 AIW 中，但尚未写入 Jacky 的日历。",
  "measure.heldOther": "尚未写入日历——{note}",
  "measure.queued": "已加入 Jacky 的日历队列，正在等待保存确认。",
  "measure.move": "如需改期，请点击下方其他时间。",
  "measure.cancel": "取消预约",
  "measure.cancelled": "测量预约已取消。",
  "measure.copyLink": "复制预约链接",
  "measure.linkCopied": "已复制预约链接，有效期至 {date}。请粘贴到您的消息中。",
  "measure.linkShown": "预约链接如下，有效期至 {date}。请立即复制，它只显示一次。",
  "measure.needsApproval": "等待批准——尚未做任何更改。",
  "measure.more": "显示更多时间",
  // 最新的在前，然后“显示更多”——共享列表分页（2026年10月13日）。
  "list.showing": "显示 {total} 条中的 {shown} 条，最新的在前",
  "list.showMore": "显示更多（还剩 {remaining} 条）",
  "list.loadingMore": "正在加载更多…",
  "list.moreFailed": "下一页无法加载。当前显示的只是最新的部分。",
  "list.retry": "重试",
  "list.stageShowing": "此阶段已显示 {total} 条中的 {shown} 条",
  "list.onlyStage": "只看此阶段",
  "list.proposalUntitled": "报价书",
  "list.overdueMore": "还有 {count} 条逾期——打开跟进",
  "communications.truncated": "这些对话不含更早的记录：{sources} 超过此处读取的最新 {limit} 条。",
  "communications.source.drafts": "已保存的草稿",
  "communications.source.touches": "已记录的活动",
  "communications.source.supplier_evidence": "供应商证据",
  "communications.source.installer_requests": "安装团队邀请",
  "inv.truncated": "只读取了前 {limit} 条库存。搜索和筛选只覆盖这些条目。",
  // New work for a customer already on file (14 Oct 2026).
  "newOpp.button": "新建商机 / 报价",
  "newOpp.forPerson": "新工地",
  "newOpp.title": "{name} 的新工程",
  "newOpp.lede": "为已有客户新增工地。客户资料直接关联，无需重新录入；商机打开后即可准备报价。",
  "newOpp.siteAddress": "工地地址",
  "newOpp.suburb": "区域",
  "newOpp.postcode": "邮编",
  "newOpp.category": "类别",
  "newOpp.categoryUnsure": "暂未确定",
  "newOpp.source": "工程来源",
  "newOpp.source.trade_network": "现有行业合作",
  "newOpp.source.referral": "推荐",
  "newOpp.source.phone": "电话",
  "newOpp.source.email": "电邮",
  "newOpp.source.website": "网站",
  "newOpp.source.other": "其他",
  "newOpp.brief": "简述",
  "newOpp.briefHint": "例如：3 个单元，全屋复合地板，12 月交房",
  "newOpp.submit": "打开商机",
  "newOpp.cancel": "取消",
  "newOpp.done": "商机已打开",
  "newOpp.pending": "等待批准——尚未做任何更改。",
  "newOpp.failed": "未能打开：{reason}",
  // EmmA on the Proposal page and the customer's language (14 Oct 2026).
  "custLang.label": "EmmA 与该客户沟通使用",
  "custLang.hint": "EmmA 在客户的报价页和工程页上使用此语言。页面本身仍为英文。",
  "custLang.saved": "已保存。EmmA 将从客户的下一条消息开始使用此语言。",
  "custLang.pending": "等待批准——尚未做任何更改。",
  "custLang.failed": "未保存：{reason}",
  "ppChat.title": "报价页上的对话",
  "ppChat.empty": "客户尚未在报价页上留言。",
  "ppChat.failed": "无法加载对话。",
  "ppChat.handedOff": "EmmA 已转交办公室：",
  "ppChat.customer": "客户",
  // Sharing a Proposal: addressee, undecided colour, how to accept (report 89bffef3, 16 Oct 2026).
  "customer.doc.site": "施工地址",
  "customer.doc.toGoAhead": "如需确认，请在报价页面点击“接受”，或致电 Jacky：0432 399 295。",
  "customer.print.colourNotChosen": "颜色：尚未选定",
  "customer.print.chooseColour": "选择颜色 — {collection} 系列",
  "customer.print.chooseColourHint": "您尚未选定颜色。请在报价页面选择并点击“确认”，或回复我们的邮件并写明下方的颜色名称。点击颜色可在我们的网站上查看。",
  "customer.print.seeOnline": "{code} · 在线查看",
  "share.attachPdf": "附上报价 PDF",
  "share.attachPdfHint": "与链接内容相同的文件，方便客户保存。",
  "share.pdfPreparing": "正在生成 PDF…",
  "share.pdfFailed": "无法生成报价 PDF：{error}",
  "share.pdfReady": "{name} · {size}",
  "share.pdfRetry": "重试",
  // Choose and confirm a classic Proposal's colour (report 89bffef3, 16 Oct 2026).
  "customer.colour.section": "选择颜色",
  "customer.colour.title": "选择颜色 — {product}",
  "customer.colour.hint": "您尚未选定颜色。点击颜色可放大查看，然后确认。",
  "customer.colour.chosen": "您的颜色：{colour}。接受报价之前可随时更改。",
  "customer.colour.yourChoice": "您的选择",
  "customer.colour.confirm": "确认 {colour}",
  "customer.colour.saving": "正在保存…",
  "customer.colour.confirmed": "谢谢——{product} 已确认为 {colour}。",
  "customer.colour.priceToConfirm": "谢谢——已记录 {colour}。该颜色价格不同，Artemis 将确认价格并发送更新后的报价。",
  // The Proposal's strata scope pack, in the Proposal's format (report 89bffef3, 16 Oct 2026).
  "strata.incomplete": "物业管理提交文件包不完整。准备邮件前，请检查报价、隔音报告及保险文件。",
  "strata.fixed.builtIn": "地板通过出厂自带的隔音背层浮铺。背层已包含在标示的地板厚度内，不是额外铺设的垫层。",
  "strata.c.quotation": "报价 — 已发布的报价单（随附原件）",
  "strata.c.common": "公共区域 — 通行、保护、粉尘控制和清洁",
  "strata.common.title": "3. 共有物业公共区域的工作",
  "strata.common.intro": "地板更换仅限于 {rooms}。公共区域只用于约定的通行、送货和废料移出，并按以下安排进行保护和清洁。不包括更改共有物业的饰面。",
  "strata.common.hours.label": "楼宇规定和施工时间",
  "strata.common.hours": "进场前取得书面楼宇规定。遵守业主委员会批准的施工、噪音、送货和废料收集时间；排期前与楼宇经理确认通知、入场要求和预约。",
  "strata.common.protection.label": "保护共用通道",
  "strata.common.protection": "施工前记录约定卸货区域和通行路线的状况。保护使用到的共用地面、墙角、门框和电梯内壁。保持入口、防火门、出口及住户通道畅通，并在施工期间检查保护措施。",
  "strata.common.movement.label": "送货及材料搬运",
  "strata.common.movement": "与楼宇经理约定卸货、停车及电梯或楼梯路线。确认电梯预约、尺寸及载重限制。用有保护的手推车或受控人力搬运地板和工具；材料分批放在单元内，不占用公共区域储存。",
  "strata.common.dust.label": "粉尘及切割控制",
  "strata.common.dust": "在约定的封闭切割区域作业，使用工具集尘和适合该作业的商用 HEPA 吸尘设备。使用前检查软管、滤芯及集尘袋。不得干扫或用压缩空气吹尘。围护失效时停止施工；发现疑似有害材料时，须另行评估并采取适当控制措施后方可恢复施工。",
  "strata.common.clean.label": "每日公共区域清洁",
  "strata.common.clean": "材料和废料搬运后及每日收工时，检查并吸尘清洁已保护的通道。清除散落碎屑、包装和施工粉尘，并用适合的清洁产品处理污迹。共用路线须保持畅通、干燥，让住户安全使用。",
  "strata.common.waste.label": "所有施工废料移出现场",
  "strata.common.waste": "拆除的地毯和地垫、边角料、包装及集尘废物须装入封闭袋或有盖容器，经保护路线移出并送往适当的场外设施。不得使用住户垃圾桶或垃圾槽。任何废料箱或临时收集点均须楼宇批准。",
  "strata.common.handover.label": "完工检查及交接",
  "strata.common.handover": "Artemis 现场负责人检查施工区域和共用通道，小心移除临时保护，完成最后吸尘清洁并拍照记录状况。任何损坏须及时报告，并与业主或楼宇经理商定修复安排。",
  "strata.att.quotation": "报价 — 随附当前已发布报价单的完整原件，包括范围、数量、条款和未含 GST 的金额。",
  "strata.title": "物业施工范围文件",
  "strata.byline": "Artemis Flooring · 悉尼 · 报价 {ref} · {date}",
  "strata.bylineNoRef": "Artemis Flooring · 悉尼 · {date}",
  "strata.banner": "致业主委员会：本文件包包括报价、拟施工区域、地板固定方式、公共区域保护和清洁、隔音报告及我们的保险。",
  "strata.contents": "文件内容",
  "strata.c.scope": "施工范围 — 区域、包含与不包含的内容",
  "strata.c.fixed.floating": "地板与地基的固定方式 — 悬浮安装",
  "strata.c.fixed.glued": "地板与地基的固定方式 — 胶粘安装",
  "strata.c.acoustic": "隔音报告 — {title}（附原件）",
  "strata.c.certificate": "{title}（附原件）",
  "strata.scope": "1. 施工范围",
  "strata.scope.roomsFromQuote": "随附已发布报价列出的区域",
  "strata.scope.sentence": "供应并安装 {product}，{method}{underlay}，铺于{subfloor}之上，区域：{rooms}。",
  "strata.scope.floating": "悬浮安装",
  "strata.scope.glued": "胶粘安装",
  "strata.scope.onUnderlay": "，铺设 {underlay}",
  "strata.scope.slab": "现有地基",
  "strata.scope.outOne": "{rooms}不在施工范围内。",
  "strata.scope.outMany": "{rooms}均不在施工范围内。",
  "strata.included": "包含的内容",
  "strata.col.item": "项目",
  "strata.col.quantity": "数量",
  "strata.col.covers": "施工内容",
  "strata.notIncluded": "不包含",
  "strata.fixed.floating": "2. 地板与地基的固定方式 — 悬浮安装",
  "strata.fixed.glued": "2. 地板与地基的固定方式 — 胶粘安装",
  "strata.fixed.floatingIntro": "地板相互扣合，悬浮铺设于指定地垫之上。地板不用胶、钉或螺丝固定于基层；已包含的基层处理及收边固定属于报价中的独立施工内容。",
  "strata.fixed.gluedIntro": "地板用厂家指定的胶全面粘接在处理好的地基上，不在楼板上钉钉或打螺丝。",
  "strata.fixed.floating1": "地垫：报价中的悬浮地板区域采用 {underlay}。铺板前确认混凝土所需的防潮处理。",
  "strata.fixed.floating2": "边缘：在墙面、门框及门槛处保留厂家要求的伸缩缝。收边条固定于墙面或踢脚线，不得压死悬浮地板；现场确认过渡条的固定方法。",
  "strata.fixed.glued1": "地基：涂胶前须清洁、平整、干燥；混凝土地基须做湿度检测。",
  "strata.fixed.glued2": "边缘：在墙、门框和门槛处用收边条或只固定在墙上的踢脚线条收口。",
  "strata.fixed.buildUp": "总厚度：比楼板高约 {mm} 毫米；安装前检查门下间隙。",
  "strata.fig.wall": "墙",
  "strata.fig.slab": "混凝土楼板",
  "strata.fig.adhesive": "满涂地板胶",
  "strata.fig.scotia": "踢脚线条只固定在墙上",
  "strata.fig.gap": "每面墙和门框处留 10 毫米伸缩缝",
  "strata.fig.note.floating": "剖面示意，非按比例。地板悬浮铺设；基层处理与收边固定另行说明。",
  "strata.fig.note.glued": "剖面示意，非按比例。用胶粘接；不在楼板上钉钉或打螺丝。",
  "strata.attached": "4. 报价及证明文件",
  "strata.att.acoustic": "隔音报告 — {title}。报告测试的是该地板配该地垫、在报告所述楼板和天花条件下的效果；是否符合本楼的标准，由业主委员会确认。",
  "strata.att.certificate": "{title} — {note}{valid}。",
  "strata.att.validTo": "，有效期至 {date}",
  "strata.notOnFile": "尚未存档：{items}",
  // The strata scope pack on the Proposal email (report 89bffef3, 16 Oct 2026).
  "share.attachStrata": "附上物业施工范围文件",
  "share.attachStrataHint": "供业主委员会审核：报价、施工范围、固定方式、公共区域保护和清洁、隔音报告及两份保险证明。",
  // The supplier hub (Supply → Suppliers & stock, 16 Oct 2026).
  "sup.kicker": "供应",
  "sup.title": "供应商与库存",
  "sup.lede": "所有供应商集中一处：订单、关联的工单、条款、价目表、所供库存及表现。",
  "sup.watchTitle": "需要沟通的供应商 {count} 家：",
  "sup.search": "搜索供应商或邮箱",
  "sup.all": "全部",
  "sup.toOrders": "订单",
  "sup.toInventory": "库存",
  "sup.unavailable": "无法读取供应商：{message}",
  "sup.listTitle": "供应商",
  "sup.listMeta": "近 {days} 天的订单与支出",
  "sup.col.supplier": "供应商",
  "sup.col.category": "类别",
  "sup.col.open": "未完成",
  "sup.col.orders": "订单数",
  "sup.col.spend": "支出",
  "sup.col.confirmed": "已确认",
  "sup.col.onTime": "准时",
  "sup.col.pricelist": "价目表",
  "sup.col.stock": "库存",
  "sup.col.last": "最近订单",
  "sup.col.order": "订单",
  "sup.col.job": "工单",
  "sup.col.status": "状态",
  "sup.col.sent": "发出",
  "sup.col.promised": "应到 / 已到",
  "sup.col.total": "金额",
  "sup.col.stage": "阶段",
  "sup.col.effective": "生效日期",
  "sup.col.document": "文件",
  "sup.col.kind": "类型",
  "sup.col.received": "收到",
  "sup.exGstHint": "不含 GST",
  "sup.confirmedHint": "供应商已确认的订单：回执、提货号、发票、备货完成或已提货。",
  "sup.onTimeHint": "在承诺日期当天或之前送达或提货，仅计入两个日期都有记录的订单。",
  "sup.none": "没有匹配的供应商。",
  "sup.footnote": "没有记录支撑的比率显示为 —，而不是 0 %。在订单页登记收货后，准时率会逐步出现。",
  "sup.watch": "关注",
  "sup.loading": "正在读取供应商…",
  "sup.back": "全部供应商",
  "sup.creditTerms": "有账期",
  "sup.noCreditTerms": "按单付款",
  "sup.worksInChinese": "使用中文沟通",
  "sup.watchReason": "在足够的已记录交付中准时率低于 {min} %——值得沟通一下。",
  "sup.stat.open": "未完成订单",
  "sup.stat.spend": "{days} 天支出",
  "sup.stat.confirmed": "已确认",
  "sup.stat.onTime": "准时",
  "sup.stat.lead": "平均交货期",
  "sup.stat.ofOrders": "共 {count} 单",
  "sup.stat.ofDelivered": "已交付 {count} 单",
  "sup.stat.noDeliveries": "尚无交付记录",
  "sup.days": "{count} 天",
  "sup.contact": "联系方式",
  "sup.contactFootnote": "订单发往订货邮箱并抄送销售；财务邮箱不接收订单。",
  "sup.terms": "订货条款",
  "sup.termsDefault": "默认条款——此供应商未单独设置",
  "sup.f.ordering": "订货",
  "sup.f.sales": "销售",
  "sup.f.accounts": "财务",
  "sup.f.phone": "电话",
  "sup.f.warehouse": "仓库",
  "sup.f.notes": "备注",
  "sup.f.lead": "交货期",
  "sup.f.cutOff": "截单时间",
  "sup.f.minOrder": "最低订货额",
  "sup.f.delivery": "交付方式",
  "sup.f.waste": "损耗预留",
  "sup.pickup": "我方提货",
  "sup.delivery": "对方送货",
  "sup.tab.orders": "订单",
  "sup.tab.jobs": "工单",
  "sup.tab.stock": "库存",
  "sup.tab.pricelists": "价目表",
  "sup.stockOrder": "备库存",
  "sup.noOrders": "尚未向该供应商下单。",
  "sup.noJobs": "尚无工单向该供应商订货。",
  "sup.noStock": "库存中没有该供应商的货品。",
  "sup.noPricelists": "该供应商没有存档的价目表。",
  "sup.pricelistPdf": "价目表 PDF",
  "sup.priceRevision": "价格修订",
  // 供应商价目表与配件配色（2026 年 10 月 23 日）。
  "sup.tab.accessories": "配件",
  "sup.pl.searchTitle": "搜索所有价目表",
  "sup.pl.searchMeta": "各供应商当前价目表 · 编码、颜色、系列 · 我方进价，不含 GST",
  "sup.pl.searchPlaceholder": "编码、颜色、系列或尺寸——如 spotted gum 1900",
  "sup.pl.more": "匹配的行更多——再加一个词缩小范围。",
  "sup.pl.col.code": "编码",
  "sup.pl.col.item": "系列 · 颜色",
  "sup.pl.col.size": "尺寸 · 包装",
  "sup.pl.col.cost": "我方进价",
  "sup.pl.priceOnApplication": "价格需询价",
  "sup.pl.col.page": "页码",
  "sup.pl.col.weSell": "我们在售",
  "sup.pl.kind.floor": "地板",
  "sup.pl.kind.accessory": "配件",
  "sup.pl.kind.missing": "不在我们的产品目录中",
  "sup.pl.basis.cod": "货到付款价",
  "sup.pl.basis.partner": "合作伙伴价",
  "sup.pl.basis.promotion": "促销",
  "sup.pl.yes": "是",
  "sup.pl.no": "否",
  "sup.pl.noMatch": "没有匹配的行。",
  "sup.pl.none": "{name} 尚未录入价目表。",
  "sup.pl.askLoad": "供应商 {name}（id {id}）尚未录入价目表。请在邮件或 OneDrive 中找到其最新价目表，逐行读取，并用 record_supplier_price_list 录入。要做的事：",
  "sup.pl.askLoadButton": "请 EmmA 录入最新价目表",
  "sup.pl.current": "当前价目表 · {day} 起生效",
  "sup.pl.summary": "共 {lines} 行 · 地板 {floors} · 配件 {accessories} · 目录中已有 {sold}",
  "sup.pl.listPlaceholder": "在此价目表中搜索",
  "sup.pl.collection": "系列",
  "sup.pl.allCollections": "全部系列",
  "sup.pl.shown": "显示 {count} 行",
  "sup.pl.footnote": "按供应商原样记录。价目表不会改变我们的目录进价：低于价目表的协议价保持不变。",
  "sup.pl.history": "价目表历史",
  // Carpet supplier B2B portals — supplier stock and the morning read (26 Oct 2026)
  "sup.tab.supplierStock": "供应商库存",
  "sup.stock.readAt": "读取自 {portal}，时间 {at}",
  "sup.stock.pricesChanged": "价格较上次读取有变化",
  "sup.stock.pricesSame": "价格未变",
  "sup.stock.costsApplied": "目录成本已于 {at} 更新",
  "sup.stock.costsNotApplied": "本次读取未更改目录成本",
  "sup.stock.report": "门户价格与目录成本对比",
  "sup.stock.col.product": "产品",
  "sup.stock.col.portalLm": "门户价 / 延米",
  "sup.stock.col.portalM2": "门户价 / 平方米",
  "sup.stock.col.catalogueM2": "目录成本 / 平方米",
  "sup.stock.col.change": "结果",
  "sup.stock.change.same": "一致",
  "sup.stock.change.up": "门户更高",
  "sup.stock.change.down": "门户更低",
  "sup.stock.change.mixed": "不一",
  "sup.stock.change.no_portal_price": "门户无此产品",
  "sup.stock.change.agreed": "约定价——保留",
  "sup.stock.change.applied": "已按门户更新",
  "sup.stock.search": "搜索系列、颜色或编号",
  "sup.stock.shown": "显示 {count} 项 · 门户共 {colours} 个颜色 · {rolls} 卷",
  "sup.stock.col.range": "系列",
  "sup.stock.col.colour": "颜色",
  "sup.stock.col.code": "编号",
  "sup.stock.col.metres": "米数",
  "sup.stock.col.rolls": "卷数",
  "sup.stock.col.largest": "最大卷",
  "sup.stock.col.dyeLots": "染缸批次",
  "sup.stock.col.next": "下次生产",
  "sup.stock.col.roll": "卷号",
  "sup.stock.col.dyeLot": "染缸号",
  "sup.stock.col.location": "地点",
  "sup.stock.col.price": "单独价格 / 延米",
  "sup.stock.notInCatalogue": "不在我们的目录中",
  "sup.stock.empty": "没有匹配的颜色。",
  "sup.stock.footnote": "供应商自己的库存，即早间读取时其门户所显示的数据——不是我们的仓库（见“库存”标签）。价格不含 GST。",
  "sup.stock.line": "供应商库存：{lm} 米 · {rolls} 卷 · 最大 {largest} 米 · {lots} 个染缸批次 · 截至 {at}",
  "sup.stock.none": "供应商库存：无现货 · 截至 {at}",
  "sup.stock.noneNext": "供应商库存：无现货 · 下次生产 {next} · 截至 {at}",
  "sup.acc.counts": "{colours} 个地板颜色中有 {complete} 个已配好收边线和铝收边条",
  "sup.acc.gaps": "{count} 个待补",
  "sup.acc.search": "产品或颜色",
  "sup.acc.gapsOnly": "只看缺失",
  "sup.acc.askFill": "供应商 {name}（id {id}）有 {count} 个硬质地板颜色缺少收边线或铝收边条配色。请用 accessory_matches（gaps_only）读取，依据该供应商的价目表和配色表逐一配色，并用 save_accessory_match 保存。要做的事：",
  "sup.acc.askFillButton": "请 EmmA 补齐缺失",
  "sup.acc.scotia": "收边线",
  "sup.acc.trim": "铝收边条",
  "sup.acc.product": "产品",
  "sup.acc.floor": "地板颜色",
  "sup.acc.matchColour": "配色",
  "sup.acc.spec": "型材 / 规格",
  "sup.acc.gap": "无配色",
  "sup.acc.fill": "补上",
  "sup.acc.useSupplier": "用供应商的",
  "sup.acc.retired": "已改回供应商的配色",
  "sup.acc.supplierSays": "供应商配色表：{colour}",
  "sup.acc.src.emma": "EmmA 配色",
  "sup.acc.src.staff": "我方",
  "sup.acc.src.supplier": "供应商",
  "sup.acc.none": "该供应商没有硬质地板颜色。",
  "sup.acc.noGaps": "没有缺失：每个颜色都有收边线和铝收边条配色。",
  "sup.acc.stateCounts": "已确认 {confirmed} · 旧版 {legacy} · 冲突 {conflict} · 缺失 {missing}",
  "sup.acc.state.confirmed": "已确认",
  "sup.acc.state.legacy": "旧版——待确认",
  "sup.acc.state.conflict": "冲突",
  "sup.acc.state.missing": "缺失",
  "sup.acc.accessoryProduct": "配件产品",
  "sup.acc.chooseVariant": "选择精确规格",
  "sup.acc.evidence": "供应商价目表依据",
  "sup.acc.noEvidence": "无关联价目行",
  "sup.acc.footnote": "已确认的配色使用地板和配件目录中的精确身份。旧版供应商文字仅作为依据显示，绝不会用于选择采购项。导入价目表不会覆盖我方配色。",
  // Supplier hub: people, WeChat chats and the message timeline (16 Oct 2026).
  "sup.askEmma": "问 EmmA",
  "sup.askEmmaPrompt": "供应商 {name}（id {id}）——get_supplier 可查其联系人、群聊和订单。要做的事：",
  "sup.people": "联系人",
  "sup.peopleMeta": "在该供应商对接的人",
  "sup.col.name": "姓名",
  "sup.col.role": "职责",
  "sup.f.email": "邮箱",
  "sup.f.wechat": "微信名",
  "sup.f.language": "语言",
  "sup.role.rep": "业务代表",
  "sup.role.ordering": "订货",
  "sup.role.accounts": "财务",
  "sup.role.warehouse": "仓库",
  "sup.role.owner": "负责人",
  "sup.role.other": "其他",
  "sup.edit": "编辑",
  "sup.archive": "归档",
  "sup.archiveWhy": "为什么归档 {name}？（如：已离职）",
  "sup.archived": "已归档",
  "sup.noPeople": "尚未记录联系人。",
  "sup.addPerson": "添加联系人",
  "sup.save": "保存",
  "sup.saved": "已保存",
  "sup.cancel": "取消",
  "sup.wechat": "微信群聊",
  "sup.wechatMeta": "消息显示在“消息”中",
  "sup.noWechat": "尚未关联微信群聊。",
  "sup.linkChat": "关联微信群聊",
  "sup.pickChat": "选择 EmmA 已见过的群聊",
  "sup.chatMessages": "{count} 条消息",
  "sup.link": "关联",
  "sup.linked": "已关联",
  "sup.unlink": "取消关联",
  "sup.unlinkConfirm": "将“{chat}”与该供应商取消关联？消息仍保留在微信中。",
  "sup.unlinked": "已取消关联",
  "sup.tab.messages": "消息",
  "sup.ch.email": "邮件",
  "sup.ch.wechat": "微信",
  "sup.ch.record": "订单记录",
  "sup.dir.in": "对方发来",
  "sup.dir.out": "我方发出",
  "sup.dir.note": "已记录",
  "sup.noMessages": "暂无内容。关联对方的微信群聊，或在订单办公室读取其邮件回复后会显示在这里。",
  "sup.messagesFootnote": "邮件回复来自订单办公室邮箱；微信来自 EmmA 的桥接。发给供应商的任何内容仍需人工批准。",
  // Receiving on an order (supplier hub PR 3, 16 Oct 2026).
  "po.receive.title": "登记收货",
  "po.receive.pending": "还有 {count} 行未到货",
  "po.receive.allIn": "已全部到货",
  "po.receive.closed": "已到货记录",
  "po.receive.received": "已到",
  "po.receive.now": "本次到货",
  "po.receive.nowFor": "本次到货：{item}",
  "po.receive.where": "存放位置",
  "po.receive.toJob": "直接用于工单 {job}",
  "po.receive.everything": "全部到货",
  "po.receive.on": "到货日期",
  "po.receive.note": "备注",
  "po.receive.notePlaceholder": "例如：6 卷，无损坏",
  "po.receive.over": "超过订购数量——请核对数量。",
  "po.receive.nothing": "填写本次到货数量，或点“全部到货”。",
  "po.receive.closes": "订购的货物将全部到齐——订单将关闭。",
  "po.receive.part": "部分到货——订单保持开启，等待其余货物。",
  "po.receive.record": "登记收货",
  "po.receive.history": "已登记 {count} 次收货",
  "po.receive.line": "第 {line} 行",
  // Supplier promotions (supplier hub PR 4, 16 Oct 2026).
  "sup.promo": "促销",
  "sup.promos": "促销",
  "sup.promosMeta": "该供应商给我们的优惠——仅作参考，不改价格",
  "sup.promoNow": "今日有效的供应商促销 {count} 项：",
  "sup.promo.until": "至 {day}",
  "sup.promo.title": "促销",
  "sup.promo.titlePlaceholder": "例如：复合橡木九折",
  "sup.promo.covers": "适用范围",
  "sup.promo.which": "具体",
  "sup.promo.offer": "优惠",
  "sup.promo.percent": "折扣（%）",
  "sup.promo.percentOff": "减 {value}%",
  "sup.promo.priceExGst": "价格（不含 GST）",
  "sup.promo.unit": "单位",
  "sup.promo.dates": "日期",
  "sup.promo.from": "开始",
  "sup.promo.to": "结束",
  "sup.promo.source": "来源",
  "sup.promo.sourceRef": "具体邮件、群聊、文件或联系人",
  "sup.promo.terms": "条件",
  "sup.promo.upcoming": "尚未开始",
  "sup.promo.none": "没有当前或即将开始的促销记录。",
  "sup.promo.past": "以往促销 {count} 项",
  "sup.promo.add": "登记促销",
  "sup.promo.end": "提前结束",
  "sup.promo.endWhy": "“{title}”为什么提前结束？（如：已撤回）",
  "sup.promo.ended": "已结束",
  "sup.promo.footnote": "促销不会改变任何价格。若要按促销成本报价或订货，请将其设为目录价格。",
  "sup.promo.onOrder": "该供应商当前优惠：",
  "sup.promo.see": "查看促销",
  "sup.offer.percent_off": "折扣",
  "sup.offer.price": "特价",
  "sup.offer.free_freight": "免运费",
  "sup.offer.bonus": "赠送货品",
  "sup.offer.other": "其他",
  "sup.scope.all": "全部产品",
  "sup.scope.category": "类别",
  "sup.scope.collection": "系列",
  "sup.scope.product": "产品",
  "sup.scope.variant": "颜色",
  "sup.src.pdf": "PDF / 宣传单",
  "sup.src.rep": "对方业务代表",
 // Files on every Opportunity and Job (18 Oct 2026): the Files panel.
 "files.title":"文件",
 "files.lede":"此工作的图纸、照片、供应商和客户文件。每个文件最大 100 MB。隐藏的文件不会被删除。",
 "files.carried":"在商机中添加的文件也显示在这里。",
 "files.types":"PDF、照片、视频、DWG/DXF 图纸、Word、Excel、CSV",
 "files.drop":"将文件拖到这里，或",
 "files.choose":"选择文件",
 "files.category":"类别",
 "files.cat.plans":"图纸",
 "files.cat.photos":"照片",
 "files.cat.supplier":"供应商",
 "files.cat.customer":"客户",
 "files.cat.other":"其他",
 "files.note":"备注（可选）",
 "files.notePlaceholder":"这是什么文件？",
 "files.tooBig":"{name} 超过 100 MB，未上传。",
 "files.empty":"暂无文件。",
 "files.loading":"正在加载文件…",
 "files.loadFailed":"无法加载文件。",
 "files.retry":"重试",
 "files.showHidden":"显示已隐藏",
 "files.hide":"隐藏",
 "files.restore":"恢复",
 "files.hidden":"已隐藏",
 "files.planTag":"平面图",
 "files.fromOpportunity":"来自商机",
 "files.unfinished":"上传未完成：请再次拖入同一文件",
 "files.open":"打开",
 "files.openLink":"打开 {name}",
 "files.openFailed":"无法打开该文件。",
 "files.editNote":"备注",
 "files.saveNote":"保存备注",
 "files.cancel":"取消",
 "files.by.you":"你",
 "files.by.staff":"员工",
 "files.hashing":"正在校验 {name}…",
 "files.reserving":"正在登记 {name}…",
 "files.uploading":"正在上传 {name}：{pct}%",
 "files.verifying":"正在确认 {name}…",
 "files.done":"{name} 已保存。",
 "files.already":"{name} 已在此保存过。",
 "files.pending":"{name} 正在等待批准，尚未上传。",
 "files.failed":"{name} 上传失败：{reason}",
 "files.saveFailed":"未保存：{reason}",
 // 地毯块 is provisional until Jacky confirms the term (18 Oct 2026).
 "canvas.carpetTileNote":"地毯块 · {area} m² — 按平方米计价，无需卷材裁切方案。",
 // Room choice at sell prices (18 Oct 2026, Minto).
 "customer.roomChoice.aria":"为每个区域选择地板",
 "customer.roomChoice.title":"按区域选择您的地板",
 "customer.roomChoice.intro":"均为含安装价格，不含 GST。已按我们的推荐预设；更改任一区域，合计即时更新。",
 "customer.roomChoice.total":"您的合计",
 "customer.roomChoice.reset":"恢复我们的推荐",
 "customer.roomChoice.groupSize":"{count} 个房间 · {area} 平方米",
 "customer.roomChoice.area":"面积 {area} m²",
 "customer.roomChoice.areaAmount":"{area} 平方米 · {amount}",
 "customer.roomChoice.pinned":"此处仅一种地板",
 "customer.roomChoice.recommended":"推荐",
 "customer.roomChoice.mixed":"混合",
 "customer.roomChoice.mixRooms":"逐个房间选择",
 "customer.roomChoice.hideRooms":"收起房间",
 "customer.roomChoice.otherRooms":"其他房间",
 "customer.roomChoice.fixedTitle":"无论如何选择均包含",
 "customer.roomChoice.planTitle":"您的平面图",
 "customer.roomChoice.stepTotal":"本步骤",
 "customer.roomChoice.budgetTitle":"仅在需要时，由您当天确认",
 "customer.roomChoice.budgetNote":"不计入价格。现已定价并商定；只有在实际使用时才收费，并由您当天确认。",
 "customer.roomChoice.grandTotal":"您的价格",
 "customer.roomChoice.pricedThen":"届时定价",
 "customer.doc.callJacky":"如需进行，请致电 Jacky：0432 399 295。我们会把地板工程安排进您的装修计划，按您的时间表进行。",
 "customer.roomChoice.colourTitle":"颜色",
 "customer.roomChoice.colourNone":"选择一种颜色，或稍后再定",
 "customer.roomChoice.details":"详细规格",
 "customer.roomChoice.cutting":"价格按每 m² 地板计。金额已含 {percent}% 裁切损耗（实测 {area} m²）；最终按实际用量结算。",
 "commercial.roomChoiceTitle":"按售价的房间选择（工作草稿）",
 "commercial.roomChoiceTotal":"推荐方案合计 {amount} + GST",
 "commercial.roomChoiceNote":"由 EmmA 保存（save_room_choice_offer）。「发布版本」会在同一私密链接发布此房间选择，取代编辑器中的明细；客户按区域选择，副本中不含任何成本信息。",
 "commercial.roomChoiceProducts":"产品",
 "commercial.roomChoiceAnyRoom":"任意房间",
 "commercial.roomChoiceRooms":"{count} 个房间",
 "commercial.roomChoiceCutting":"按 m² 另加 {percent}% 裁切损耗",
 "commercial.roomChoiceGroups":"区域",
 "commercial.roomChoicePinned":"固定为 {product}：{reason}",
 "commercial.roomChoiceFixed":"固定项目",
 "commercial.roomChoiceBudget":"预算，按实际用量计费",
 "commercial.roomChoiceProfile":"{count} 种颜色及规格显示在页面上",
 // Room choice vs CRM total before release (23 Oct 2026, Minto)
 "commercial.roomChoiceMismatch":"CRM 中此报价为 {crm}，但客户看到的房间选择为 {page}（不含 GST）。请先把缺少的工程作为固定项目加入房间选择，再发布。",
 // EmmA's private Proposal brief (18 Oct 2026)
 "proposal.emmaBrief.title":"EmmA 简报",
 "proposal.emmaBrief.blurb":"EmmA 在此客户的报价页面与客户交谈时须遵守的内容。仅限员工：客户永远看不到。",
 "proposal.emmaBrief.kind":"场地",
 "proposal.emmaBrief.residential":"住宅",
 "proposal.emmaBrief.commercial":"商业",
 "proposal.emmaBrief.conditions":"条件",
 "proposal.emmaBrief.conditionsHint":"EmmA 必须遵守的本项目条件",
 "proposal.emmaBrief.scenarios":"如果客户… / EmmA 回答…",
 "proposal.emmaBrief.if":"如果客户…",
 "proposal.emmaBrief.say":"EmmA 回答…",
 "proposal.emmaBrief.avoid":"不要提及",
 "proposal.emmaBrief.avoidHint":"EmmA 不应提起或承诺的事项",
 "proposal.emmaBrief.add":"添加",
 "proposal.emmaBrief.remove":"删除",
 "proposal.emmaBrief.private":"随草稿保存。不会出现在发布给客户的版本中；EmmA 也可按您的指示设置。",
  "proposal.showcase.from": "起价 {amount} + GST",
  "proposal.showcase.options": "您的地板方案",
  "proposal.showcase.product": "产品",
  "proposal.showcase.rate": "每平方米报价基准",
  "proposal.showcase.flooring": "地板金额",
  "proposal.showcase.upgrade": "升级差额",
  "proposal.showcase.total": "总额",
  "proposal.showcase.exGst": "所有金额均未含 GST。",
  "proposal.showcase.base": "基础方案",
  "proposal.showcase.view": "查看铺装效果",
  "proposal.showcase.choose": "选择此地板",
  "proposal.showcase.chosen": "已选地板",
  "proposal.showcase.whole": "各收费项目先向下取整至整澳元再合计。所有金额均未含 GST。",
  "proposal.showcase.included": "所有方案均包含",
  "proposal.showcase.scope": "范围及条件",
  "proposal.showcase.schedule": "建议施工流程",
  "proposal.showcase.proposed": "建议日期，须确认进场、库存、基层处理及具体安排。",
  "proposal.showcase.specs": "产品规格",
  "proposal.showcase.colours": "颜色参考",
  "proposal.showcase.digital": "屏幕颜色仅供参考，请以实物样品为准。色板不会改变平面图颜色。",
  "proposal.showcase.aiSwatches": "AI 生成示意色板，请以实物样品为准。",
  "proposal.showcase.hero": "AI 生成的房间与饰面示意图，并非客户实际房屋，也不代表精确板材尺寸。",
  "proposal.showcase.source": "厂商资料",
  "proposal.showcase.package": "已商定地板套餐",
  "proposal.showcase.details": "详细范围、报价及其他选项",
  "proposal.showcase.plan": "查看板材铺装布局",
  "proposal.showcase.planIntro": "中性橡木示意仅展示板材规格、方向与接缝排列，不模拟产品颜色，也不改变报价数量。",
  "proposal.showcase.estimated": "估算平面图 · 未现场测量",
  "proposal.showcase.measured": "已保存现场测量",
  "proposal.showcase.all": "所有报价房间",
  "proposal.showcase.zoom": "放大布局",
  "proposal.showcase.close": "关闭",
  "proposal.showcase.horizontal": "横向",
  "proposal.showcase.vertical": "纵向",
  "proposal.showcase.room": "房间",
  "proposal.showcase.area": "{area} 平方米",
  "proposal.showcase.rules": "相邻排接缝随机错开 ≥ {percent}% · 两端板长 ≥ {end} 毫米 · 两侧收边均衡",
  "proposal.showcase.irregular": "该房间须现场确认铺装方案后才能显示单块板材切割布局。",
  "proposal.showcase.herringbone": "人字拼 45° · 中线与{room}墙面取正并居中铺设 · 两侧对称收口",
  "proposal.showcase.oneRun": "范围内所有房间一次连续铺设，地板按实际尺寸绘制在您的平面图上；仅在有墙的位置显示线条。",
  "proposal.showcase.robe": "衣柜",
  "proposal.showcase.visual": "仅为视觉布局，并非安装或切割施工图。应遵循所选产品说明及已确认现场条件。",
  "proposal.showcase.faq": "地板常见问题",
  "proposal.showcase.method": "Artemis 的地板铺装方法",
  "proposal.showcase.unpriced": "价格待确认",
  "presentation.title": "提案展示",
  "presentation.intro": "产品展示沿用现有报价和客户选择。切换页签前请应用修改，再保存或预览提案。",
  "presentation.enable": "从已选产品创建",
  "presentation.apply": "应用展示修改",
  "presentation.disable": "使用标准展示",
  "presentation.rounding": "每项收费向下取整到整澳元",
  "presentation.heading": "标题",
  "presentation.copy": "说明",
  "presentation.basis": "尺寸依据",
  "presentation.inclusions": "包含项目 · 每行一项",
  "presentation.rules": "中性铺板示意规则",
  "presentation.stagger": "最小相邻接缝错位 (%)",
  "presentation.end": "最小端板长度 (毫米)",
  "presentation.balance": "平衡两侧裁板宽度",
  "presentation.name": "名称",
  "presentation.tier": "档次说明",
  "presentation.benefit": "升级带来的优势",
  "presentation.hero": "主图网址",
  "presentation.alt": "图片说明",
  "presentation.colour": "示意颜色",
  "presentation.illustrative": "AI 示意图",
  "presentation.source": "制造商资料网址",
  "presentation.image": "图片网址",
  "presentation.scheduleHint": "可选，仅适用于本次工程。此流程不会预约安装日历。",
  "presentation.date": "拟定日期",
  "presentation.newStage": "新阶段",
  "presentation.addStage": "添加阶段",
  "presentation.scheduleNote": "流程条件",
  "presentation.applied": "已应用至草稿。请保存或预览以检查提案。",
  "presentation.configureProducts": "请先为推荐产品报价；展示内容会跟随报价。",
  "presentation.configError": "无法加载展示默认配置。",
  "proposal.showcase.commonCharges": "共同收费项目 · 已计入上述总价",
  "presentation.measurementNote": "尺寸免责声明",
  "proposal.showcase.optionalWork": "现场可讨论的可选工程",
  "proposal.showcase.separatePrice": "不计入本提案总价。任何可选工程均须另行确认范围、单价并获同意后才能进行。",
  "presentation.addOption": "添加另行报价的工程选项",
  "presentation.newOption": "另行报价的工程选项",
  "presentation.informational": "资料介绍 — 不要求在线选择或接受",
  "proposal.showcase.information": "供了解和沟通使用，无需在线选择或接受。",
  // Installer scope sheet and the crew's WeChat group (24 Oct 2026).
  "scopeLink.title": "工作评估",
  "scopeLink.unavailable": "此链接不可用，请向 Artemis 办公室索取新链接。",
  "scopeLink.site": "工地",
  "scopeLink.work": "你的工作",
  "scopeLink.included": "含在地板单价内",
  "scopeLink.pleaseQuote": "请报价",
  "scopeLink.total": "总价",
  "scopeLink.totalExGst": "你的总额（不含GST）",
  "scopeLink.toQuote": "{count} 项待报价",
  "scopeLink.days": "预计约 {days} 天。",
  "scopeLink.available": "可开工日期",
  "scopeLink.availableHelp": "勾选你能来的每一天，可多选。",
  "scopeLink.note": "问题或备注",
  "scopeLink.decline": "不接",
  "scopeLink.accept": "接受并发送",
  "scopeLink.pickDay": "请至少勾选一天可开工日期。",
  "scopeLink.needPrice": "每个标有“请报价”的项目都需要填写价格。",
  "scopeLink.saved": "已发送给 Artemis 办公室。",
  "scopeLink.superseded": "办公室已发送此工作的新版本，请打开最新链接。",
  "scopeLink.approved": "办公室已确认此工作。如需更改请致电办公室。",
  "scopeLink.answeredAccept": "你已接受。办公室确认前你可以修改。",
  "scopeLink.answeredDecline": "你已拒绝。仍可在下方接受。",
  "scopeLink.exGst": "金额不含GST。",
  "scope.title": "安装工作范围",
  "scope.help": "来自最新保存的提案的工作内容，不含客户价格。勾选此安装队负责的工作，设置他们的单价或请他们报价，然后发送一条带私密链接的简短消息。",
  "scope.crew": "安装队",
  "scope.pickCrew": "选择安装队",
  "scope.loading": "加载中…",
  "scope.unavailable": "无法加载：{message}",
  "scope.noTasks": "此工单还没有可发送的已接受工作。",
  "scope.mode.book": "价目表",
  "scope.mode.manual": "手动",
  "scope.mode.quote": "报价",
  "scope.included": "含在地板单价内",
  "scope.ownCrew": "自有员工 · 无费用",
  "scope.fromBook": "来自其价目表",
  "scope.toQuote": "由安装队报价",
  "scope.notAssigned": "未分配",
  "scope.lumpSum": "费用",
  "scope.known": "已知金额（不含GST）",
  "scope.plusQuote": "{amount} + {count} 项待报价",
  "scope.language": "安装队语言",
  "scope.note": "给安装队的备注",
  "scope.noteLocal": "中文备注",
  "scope.labelLocal": "中文",
  "scope.days": "预计天数（可选）",
  "scope.translate": "由 EmmA 翻译",
  "scope.translating": "翻译中…",
  "scope.translateFailed": "EmmA 暂时无法翻译，请手动输入中文。",
  "scope.sendBy": "发送方式",
  "scope.sms": "短信",
  "scope.wechat": "EmmA 微信 · {group}",
  "scope.wechatHelp": "只有在安装队页面关联了微信群后，才会出现微信选项。",
  "scope.recipient": "收件人",
  "scope.noRecipient": "此安装队没有登记手机号码。",
  "scope.message": "消息",
  "scope.regenerate": "按勾选重新生成",
  "scope.linkNote": "发送时 {link} 会替换为安装队的私密链接，其余内容不变。",
  "scope.preview": "以安装队视角预览",
  "scope.hidePreview": "隐藏预览",
  "scope.send": "发送短信",
  "scope.sendWechat": "在微信发送",
  "scope.sending": "发送中…",
  "scope.sent": "已发送。安装队的回复会显示在这里。",
  "scope.queued": "已排队，将于 {time} 发出（安静时段）。",
  "scope.pending": "等待在工作台上批准。",
  "scope.failed": "未发送：{message}",
  "scope.history": "已发送给安装队",
  "scope.sheetLine": "第 {version} 版 · {date} · {channel} · {state}",
  "scope.replaced": "已替换",
  "scope.noAnswer": "尚未回复",
  "scope.accepted": "{crew} 已接受",
  "scope.declined": "{crew} 已拒绝",
  "scope.availableOn": "可开工：{dates}",
  "scope.asks": "{task} 报价 {price}",
  "scope.feeIfApproved": "批准后的安装费：{total} + GST",
  "scope.approve": "批准价格",
  "scope.approving": "批准中…",
  "scope.approved": "已确认 · 已记录费用 {total} + GST",
  "scope.book": "按这些日期排期",
  "scope.resend": "修改后重新发送",
  "scope.chooseOne": "请至少勾选一项工作。",
  "scope.needFee": "每个手动项目都需要单价或费用。",
  "scope.msgState": "消息：{state}",
  "crewWechat.title": "微信群",
  "crewWechat.help": "EmmA 可以在此安装队的微信群里发布工作范围。只提供已关联的群。",
  "crewWechat.none": "未关联微信群。",
  "crewWechat.linked": "已关联：{group}",
  "crewWechat.pick": "选择 EmmA 所在的群",
  "crewWechat.link": "关联群",
  "crewWechat.unlink": "取消关联",
  "crewWechat.saved": "已保存。",
  "crewWechat.failed": "未保存：{message}",
  "crewWechat.loading": "正在加载群…",
  "scope.msg.hello": "{crew} 你好，新工作请评估：",
  "scope.msg.days": "预计 {days} 天。",
  "scope.msg.quote": "请打开链接查看详情、报价并填写可开工日期：",
  "scope.msg.open": "请打开链接查看详情并填写可开工日期：",
  "scope.msg.signEmma": "— EmmA · Artemis Flooring 安装协调",
  "scope.msg.sign": "— Artemis Flooring 办公室",
  "scope.msg.more": "等",
  "scope.msg.join": "、",
  "scopeLink.failed": "未发送。你的回答仍保留在此，请重试或致电 Artemis 办公室。",
  // Installer fee payments, per Job (25 Oct 2026)
  "crewpay.title": "工单与付款",
  "crewpay.meta": "该安装队做过的每个工单、约定费用及是否已付。不含 GST。",
  "crewpay.loading": "正在加载付款…",
  "crewpay.unavailable": "付款暂时无法读取，请刷新重试。",
  "crewpay.ownCrew": "Artemis 自有安装队按员工支付。自有安装队人工在工单中始终为 $0。",
  "crewpay.none": "该安装队尚无任何工单的约定费用。",
  "crewpay.paysBy": "付款方式：{method}",
  "crewpay.noPrefYet": "未登记付款偏好",
  "crewpay.stillToPay": "待付",
  "crewpay.col.job": "工单",
  "crewpay.col.agreed": "约定费用",
  "crewpay.col.paid": "已付",
  "crewpay.col.status": "状态",
  "crewpay.status.unpaid": "未付",
  "crewpay.status.part_paid": "部分已付",
  "crewpay.status.paid": "已付清",
  "crewpay.noFee": "无约定费用",
  "crewpay.invoiceNo": "发票 {number}",
  "crewpay.markPaid": "标记已付",
  "crewpay.details": "付款记录",
  "crewpay.open": "安装队付款",
  "crewpay.cancel": "取消",
  "crewpay.failed": "未保存：{reason}",
  "crewpay.pending": "等待批准",
  "crewpay.pdfNote": "安装队发票 · {crew} · {number}",
  "crewpay.pay.amount": "付款金额（不含 GST）",
  "crewpay.pay.method": "付款方式",
  "crewpay.pay.cash": "现金",
  "crewpay.pay.transfer": "转账",
  "crewpay.pay.noPref": "{crew} 未登记付款偏好，请选择",
  "crewpay.pay.date": "付款日期（悉尼）",
  "crewpay.pay.refCash": "现金经手人（可选）",
  "crewpay.pay.refTransfer": "银行参考号（可选）",
  "crewpay.pay.invoice": "发票编号（仅在对方开票时填写）",
  "crewpay.pay.invoiceBlank": "留空",
  "crewpay.pay.invoiceDate": "开票日期",
  "crewpay.pay.gst": "GST（仅在对方收取时填写）",
  "crewpay.pay.pdf": "发票 PDF（可选，存于工单文件中）",
  "crewpay.pay.bank": "收款：{name} · BSB {bsb} · 账号 {account}",
  "crewpay.pay.bankMissing": "该安装队未登记银行信息，请在“编辑安装队”中添加。",
  "crewpay.pay.save": "保存",
  "crewpay.pay.help": "本工单最多还需支付 {balance}。此处不会转出任何款项。",
  "crewpay.pay.saved": "已标记为已付。",
  "crewpay.void": "作废",
  "crewpay.void.reason": "错在哪里？",
  "crewpay.void.confirm": "确认作废",
  "crewpay.void.done": "已作废，记录和原因均保留。",
  "crewpay.jobcard.paid": "{date} {method}已付",
  "crewpay.jobcard.stillToPay": "尚需支付 {amount}（不含 GST）",
  // 一次性付款，从最早的工单开始抵扣（2026-11-04）。
  "crewpay.lump.open": "一次性付款",
  "crewpay.lump.title": "一次性付款，从最早的工单开始抵扣",
  "crewpay.lump.amount": "付款总额（不含 GST）",
  "crewpay.lump.split": "抵扣明细",
  "crewpay.lump.pays": "支付 {amount}",
  "crewpay.lump.paidInFull": "已付清",
  "crewpay.lump.left": "剩余 {amount}",
  "crewpay.lump.skipped": "以下工单仍有已排期的施工日，暂不从一次性付款中抵扣：{jobs}",
  "crewpay.lump.over": "超过已完工工单的应付总额（{owed}，不含 GST）。",
  "crewpay.lump.after": "付款后仍需支付：{amount}（不含 GST）",
  "crewpay.lump.help": "此处不会转账。每张工单卡片会显示其仍需支付的金额。",
  "crewpay.lump.saved": "一次性付款已记录。",
  "crewpay.lump.tag": "属于 {date} 支付的一次性付款 {amount}",
  "crewpay.lump.void": "作废此次一次性付款",
  "crewpay.lump.voidHint": "将同时作废其在所有已抵扣工单上的金额：{jobs}",
  "crewpay.jobcard.paidOf": "已付 {paid} / 共 {agreed}",
  "crewpay.jobcard.lump": "来自一次性付款",
  "job.ov.feeLeft": "尚需支付 {amount}",
  "job.ov.feePaid": "已全部付清",
  "inst.col.owed": "待付",
  "inst.col.owedHint": "该安装队工单中尚未支付的约定费用（不含 GST）",
  // 设置页上的 AI 智能体账户（ADR-034，2026-10-25）。
  "agentAccount.title": "AI 智能体账户",
  "agentAccount.scope": "只读 · 不能做任何更改",
  "agentAccount.what": "Claude Code 会话在开发和排查问题时，用此账户以员工身份查看线上系统，而不使用任何人自己的登录或浏览器。每次使用都列在这里。",
  "agentAccount.status.pending": "等待授权",
  "agentAccount.status.active": "已授权",
  "agentAccount.status.disabled": "已停用",
  "agentAccount.since": "授权于 {when}",
  "agentAccount.disabledAt": "停用于 {when}",
  "agentAccount.waiting": "在所有者授权之前，它无法读取任何内容。",
  "agentAccount.authorize": "授权",
  "agentAccount.disable": "停用",
  "agentAccount.authorized": "已授权 · 会话将在 30 秒内可以读取",
  "agentAccount.disabled": "已停用 · 会话将在 30 秒内失去访问权限",
  "agentAccount.ownerOnly": "只有所有者可以授权。",
  "agentAccount.recent": "最近使用",
  "agentAccount.none": "尚未使用。",
  // Services with no Artemis standard rate (25 Oct 2026).
  "rates.missing.title": "{total} 项服务中有 {count} 项没有标准价",
  "rates.missing.none": "所有服务都已有标准价。",
  "rates.missing.help": "输入金额（不含GST）即可添加，会加入下方价目并在保存时保留。已定价 {priced} / {total} 项。安装队自己批准的价格始终优先。",
  // Our projects — the website showcase and the CRM Showcase page (25 Oct 2026).
  "rail.showcase": "项目展示",
  "site.nav.projects": "项目案例",
  "showcase.kicker": "网站",
  "showcase.title": "项目展示",
  "showcase.lede": "网站“项目案例”页面的过往项目。EmmA 会处理并发布每个有照片的工单；不想要的随时下架。匿名展示：不显示姓名、地址、区名或工单号。",
  "showcase.loading": "加载中…",
  "showcase.unavailable": "无法加载项目展示，请稍后再试。",
  "showcase.pending": "已提交审批。",
  "showcase.refused": "未完成：{reason}",
  "showcase.start.done": "草稿已创建。请添加照片并填写简介。",
  "showcase.start.existing": "该工单已有条目，已在下方打开。",
  "showcase.pick": "请在左侧选择一个工单。",
  "showcase.status.draft": "草稿",
  "showcase.status.released": "已上网站",
  "showcase.status.taken_down": "已下架",
  // Showcase Jobs list — EmmA prepares and releases (25 Oct 2026)
  "showcase.status.not_started": "未开始",
  "showcase.filter.all": "全部",
  "showcase.jobs.title": "有照片的工单",
  "showcase.jobs.meta": "{jobs} 个中 {released} 个已上网站 · {todo} 个待 EmmA 处理",
  "showcase.jobs.search": "搜索姓名、区名或工单",
  "showcase.jobs.line": "{ref} · 安装于 {laid} · {n} 张照片",
  "showcase.jobs.notLaid": "无安装日期",
  "showcase.jobs.empty": "没有匹配的工单。",
  "showcase.jobs.none": "还没有工单有安装照片。",
  "showcase.jobs.waiting": "EmmA 还没有处理这个工单。她会查看每张照片，保留以地板为主体的照片，进行修饰，撰写简介，并自行发布到网站。您随时可以下架。",
  "showcase.jobs.startNow": "我自己开始",
  "showcase.entry.title": "条目",
  "showcase.entry.job": "来自 {ref}。网站上永远不显示工单号。",
  "showcase.field.title": "标题",
  "showcase.field.summary": "简介",
  "showcase.field.summaryHint": "用几句话说明做了什么：房间、地板、收尾。不写姓名或地址。",
  "showcase.field.anonymous": "不写客户姓名、街道、区名、工单号、电邮或电话。发布时会检查。",
  "showcase.field.season": "季节",
  "showcase.field.year": "年份",
  "showcase.field.period": "显示为 {period}",
  "showcase.field.floors": "地板类型",
  "showcase.season.summer": "夏季",
  "showcase.season.autumn": "秋季",
  "showcase.season.winter": "冬季",
  "showcase.season.spring": "春季",
  "showcase.draft.button": "请 EmmA 起草",
  "showcase.draft.prompt": "为 {ref}（展示条目 {id}）起草网站项目简介。阅读工单及已接受的报价方案，用两三句平实的英文写出房间、地板和收尾，并用 update_showcase_project 保存。不写客户姓名、街道、区名、工单号、电邮或电话。不要发布。",
  "showcase.save": "保存",
  "showcase.saved": "已保存。",
  "showcase.saveFirst": "发布前请先保存修改。",
  "showcase.needPhoto": "发布前至少添加一张照片。",
  "showcase.releaseHint": "发布后，标题、简介、季节、地板类型和照片会显示在网站上。",
  "showcase.release": "发布到网站",
  "showcase.released": "已在网站上显示。",
  "showcase.takeDown": "下架",
  "showcase.takenDown": "已从网站下架。",
  "showcase.photos.title": "照片（{n}）",
  "showcase.photos.add": "从该工单添加照片",
  "showcase.photos.closePicker": "关闭",
  "showcase.photos.none": "该条目还没有照片。",
  "showcase.photos.added": "照片已添加。",
  "showcase.photos.remove": "移除",
  "showcase.photos.removed": "照片已从条目移除，原图不受影响。",
  "showcase.picker.title": "该工单的安装照片",
  "showcase.picker.check": "只选以地板为主体的已完工照片。",
  "showcase.picker.none": "该工单没有安装照片。",
  "showcase.picker.alt": "安装照片",
  "showcase.picker.crew": "安装队",
  "showcase.picker.on": "已在条目中",
  "showcase.picker.add": "添加",
  "showcase.picker.more": "显示更多（{n}）",
  "showcase.photo.loading": "照片加载中…",
  "showcase.photo.unavailable": "照片不可用",
  "showcase.photo.alt": "展示照片",
  "showcase.touch.title": "修图",
  "showcase.touch.after": "展示效果",
  "showcase.touch.before": "原图",
  "showcase.touch.left": "向左转",
  "showcase.touch.right": "向右转",
  "showcase.touch.straighten": "拉直",
  "showcase.touch.trim.top": "裁剪上边",
  "showcase.touch.trim.right": "裁剪右边",
  "showcase.touch.trim.bottom": "裁剪下边",
  "showcase.touch.trim.left": "裁剪左边",
  "showcase.touch.brightness": "亮度",
  "showcase.touch.contrast": "对比度",
  "showcase.touch.smooth": "平滑（轻度降噪）",
  "showcase.touch.watermark": "水印",
  "showcase.touch.save": "保存修图",
  "showcase.touch.reset": "撤销修改",
  "showcase.touch.saved": "修图已保存。",
  "showcase.touch.savedStale": "修图已保存。EmmA 的修图是在此修改之前做的，已不再使用，请让她重新修图。",
  "showcase.watermark.bottom-right": "右下角",
  "showcase.watermark.bottom-left": "左下角",
  "showcase.watermark.top-right": "右上角",
  "showcase.watermark.top-left": "左上角",
  "showcase.watermark.none": "不加",
  "showcase.retouch.title": "EmmA 的修图：请你决定",
  "showcase.retouch.before": "当前",
  "showcase.retouch.after": "修图后",
  "showcase.retouch.check": "请检查地板本身：地板或地毯必须与实际安装一致（颜色、纹理、质感）。",
  "showcase.retouch.accept": "使用修图",
  "showcase.retouch.reject": "保持原样",
  "showcase.retouch.accepted": "修图已成为展示照片。",
  "showcase.retouch.rejected": "已保持原样。",
  "showcase.retouch.inUse": "正在使用 EmmA 的修图",
  "showcase.retouch.stale": "修图已过期",
  "showcase.retouch.again": "请 EmmA 重新修图",
  "showcase.retouch.askEmma": "请 EmmA 修图",
  "showcase.retouch.prompt": "用 retouch_showcase_photo 修饰展示照片 {id}：清除杂物、均匀光线，地板保持与实际安装完全一致。完成后回到这里由人工对比决定。",
  // Price my floor page (25 Oct 2026): summary, filters, sort, cost breakdown, EmmA briefing.
  "website.kicker": "数字运营",
  "website.title": "网站运营",
  "website.lede": "集中管理客户看到的内容：产品价格、获客表现及已完成项目展示。",
  "website.nav.label": "网站运营分区",
  "website.nav.overview": "总览",
  "website.nav.pricing": "价格",
  "website.nav.marketing": "市场推广",
  "website.nav.showcase": "项目展示",
  "website.pricingTitle": "网站价格",
  "website.pricingDescription": "选择哪些产品显示地板报价金额，核对成本依据并保持客户价格更新。",
  "website.pricingMetric": "{total} 个产品中有 {listed} 个当前显示网站价格",
  "website.pricingAction": "管理价格",
  "website.marketingTitle": "市场推广",
  "website.marketingDescription": "在现有表现工作台查看渠道费用、线索结果、网站流量及产品关注情况。",
  "website.marketingMetric": "费用、线索、成交工程及网站互动",
  "website.marketingAction": "查看表现",
  "website.showcaseTitle": "项目展示",
  "website.showcaseDescription": "选择安装照片、编写匿名项目介绍，并控制发布到公开网站的内容。",
  "website.showcaseMetric": "{released} 个已发布 · {drafts} 个草稿 · {total} 个有照片的工程",
  "website.showcaseAction": "管理项目展示",
  "website.loading": "正在加载当前数据……",
  "website.unavailable": "当前数据不可用；请进入工作台重试。",
  "website.operatingTitle": "一个网站，三项运营流程",
  "website.operatingNote": "这些卡片进入现有价格、市场推广和项目展示工作台。数字来自已保存记录；此总览不会发布内容或发送通信。",
  "pmf.kicker": "网站",
  "pmf.title": "地板报价",
  "pmf.lede": "已上架的产品会在网站显示价格，并可在“地板报价”中选择。其他产品显示“价格请咨询”。",
  "pmf.search": "搜索产品、供应商或类别",
  "pmf.segment.residential": "住宅",
  "pmf.segment.commercial": "商业",
  "pmf.segment.all": "全部",
  "pmf.filter.category": "全部类别",
  "pmf.filter.supplier": "全部供应商",
  "pmf.filter.status": "任何状态",
  "pmf.filter.listed": "已上架",
  "pmf.filter.ready": "可上架",
  "pmf.filter.blocked": "无法上架",
  "pmf.filter.loss": "低于总成本",
  "pmf.filter.unresolved": "成本未核实",
  "pmf.briefEmma": "交给 EmmA",
  "pmf.briefEmma.prompt": "地板报价——先读取 price_my_floor（涉及毛利的产品再读取 price_my_floor_cost）。然后按以下要求修改，并逐个汇报标价的修改前 → 修改后：",
  "pmf.sum.category": "类别",
  "pmf.sum.products": "产品",
  "pmf.sum.listed": "已上架定价",
  "pmf.sum.ready": "可上架",
  "pmf.sum.blocked": "无法上架",
  "pmf.sum.loss": "低于成本",
  "pmf.sum.unresolved": "成本未核实",
  "pmf.sum.all": "全部类别",
  "pmf.units": "所有金额按每平方米 · 不含GST",
  "pmf.col.product": "产品",
  "pmf.col.supplier": "供应商",
  "pmf.col.supplierCost": "供应商成本",
  "pmf.col.install": "+ 安装",
  "pmf.col.total": "= 总成本",
  "pmf.col.price": "标价",
  "pmf.col.gm": "毛利率",
  "pmf.col.website": "网站显示",
  "pmf.col.listed": "上架",
  "pmf.col.sortBy": "按{column}排序",
  "pmf.install.separate": "单独计价",
  "pmf.cat.listedOf": "已上架 {listed} / {total}",
  "pmf.cat.listReady": "上架全部 {count} 个可上架产品",
  "pmf.cat.included": "已含标准安装：",
  "pmf.cat.noInstall": "没有标准安装记录——无法显示毛利率",
  "pmf.cat.supplyOnly": "各安装方式的安装部分（已含在每个价格中）：",
  "pmf.cat.cost": "成本 {amount}",
  "pmf.cat.price": "售价",
  "pmf.cat.setPrice": "设定价格",
  "pmf.cat.gm": "毛利率 {pct}%",
  "pmf.cat.costUnknown": "成本未知",
  "pmf.cat.perM2": "{amount}/平方米",
  "pmf.cat.installPrice": "{name} 每平方米售价",
  "pmf.status.from": "{price} 起 + GST",
  "pmf.status.request": "价格请咨询",
  "pmf.status.notOnWebsite": "未在网站展示",
  "pmf.refusal.noPrice": "没有售价",
  "pmf.refusal.notM2": "不是按平方米计价",
  "pmf.refusal.atCost": "售价不高于成本",
  "pmf.tip.unresolved": "报价引擎无法核算此产品成本：此数字取自目录原始行。展开该行查看原因。",
  "pmf.tip.price": "含GST {inc}——点击修改",
  "pmf.tip.setPrice": "点击设定价格",
  "pmf.tip.gmNeedsCost": "毛利率需要供应商成本和安装成本",
  "pmf.tip.gmLoss": "成本高于标价——点击设定毛利率",
  "pmf.tip.gm": "点击设定毛利率；价格随之调整，向上取整到整元",
  "pmf.tip.listed": "已上架——关闭后显示“价格请咨询”",
  "pmf.tip.list": "打开后在网站显示价格",
  "pmf.tip.noInstall": "此产品的安装方式没有标准安装",
  "pmf.tip.separate": "{name}——单独计价",
  "pmf.switch.list": "上架 {name}",
  "pmf.switch.unlist": "下架 {name}",
  "pmf.edit.price": "{name} 标价",
  "pmf.edit.gm": "{name} 毛利率",
  "pmf.set": "设定",
  "pmf.done.listed": "已上架",
  "pmf.done.unlisted": "已下架",
  "pmf.done.price": "标价 {price}",
  "pmf.loading": "正在加载目录…",
  "pmf.none": "没有符合条件的产品。",
  "pmf.tally.listed": "已在网站上架",
  "pmf.tally.ready": "可上架",
  "pmf.tally.blocked": "需先修正目录价格才能上架",
  "pmf.tally.loss": "标价低于总成本",
  "pmf.tally.unresolved": "成本未核实",
  "pmf.foot": "每个价格都含供货与安装。工程实木地板每种安装方式（悬浮铺装、直接胶粘）各有一个价格。点击产品查看成本计算方式；点击价格或毛利率即可修改（向上取整到整元）。成本 = 供应商成本 + 安装 + 按厂商价格计算的所含底垫和压条，不含损耗——损耗由客户支付。成本与毛利率仅供员工查看，不会离开CRM。",
  "pmf.sort.group": "按类别分组",
  "pmf.col.included": "+ 所含配件",
  "pmf.tip.includedMissing": "该厂商尚未设置{what}——请在“各厂商所含配件”中设置",
  "pmf.inc.missing": "缺失",
  "pmf.inc.m.underlay": "底垫价格",
  "pmf.inc.m.scotia": "压条价格",
  "pmf.method.floating": "悬浮铺装",
  "pmf.method.glued": "直接胶粘",
  "pmf.method.herringbone": "鱼骨纹",
  "pmf.method.chevron": "人字拼",
  "pmf.method.standard": "含安装",
  "pmf.inc.includes": "包含：",
  "pmf.inc.item.installation": "安装",
  "pmf.inc.item.underlay": "底垫",
  "pmf.inc.item.scotia": "压条",
  "pmf.inc.optional": "可选：收边条、防潮膜",
  "pmf.inc.saved": "已保存所含配件价格",
  "pmf.inc.title": "各厂商所含配件",
  "pmf.inc.scotiaRule": "悬浮地板含底垫 + 压条按每平方米 {lm} 延米计，不含损耗",
  "pmf.inc.missingCount": "{count} 项缺少价格",
  "pmf.inc.manufacturer": "厂商",
  "pmf.inc.underlay": "底垫 $/平方米",
  "pmf.inc.scotia": "压条 $/延米",
  "pmf.inc.note": "备注",
  "pmf.inc.underlayFor": "{name} 底垫价格",
  "pmf.inc.underlayTip": "0 = 地板自带底垫或不使用",
  "pmf.inc.attached": "自带",
  "pmf.inc.glued": "胶粘——无底垫",
  "pmf.inc.scotiaFor": "{name} 压条价格",
  "pmf.inc.help": "不含GST。该厂商、该类别的所有硬质地板都使用这一行。价格留空则这些地板不显示估算成本和毛利率。收边条和防潮膜为可选，不包含在内。",
  "pmf.inc.unknown": "未设置",
  "pmf.inc.none": "无",
  "pmf.x.included": "所含配件 · 底垫 + 压条",
  "pmf.x.includedHow": "底垫 {underlay}/平方米 + 压条 {scotia}/平方米 · 厂商配件表，不含损耗",
  "pmf.x.methodHow": "成本 {cost} · 毛利率 {gm}%",
  "pmf.edit.methodPrice": "{name} {method} 价格",
  "pmf.tip.methodPrice": "此安装方式的含供货与安装价格——点击修改",
  "pmf.cat.timberMethods": "每种安装方式都有自己的含供货与安装价格——在每一行设置价格或毛利率。",
  "pmf.edit.methodGm": "{name} {method} 毛利率",
  "pmf.x.title": "成本计算方式",
  "pmf.x.loading": "正在计算成本…",
  "pmf.x.colour": "颜色",
  "pmf.x.cost": "供应商成本",
  "pmf.x.perM2": "每平方米",
  "pmf.x.source": "来源",
  "pmf.x.src.price_history": "价目表，{date} 生效",
  "pmf.x.src.catalogue_column": "目录字段",
  "pmf.x.src.default_row": "沿用默认行",
  "pmf.x.src.none": "无成本",
  "pmf.x.dearest": "最高——采用",
  "pmf.x.perUnit": "{amount} / {unit}",
  "pmf.x.width": "卷宽 {width} 米：每延米 ÷ 卷宽 = 每平方米",
  "pmf.x.noColours": "没有客户可选颜色记录。",
  "pmf.x.fallback": "报价引擎无法核算此产品成本（{issues}）。页面采用目录行“{row}”的 {cost}/平方米{unit}。请在目录中补全各颜色成本（或卷宽）后即可核实。",
  "pmf.x.fallbackUnit": "（记录单位：{unit}）",
  "pmf.x.fallbackNoUnit": "（未记录单位）",
  "pmf.x.issue.material": "颜色没有材料成本",
  "pmf.x.issue.carpet": "颜色没有每延米成本或卷宽",
  "pmf.x.issue.width": "没有卷宽，无法把每延米成本换算为每平方米",
  "pmf.x.install": "安装 · {name}",
  "pmf.x.installParts": "人工 {labour}{glue} 每{unit}{width}{underlay} · 报价定价",
  "pmf.x.glue": " + 胶 {amount}",
  "pmf.x.underlay": " + 底垫 {amount}/平方米",
  "pmf.x.perWidth": " ÷ {width} 米卷宽",
  "pmf.x.noInstall": "没有安装成本：该安装方式没有费率，或地毯没有卷宽。",
  "pmf.x.installSeparate": "{name}——单独销售，{price}/平方米（不计入此成本）",
  "pmf.x.installUnpriced": "{name}——单独销售，尚未定价",
  "pmf.x.total": "总成本",
  "pmf.x.totalHow.installed": "供应商成本 + 安装 + 所含底垫/压条",
  "pmf.x.totalHow.supply": "最高颜色成本（仅供货）",
  "pmf.x.gm": "毛利率",
  "pmf.x.gmHow": "（{price} − {cost}）÷ {price}",
  "pmf.x.check": "上架开关检查的是 {cost}（目录原始行中的最高值），而不是这里采用的 {page}。",
  "pmf.x.notIncluded": "不计入此成本",
  "pmf.x.ni.waste": "损耗与边角料（由客户支付）",
  "pmf.x.ni.accessories": "收边条和防潮膜（可选）",
  "pmf.x.ni.prep": "地面处理与拆除",
  "pmf.x.changed": "上架状态于 {day} 更改",
  "pmf.x.brief": "让 EmmA 处理此产品",
  "pmf.x.briefPrompt": "地板报价 · {name}（{id}）：先读取它的 price_my_floor_cost，然后按以下要求修改，并汇报标价的修改前 → 修改后：",
  "scope.fromStandard": "Artemis 标准价",
  "scope.headerButton": "发给安装队",

  // 构建记录（2026-10-25）：每个功能新增、修改、修复了什么，用大白话写下来。
  "bt.kicker": "系统",
  "bt.title": "构建记录",
  "bt.lede": "每个页面新增、修改或修复了什么，最新的在前。每一次改到页面的代码改动都会用大白话记在这里。",
  "bt.all": "全部页面",
  "bt.feature": "页面",
  "bt.count": "{count} 项改动",
  "bt.kind.added": "新增",
  "bt.kind.changed": "修改",
  "bt.kind.fixed": "修复",
  "bt.kind.internal": "内部",
  "bt.showInternal": "显示内部改动",
  "bt.hideInternal": "隐藏内部改动",
  "bt.pr": "合并记录 #{pr}",
  "bt.backfill": "由合并记录补录",
  "bt.empty": "此页面暂无记录。",
  "bt.unavailable": "无法加载构建记录。",
  "bt.search": "搜索改动",
  // Crew payment link (26 Oct 2026)
  "paylink.title": "付款明细",
  "paylink.unavailable": "此链接无效，请向 Artemis Flooring 索取新链接。",
  "paylink.expired": "此链接已过期，请向 Artemis Flooring 索取新链接。",
  "paylink.replaced": "已向您发送了新链接，请打开最新的链接。",
  "paylink.codeTo": "我们会向尾号 {tail} 的手机发送 6 位验证码。",
  "paylink.codeToUnknown": "我们会向您登记的手机发送 6 位验证码。",
  "paylink.sendCode": "发送验证码",
  "paylink.codeSent": "验证码已发送至尾号 {tail} 的手机，10 分钟内有效。",
  "paylink.enterCode": "输入 6 位验证码",
  "paylink.open": "查看",
  "paylink.wrongCode": "验证码不正确，请重试或重新获取。",
  "paylink.failed": "出现问题，请重试或致电 Artemis 办公室。",
  "paylink.asOf": "截至 {date}",
  "paylink.stillToPay": "待付",
  "paylink.nothingOwing": "已完成的工单没有待付款项。",
  "paylink.paid": "已付",
  "paylink.nothingPaid": "尚无付款记录。",
  "paylink.upcoming": "即将开工的工单",
  "paylink.nothingUpcoming": "暂无已排期的工单。",
  "paylink.starts": "{date} 开工",
  "paylink.feeNotConfirmed": "费用尚未确认",
  "paylink.cash": "现金",
  "paylink.transfer": "银行转账",
  "paylink.partPaid": "部分已付",
  "paylink.notPaid": "未付",
  "paylink.invoice": "发票 {number}",
  "paylink.exGst": "金额不含 GST。显示的费用为 Artemis 已向您确认的费用。",
  "paylink.expires": "此链接有效期至 {date}。",
  "paylink.help": "如有疑问，请致电 Artemis 办公室。",
  "crewpay.link.title": "付款链接",
  "crewpay.link.lede": "向该安装队发送一个私人页面，显示待付、已付及即将开工的工单。对方需输入发到其手机的验证码才能打开。有效期 7 天，重新发送会替换旧链接。",
  "crewpay.link.by": "发送方式",
  "crewpay.link.sms": "短信",
  "crewpay.link.wechat": "微信群",
  "crewpay.link.noWechat": "未关联微信群",
  "crewpay.link.noMobile": "请先为该安装队登记手机号码：验证码会发送到该号码。",
  "crewpay.link.to": "发送至 {to}",
  "crewpay.link.quiet": "短信会在免打扰时段结束后发送。",
  "crewpay.link.send": "发送付款链接",
  "crewpay.link.sent": "已发送，链接 7 天内有效。",
  "crewpay.link.history": "已发送的链接",
  "crewpay.link.row": "{when} · {channel} · 有效至 {until}",
  "crewpay.link.opened": "已打开 {count} 次",
  "crewpay.link.replaced": "已替换",
  "crewpay.link.expired": "已过期",
  "crewpay.link.message": "{crew} 你好，Artemis 付款明细已更新：待付、已付和即将开工的工单。打开链接并输入短信验证码查看：{link}（7 天内有效）",
  "scope.msgFailed": "未送达：{reason}。排除原因后重新发送。",
  "scope.resendFailed": "重新发送",
  "scope.msg.pay": "本单工钱 {amount} + GST",
  "scope.msg.payPlusQuote": "本单工钱 {amount} + GST，另有 {count} 项待报价",
  "scope.msg.quoteOnly": "{count} 项待报价",
  // Customer carpet page without a cut diagram, and the rounding line in its own words (P-1126-10336, 3 Nov 2026).
  'customer.doc.floorCovered': '铺设面积',
  'customer.doc.roundingLine': '取整至整元',
  // Supplier statements and Xero bills (4 Nov 2026).
  'sup.view.suppliers': "供应商",
  'sup.view.accounts': "对账单与账单",
  'sup.tab.accounts': "对账单与账单",
  'acct.title': "对账单与账单",
  'acct.meta': "我们欠每个供应商的金额、供应商为我们持有的余额，以及等待您批准的 Xero 账单。金额含 GST，与对账单一致。",
  'acct.col.supplier': "供应商",
  'acct.col.terms': "付款条件",
  'acct.col.asAt': "对账单",
  'acct.col.balance': "余额",
  'acct.col.overdue': "逾期",
  'acct.col.toBill': "待建账单",
  'acct.col.billed': "Xero 未付",
  'acct.col.look': "需查看",
  'acct.terms.credit': "信用账期",
  'acct.terms.perInvoice': "逐张发票付款",
  'acct.terms.creditHint': "按月末对账单付款；每张订单发票只做记录。",
  'acct.terms.perInvoiceHint': "每张发票到达时单独生成账单。",
  'acct.weOwe': "我们欠 {amount}",
  'acct.creditHeld': "为我们持有 {amount} 余额",
  'acct.settled': "已结清",
  'acct.noStatement': "暂无对账单",
  'acct.incGst': "含 GST",
  'acct.approvals': "等待您批准",
  'acct.none': "尚未记录供应商对账单或发票。EmmA 会从采购办公室归档的供应商邮件中读取。",
  'acct.checksCount': "{count} 项需核对",
  'acct.heldCount': "{count} 项暂缓",
  'acct.unavailable': "无法读取供应商对账单：{message}",
  'acct.statementTitle': "最新对账单 · 截至 {day}",
  'acct.checksTitle': "对账单核对",
  'acct.check.not_recorded': "对账单上有，我们没有记录",
  'acct.check.amount_differs': "对账单为 {statement}，发票为 {recorded}",
  'acct.check.not_on_statement': "发票不在对账单上，因此不会被支付",
  'acct.docs': "发票、贷项通知单与对账单",
  'acct.col.document': "单据",
  'acct.col.dated': "日期",
  'acct.col.due': "到期",
  'acct.col.job': "工单",
  'acct.col.amount': "含 GST",
  'acct.col.state': "状态",
  'acct.kind.invoice': "发票",
  'acct.kind.credit_note': "贷项通知单",
  'acct.kind.statement': "对账单",
  'acct.state.recorded': "已记录",
  'acct.state.toBill': "待建账单",
  'acct.state.held': "暂缓：{reason}",
  'acct.state.billed': "账单 {number} · {status}",
  'acct.hold': "暂缓",
  'acct.release': "恢复",
  'acct.holdReason': "为什么暂不建账单？",
  'acct.void': "作废账单",
  'acct.voidReason': "为什么在 Xero 作废此账单？",
  'acct.createBills': "在 Xero 创建 {count} 张账单 · {amount}",
  'acct.createBillsHint': "您的点击即为批准。账单将以待付款状态进入 Xero。请在 Xero 中将银行付款与账单匹配，切勿再录入为支出。",
  'acct.refresh': "从 Xero 刷新",
  'acct.openEmail': "打开邮件",
  'acct.open': "打开",
};
